import * as THREE from 'three'
// WebGPU migration, Stage 1 (?webgpu=1, opt-in — see
// /Users/uldis.liepins/.claude/plans/warm-sleeping-barto.md). Only
// WebGPURenderer is pulled from the 'three/webgpu' subpath; every other
// three.js class (Scene, PerspectiveCamera, Mesh, ...) still comes from the
// plain 'three' import above, unchanged, to avoid mixing class identities
// between the two entry points.
import { WebGPURenderer } from 'three/webgpu'
import {
  OrbitControls
} from 'three/examples/jsm/controls/OrbitControls'
import {
  SplatLoader,
  Splat,
  createWebGPUSplatMesh,
  SOG_DECODE_MEMORY_MULTIPLIER,
  estimateAvailableMemoryBytes,
  isMobileDevice,
  isPhoneDevice,
  useWebGPUAddon,
  autoNavQuality,
  hasWebGPUSupport,
  autoRenderer,
  autoRendererStreamed,
  requestedRenderer,
  isLowMemoryDevice,
  autoTierFor,
  streamPerformanceCap
} from '../Splat.ts'
import '../styles.css'
import {
  Raycaster
} from '../utils/Raycaster';
import { authFetch, hasSession } from '../utils/apiHelper';
import { setAccessQuery } from '../utils/lodChunkCache';
import { API_URL, S3_UPLOADS_BASE_URL, CAPTURES_CDN_URL, UPLOADS_PUBLIC_URL } from '../config';
import { getViewingModelId } from '../utils/shareSession';
import { fetchWithModelCache } from '../utils/modelCache';
import { readCachedSpeed, fetchSpeed, ZOOM_SPEED, MOVE_SPEED, LOOK_SPEED } from '../utils/speedPrefs';
import { createLoadProgress } from '../utils/loadProgress';
import { createAdaptiveQuality, isAdaptiveOn } from './adaptiveQuality';
import { createTouchJoystick, isJoystickEnabled, JOYSTICK_KEYS } from './touchJoystick';
import { qualityPos, qualitySettings, QUALITY_POS } from '../utils/renderQuality';
import { saveAs } from 'file-saver';
import { showToast } from '../utils/toast';

//import { useNavigate } from 'react-router-dom';

// Caps the focal-point picking cloud's vertex count via fixed-stride sampling
// (same cap size as the visible splat mesh). Without this, raycasting against
// an arbitrarily large, unindexed point cloud on every double-click/double-tap
// can hang the main thread — see onMouseUp's raycast against scene.children[1].
const MAX_PICKING_POINTS = 250000;
function decimateGeometry(geometry, maxPoints) {
  const positionAttr = geometry.attributes.position;
  if (!positionAttr || positionAttr.count <= maxPoints) return geometry;

  const stride = Math.ceil(positionAttr.count / maxPoints);
  const decimated = new THREE.BufferGeometry();
  for (const name of Object.keys(geometry.attributes)) {
    const attr = geometry.attributes[name];
    const itemSize = attr.itemSize;
    const keptCount = Math.ceil(attr.count / stride);
    const ArrayType = attr.array.constructor;
    const newArray = new ArrayType(keptCount * itemSize);
    for (let i = 0, kept = 0; i < attr.count; i += stride, kept++) {
      for (let j = 0; j < itemSize; j++) {
        newArray[kept * itemSize + j] = attr.array[i * itemSize + j];
      }
    }
    decimated.setAttribute(name, new THREE.BufferAttribute(newArray, itemSize));
  }
  return decimated;
}

// PLYLoader.parse() is synchronous with no chunked/async variant — for a
// point cloud with millions of points (typical, before decimateGeometry
// above cuts it down) that's a genuinely multi-second main-thread block,
// which froze EVERYTHING else running on the main thread too, including the
// main splat's own ongoing download-progress updates even while its
// transfer kept flowing normally underneath. Runs the parse in a worker
// instead (see ply-parse-worker.js) and reconstructs a real BufferGeometry
// here from the plain typed arrays it hands back — a THREE.BufferGeometry
// itself isn't transferable between threads, so the worker unpacks it and
// this rebuilds it.
// maxPoints: when this is a pick-layer point cloud (the only current
// caller), passing the same cap decimateGeometry would apply afterward lets
// the worker sample straight to (roughly) that count while parsing — see
// ply-parse-worker.js's own comment on why that matters for a large file.
// Omit it to always get the full, faithful parse (unused today, kept for
// any future caller that needs one).
function parsePLYInWorker(buffer, maxPoints) {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('../ply-parse-worker.js', import.meta.url));
    worker.onmessage = (e) => {
      if (e.data.type === 'done') {
        const geometry = new THREE.BufferGeometry();
        for (const [name, attr] of Object.entries(e.data.attributes)) {
          geometry.setAttribute(name, new THREE.BufferAttribute(attr.array, attr.itemSize, attr.normalized));
        }
        resolve(geometry);
      } else {
        reject(new Error(e.data.message));
      }
      worker.terminate();
    };
    worker.onerror = (err) => {
      reject(err);
      worker.terminate();
    };
    worker.postMessage({ buffer, maxPoints }, [buffer]);
  });
}

// The running scene's dispose (see initializeScene's teardown).
let currentSceneDispose = null;
export function disposeScene() { currentSceneDispose?.(); }

export default async function initializeScene(containerId) {
  // Load generation: a newer initializeScene (another model opened, the
  // page re-entered) supersedes this one — its loading display must stop
  // writing to the shared overlay, or two loads' percentages interleave
  // (e.g. 2 3 7 13 5 7 16 …).
  const loadGeneration = (window.__sceneLoadGeneration = (window.__sceneLoadGeneration || 0) + 1);
  const isCurrentLoad = () => window.__sceneLoadGeneration === loadGeneration;

  // Teardown. Leaving the scene is in-app navigation (no page reload), so a
  // scene used to keep running behind the next page: its draw loop, window
  // listeners, timers, workers, streaming pool and GPU renderer. Window
  // listeners and repeating timers go through onWindow / every so dispose()
  // can undo them; the rest registers a disposer. Scene.js calls
  // disposeScene() when it unmounts; a newer scene disposes an older one.
  let sceneDisposed = false;
  const windowListeners = [];
  const intervals = [];
  const disposers = [];
  const onWindow = (type, fn, opts) => { window.addEventListener(type, fn, opts); windowListeners.push([type, fn, opts]); };
  const every = (fn, ms) => { const id = setInterval(fn, ms); intervals.push(id); return id; };
  const onDispose = (fn) => disposers.push(fn);
  // Resources of a loaded model: octree (decode workers), pool (GPU
  // textures), sort worker. Also used for a load that finishes after the
  // scene was left.
  const freeSplatShared = (shared) => {
    if (!shared) return;
    try { shared.octree?.destroy?.(); } catch (e) { /* already gone */ }
    try { shared.pool?.destroy?.(); } catch (e) { /* already gone */ }
    try { shared.worker?.terminate?.(); } catch (e) { /* already gone */ }
  };
  const disposeThisScene = () => {
    if (sceneDisposed) return;
    sceneDisposed = true;
    windowListeners.forEach(([type, fn, opts]) => window.removeEventListener(type, fn, opts));
    intervals.forEach(clearInterval);
    disposers.splice(0).reverse().forEach((fn) => { try { fn(); } catch (e) { console.warn('[scene] dispose step failed:', e); } });
    if (activeOctree && window.__splatOctree === activeOctree) window.__splatOctree = null;
    if (currentSceneDispose === disposeThisScene) currentSceneDispose = null;
  };
  currentSceneDispose?.();
  currentSceneDispose = disposeThisScene;

  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container #${containerId} not found.`);
    return;
  }
  // Check if the container already has a canvas (which THREE.js uses for rendering)
  // and if so, remove it before creating a new one. This prevents duplicate renderers/canvases.
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }

  let spinnerActive = false;
  let loadingInterval = null;
  let shouldCaptureThumbnail = false;

  // Get the URL parameters and assign to variables
  const queryParams = new URLSearchParams(window.location.search);
  let modelId = getViewingModelId(window.location.search);
  modelId = modelId ? modelId : 'Fabrika_EXT'; // Default model ID
  let npl = queryParams.get('npl'); // No point cloud layer (npl)
  let debug = queryParams.get('debug');
  const memdbg = queryParams.get('memdbg');
  // Streamed SOG (Phase 1+2, dev-gated via ?stream=1&model=<lod-meta.json
  // URL> — see /Users/uldis.liepins/.claude/plans/witty-watching-star.md).
  // Set by the streaming branch in app(); animate() drives its per-frame
  // LOD solve. null on the normal (non-streaming) path, which is otherwise
  // completely unaffected by this addition.
  let activeOctree = null;
  let _streamPoseLock = null; // { pos, quat, target, framesLeft } — see animate()

  if (memdbg) {
    const hud = document.createElement('div');
    hud.id = 'mem-hud';
    hud.style.cssText = [
      'position:fixed', 'top:60px', 'left:12px', 'z-index:9999',
      'background:rgba(0,0,0,0.72)', 'color:#9ecfcf',
      'font:12px/1.6 monospace', 'padding:10px 14px',
      'border-radius:8px', 'pointer-events:none',
      'white-space:pre', 'min-width:220px',
    ].join(';');
    hud.textContent = 'Waiting for splat…';
    document.body.appendChild(hud);
    onDispose(() => hud.remove());

    const fmtMB = (n) => n != null ? n.toFixed(1) + ' MB' : '—';
    every(() => {
      const s = window.__splatDebugStats;
      if (!s) return;
      if (s.phase) {
        // Early loading phase — textures not yet allocated
        hud.textContent = [
          `Phase:       ${s.phase}`,
          `Downloaded:  ${fmtMB(s.downloadedMB)} / ${fmtMB(s.totalMB)}`,
        ].join('\n');
        return;
      }
      const heap = s.heapMB != null ? `\nJS heap:     ${s.heapMB} MB` : '';
      hud.textContent = [
        `Splats:      ${s.numSplats.toLocaleString()}`,
        `SH:          ${s.hasSH ? `degree ${s.shDegree}` : 'off'}`,
        `LowMem mode: ${s.isLowMemory}`,
        ``,
        `Pos+Scale:   ${fmtMB(s.centerAndScaleMB)}`,
        `Cov+Color:   ${fmtMB(s.covAndColorMB)}`,
        `SH data:     ${fmtMB(s.shDataMB)}`,
        `CPU total:   ${fmtMB(s.cpuTotalMB)}`,
        `GPU est:     ${fmtMB(s.gpuEstMB)}`,
        heap,
        ``,
        `Loaded:      ${s.loaded}`,
      ].join('\n');
    }, 500);
  }

  // ─── Debug overlay (bottom-left) ────────────────────────────────────────
  // FPS / memory / splat + LOD stats. Off by default; toggled from the
  // Quality modal (persisted in localStorage 'debugHud'). Also force-on with
  // ?debugHud=1. Populated by a 4Hz interval only while visible, so it costs
  // nothing when off. `_frameMs`/`_fps` are updated in animate().
  let _frameT = performance.now();
  // Streamed scenes: steps resolution / streaming budget to the device's
  // measured frame time — see three/adaptiveQuality.js. Fed from animate().
  let adaptiveQuality = null;
  // The splat mesh being drawn (static or streamed) — live render-quality
  // changes set its covClamp.
  let liveSplatMesh = null;
  let captureInProgress = false;
  // Static models: splat share drawn (% of the model) and what's loaded.
  let liveSplatPct = 100;
  let staticSplatLoadedPct = 100;
  function setLiveSplatPct(pct) {
    liveSplatPct = Math.max(1, Math.min(staticSplatLoadedPct, Math.round(pct)));
    const mat = liveSplatMesh?.material;
    if (mat && 'density' in mat) mat.density = liveSplatPct / staticSplatLoadedPct;
    window.dispatchEvent(new CustomEvent('splatShareChange', { detail: { pct: liveSplatPct } }));
  }
  // Spherical harmonics: degree evaluated now (live) and what's loaded.
  let liveShDegree = 0;
  let loadedShDegree = 0;
  function setLiveShDegree(d) {
    liveShDegree = Math.max(0, Math.min(loadedShDegree, Math.round(d)));
    const mat = liveSplatMesh?.material;
    if (mat && 'shDegree' in mat) mat.shDegree = liveShDegree;
    window.dispatchEvent(new CustomEvent('shDegreeChange', { detail: { degree: liveShDegree } }));
  }
  // After a model's mesh exists: what it loaded, and the degree to draw.
  function initLiveSh(sharedState) {
    loadedShDegree = sharedState?.hasSH ? (sharedState.shDegree || 0) : 0;
    setLiveShDegree(shDegreeParam ?? loadedShDegree);
  }
  window.__shLive = {
    get degree() { return liveShDegree; },
    get loaded() { return loadedShDegree; },
    set: (d) => { adaptiveQuality?.freezeSh?.(); setLiveShDegree(d); },
    setAuto: (on) => (on ? adaptiveQuality?.resumeSh?.() : adaptiveQuality?.freezeSh?.()),
  };
  // Levers shared by both model types (adaptiveQuality.js), stepped in turn
  // — SH 3→2→1→off, resolution, [streamed: level of detail / static: splat
  // share], navigation quality, one step each per round.
  // Static: adaptive quality takes the splat share no lower than this.
  const ADAPTIVE_SPLAT_FLOOR_PCT = 50; // % of a static model
  // Adaptive quality keeps resolution between 55% of native pixels (75% on
  // the slider) and its Auto value — the Settings slider can still go
  // anywhere, which takes it off Auto.
  // Sharpness isn't a lever (see Sharpness above).
  const ADAPTIVE_MIN_RESOLUTION_PCT = 55;
  // iPad / desktop: never below 80% on the slider (2026-10-28) — lower
  // looked awful on an iPad. Phones keep the 55%-of-native floor above.
  const ADAPTIVE_MIN_RESOLUTION_SLIDER_PCT = 80;
  const adaptiveMinResolutionPct = () => Math.min(
    isPhoneDevice ? Math.round(dpr * ADAPTIVE_MIN_RESOLUTION_PCT / RESOLUTION_REF_PR / 5) * 5 : ADAPTIVE_MIN_RESOLUTION_SLIDER_PCT,
    autoResolutionPct);
  // Streamed detail floor (iPad / desktop): at least this many splats per
  // pixel drawn at the resolution floor, as a share of the budget — so
  // adaptive quality can't thin the splats out until they smear. 1.6 puts
  // a 12.9" iPad in Safari (~1.6M pixels at 80%) at ~2.5M, where it still
  // looked fine; a 16" MacBook at 8M gets ~3.6M. Never above the model's
  // own size; phones keep the lever's 50%.
  const ADAPTIVE_SPLATS_PER_PIXEL = 1.6;
  const adaptiveMinDetailPct = (budget, modelSplats) => {
    if (isPhoneDevice || !(budget > 0)) return 50;
    const pr = pixelRatioForPct(adaptiveMinResolutionPct());
    const pixels = window.innerWidth * pr * (window.innerHeight - bottomBarAdjustment) * pr;
    const floor = Math.min(pixels * ADAPTIVE_SPLATS_PER_PIXEL, modelSplats > 0 ? modelSplats : Infinity);
    const pct = Math.max(50, Math.min(100, Math.ceil(floor / budget * 100)));
    console.info(`[adaptive quality] floors: resolution ${adaptiveMinResolutionPct()}%, detail ${pct}% (${(budget * pct / 1e8).toFixed(2)}M of ${(budget / 1e6).toFixed(1)}M, ${(pixels / 1e6).toFixed(2)}M px)`);
    return pct;
  };
  const qualityLever = () => ({
    name: 'quality', label: 'resolution', step: 'fives',
    get: () => resolutionPct, set: applyResolutionPct,
    min: adaptiveMinResolutionPct(),
    max: autoResolutionPct, base: autoResolutionPct, // never above the default
    adapt: !resolutionManual, fmt: (v) => `${v}%`,
  });
  // Spherical harmonics as two levers on the one degree: 3 → 1 first, and
  // 1 → off only after the model's primary lever (see adaptiveQuality.js).
  const shFmt = (v) => (v > 0 ? `degree ${v}` : 'off');
  const shLever = () => (loadedShDegree > 1 ? {
    name: 'sh', label: 'spherical harmonics', step: 'degree',
    get: () => Math.max(1, liveShDegree), set: (v) => setLiveShDegree(liveShDegree === 0 ? 0 : v),
    min: 1, max: loadedShDegree, base: loadedShDegree,
    adapt: shDegreeParam === undefined, fmt: () => shFmt(liveShDegree),
  } : null);
  const shOffLever = () => (loadedShDegree > 0 ? {
    name: 'shOff', label: 'spherical harmonics', step: 'degree',
    get: () => Math.min(1, liveShDegree), set: (v) => setLiveShDegree(v >= 1 ? Math.max(1, liveShDegree) : 0),
    min: 0, max: 1, base: 1, after: 'sh', // continues SH 3→1 in its slot: off the round after SH 1
    adapt: shDegreeParam === undefined, fmt: () => shFmt(liveShDegree),
  } : null);

  // Navigation quality: adaptive quality's last step, 100% → 70% in 10-point
  // steps (resolution while moving). Off adaptive control when picked by hand.
  const ADAPTIVE_MIN_NAV_PCT = 70;
  // Frame-rate gates, the same on every device: lower below 20 fps, raise at 30
  // (were 25 / 35 until 2026-10-28 — an iPad at 22-32 fps kept cutting quality).
  const ADAPTIVE_GATES = { lowFps: 20, highFps: 30 };
  const navLever = () => ({
    name: 'nav', label: 'navigation quality', step: 'tens',
    get: () => Math.round(navAdaptiveCap * 100), set: (pct) => setNavAdaptiveCap(pct / 100),
    min: ADAPTIVE_MIN_NAV_PCT, max: 100, base: 100,
    adapt: navIsAuto, fmt: (v) => (v >= 100 ? 'Full' : `${v}%`),
  });

  // Quality menu: current / loaded share, live setter, and its Auto switch.
  window.__splatShare = {
    get pct() { return liveSplatPct; },
    get loaded() { return staticSplatLoadedPct; },
    set: (pct) => { adaptiveQuality?.freezePrimary?.(); setLiveSplatPct(pct); },
    setAuto: (on) => (on ? adaptiveQuality?.resumePrimary?.() : adaptiveQuality?.freezePrimary?.()),
  };
  function setOverlayStatus(text) {
    const status = document.getElementById('loading-status');
    if (status) status.textContent = text || '';
  }

  let _frameMs = 16.7;
  let _fps = 60;
  const dbgHud = document.createElement('div');
  dbgHud.id = 'debug-hud';
  dbgHud.style.cssText = [
    'position:fixed', 'left:12px', 'bottom:150px', 'z-index:9999',
    'background:rgba(0,0,0,0.78)', 'color:#a9e5e0',
    'font:11px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace',
    'padding:8px 11px', 'border-radius:8px', 'pointer-events:none',
    'min-width:252px', 'letter-spacing:0.2px', 'display:none',
  ].join(';');
  // Sit exactly 12px above the bottom toolbar's top edge — same gap as the
  // 12px from the left. Measured from the live #bottom-nav element (it's
  // injected async, and its height varies a little), re-checked on resize.
  const positionDbgHud = () => {
    const nav = document.getElementById('bottom-nav');
    const gap = nav ? Math.max(0, window.innerHeight - nav.getBoundingClientRect().top) : 138;
    dbgHud.style.bottom = `${gap + 12}px`;
  };
  onWindow('resize', positionDbgHud);
  setTimeout(positionDbgHud, 300);
  setTimeout(positionDbgHud, 1500);
  document.body.appendChild(dbgHud);

  // ─── Structured rows: label · value · inline reference · [?] tooltip ────
  // Each metric is a grid row. The value is colour-graded (green/amber/red)
  // against its reference band, a short "best" range shows inline, and the
  // "?" opens a full explanation. Rows are built once here; the 4Hz tick
  // below only updates text / colour / visibility so hover state survives.
  if (!document.getElementById('debug-hud-style')) {
    const st = document.createElement('style');
    st.id = 'debug-hud-style';
    st.textContent = [
      '#debug-hud .dh-row{display:grid;grid-template-columns:58px 1fr 13px;',
      'align-items:center;column-gap:7px}',
      // `.dh-row` sets display:grid, which beats the UA [hidden] rule — so a
      // hidden row would still render (empty) without this higher-specificity
      // override. Same for the spacer.
      '#debug-hud .dh-row[hidden],#debug-hud .dh-spacer[hidden]{display:none}',
      '#debug-hud .dh-label{color:#7fb8b2}',
      '#debug-hud .dh-mid{display:flex;justify-content:space-between;gap:8px;min-width:0}',
      '#debug-hud .dh-v{white-space:pre;overflow:hidden;text-overflow:ellipsis}',
      '#debug-hud .dh-v.good{color:#6ee7a8}',
      '#debug-hud .dh-v.warn{color:#f5c542}',
      '#debug-hud .dh-v.bad{color:#ff6b6b}',
      '#debug-hud .dh-ref{color:#5c7c78;font-size:10px;flex:0 0 auto}',
      '#debug-hud .dh-q{pointer-events:auto;cursor:help;display:inline-block;',
      'width:12px;height:12px;line-height:11px;text-align:center;font-size:9px;',
      'color:#3f6b66;border:1px solid #3f6b66;border-radius:50%;user-select:none}',
      '#debug-hud .dh-q:hover,#debug-hud .dh-q:focus{color:#a9e5e0;border-color:#a9e5e0;outline:none}',
      '#debug-hud .dh-q.pin{color:#a9e5e0;border-color:#a9e5e0;background:#2a4744}',
      '#debug-hud .dh-spacer{height:6px}',
      '#debug-hud-tip{position:fixed;z-index:10000;max-width:262px;',
      'background:rgba(12,20,19,0.97);color:#d7efec;border:1px solid #2f4a47;',
      'border-radius:7px;padding:8px 10px;letter-spacing:0;pointer-events:none;',
      'font:11px/1.45 ui-sans-serif,system-ui,-apple-system,sans-serif;',
      'box-shadow:0 6px 24px rgba(0,0,0,0.5)}',
      '#debug-hud-tip b{color:#fff}',
      '#debug-hud-tip .dh-tip-ref{display:block;margin-top:5px;padding-top:5px;',
      'border-top:1px solid #2f4a47;color:#9fc7c2}',
    ].join('');
    document.head.appendChild(st);
  }

  const dhTip = document.createElement('div');
  dhTip.id = 'debug-hud-tip';
  dhTip.hidden = true;
  document.body.appendChild(dhTip);
  onDispose(() => { dbgHud.remove(); dhTip.remove(); });

  // key -> { label, ref (short inline "best" range), help (full text),
  //          rate(arg) -> 'good' | 'warn' | 'bad' | null }
  const DH_METRICS = {
    fps: {
      label: 'FPS', ref: '60 · ≥30',
      help: 'Smoothed frames per second and frame time. 60 fps = a 16.7 ms budget per frame. Sustained drops below ~30, or a spiky frame time, mean the main thread is overloaded — GPU uploads, a late sort, or too many draw calls.',
      rate: (v) => v == null ? null : v >= 55 ? 'good' : v >= 30 ? 'warn' : 'bad',
    },
    jank: {
      label: 'stutter', ref: '0 slow/s',
      help: 'Frames that took much longer than the frame before them (over 1.5× the average and over 24 ms — e.g. a 33 ms frame among 16 ms ones), per second, over the last 2 s, and the slowest one. An average fps can look fine while these make movement go stop-go.',
      rate: (n) => n == null ? null : n === 0 ? 'good' : n <= 2 ? 'warn' : 'bad',
    },
    jankWhy: {
      label: 'slowest', ref: 'what it was',
      help: 'Where the slowest recent frame went (ms): cam = controls, stream = streaming (chunk uploads, LOD), res = resolution switch, draw = submitting the draw (includes uploading a new sort order), sort = taking in a sort result (+n = how many landed), wait = everything else — the GPU finishing the previous frame, or the browser.',
      rate: () => null,
    },
    heap: {
      label: 'JS heap', ref: 'flat · <75%',
      help: 'Used / total JS heap (Chrome/Edge only). Watch for the used figure climbing through a long walk-through and never coming back down — that is a leak. Steady state should be roughly flat.',
      rate: (r) => r == null ? null : r < 0.5 ? 'good' : r < 0.75 ? 'warn' : 'bad',
    },
    draws: {
      label: 'draws', ref: '≤10',
      help: 'Draw calls this frame and triangle count. The splat mesh is a single instanced call, so this should stay very low. A jump means extra geometry is being added to the scene. Triangles are 2 per drawn splat (each splat is a quad).',
      rate: (v) => v == null ? null : v <= 10 ? 'good' : v <= 50 ? 'warn' : 'bad',
    },
    pixelR: {
      label: 'pixelR', ref: '0.5–2× DPR',
      help: 'Renderer pixel ratio then the canvas backing-store resolution. Presets scale this below the display DPR to cut fill-rate cost. If FPS is low and this is high, drop the render-quality preset first — cost is quadratic.',
      rate: () => null,
    },
    splats: {
      label: 'splats', ref: '35–95% budget',
      help: 'Resident splat count vs the configured budget. Resident is what is uploaded to the GPU pool right now — it should sit near but a little under the usable fraction of the budget. Pinned far below = the LOD solver or the allocator is starving.',
      rate: (r) => r == null ? null : (r >= 0.3 && r <= 0.97) ? 'good' : 'warn',
    },
    leaves: {
      label: 'leaves', ref: 'res ≥ vis',
      help: 'Visible octree nodes in the frustum / total resident nodes. Visible ≫ resident means chunks in view have nothing loaded yet (grey blocks). Resident ≫ visible is memory held for off-screen chunks — normal in moderation.',
      rate: (d) => d == null ? null : d.res >= d.vis ? 'good' : 'bad',
    },
    lod: {
      label: 'lod', ref: 'L0 > 0 up close',
      help: 'Resident node count per LOD level (L0 = finest). L0:0 while standing in front of a wall means the finest level never resolves — budget too low, or the LOD distance bands too tight. Mostly L2/L3 = coarse everywhere.',
      rate: () => null,
    },
    plan: {
      label: 'plan', ref: 'cap L0 at rest',
      help: 'The streaming solver\'s last decision: cap = finest level allowed (L0 = none; (n↑) = nearest tiles allowed one finer), ×scale = how far the distance bands were pulled in to fit, use = splats planned for / on-screen draw target, p = memory pressure (1 = none), d = detail lever %, rest/move = refining at rest or holding while moving, e = visible tiles with nothing loaded (blocks refining at rest), f = lens factor on distances.',
      rate: () => null,
    },
    sort: {
      label: 'sort', ref: '< frame ms',
      help: 'Duration of the last depth sort and how many splats it sorted. The sort runs in a worker one at a time. If it exceeds the frame time, depth order lags the camera during motion — the classic smear / popping artifact.',
      rate: (d) => (d == null || d.ms == null) ? null : d.ms < d.frame ? 'good' : d.ms < d.frame * 2 ? 'warn' : 'bad',
    },
    queue: {
      label: 'queue', ref: 'wr → 0',
      help: 'Write-queue length · decodes in flight · decoded chunk files in memory. The first two spike after a big camera move and should drain toward 0 within a second or two. Write queue stuck high = GPU uploads cannot keep up (frame spikes, pop-in). The cache figure climbing without bound is a memory concern.',
      rate: (d) => d == null ? null : d.wr <= 4 ? 'good' : d.wr <= 32 ? 'warn' : 'bad',
    },
    fetch: {
      label: 'fetch', ref: 'wait ≈ 0',
      help: 'Where loading time goes, averaged over recent chunk files: network speed over the last 10 s (downloaded bytes only — 0 once everything comes from the browser cache), then per file: dl = download time, wait = time a downloaded file waited for a free decoder, dec = decode time. Large dl with wait near 0 = the connection is the limit. Large wait = the decoders are the limit (the device, or too few workers). A file must be complete before any tile in it can sharpen, so big files (large dl) mean long waits for detail.',
      rate: (d) => d == null ? null : d.waitMs < 500 ? 'good' : d.waitMs < 2000 ? 'warn' : 'bad',
    },
    allocFail: {
      label: 'allocFail', ref: 'must be 0',
      help: 'Cumulative pool allocations that could not be satisfied. Should stay 0. Each failure means a chunk kept its coarser LOD because the GPU pool had no room — detail that will not sharpen. A climbing number means the budget is too high for the pool.',
      rate: (v) => v == null ? null : v === 0 ? 'good' : 'bad',
    },
    poolFree: {
      label: 'poolFree', ref: 'largest ≈ total',
      help: 'Free pool capacity right now (splats), and the size of the single largest contiguous free block. If totalFree is substantial but largestFreeBlock is tiny, the buddy allocator has room on paper but it is fragmented into pieces too small for most requests — allocFail climbs even though the pool is not actually full. If both are near zero, the pool is genuinely full for the current budget.',
      rate: (d) => d == null || !d.total ? null : (d.largest / d.total) > 0.4 ? 'good' : (d.largest / d.total) > 0.1 ? 'warn' : 'bad',
    },
    poolUtil: {
      label: 'poolUtil', ref: 'high = good use',
      help: 'Resident splats as a fraction of the pool\'s hard capacity (not the softer `budget` target the splats row compares against). Under ?exactPack=1 (no power-of-two bucket rounding) this can approach ~100% when the view is denser than budget allows; under the default buddy allocator it is capped well below that by rounding waste even at genuine pool exhaustion — the gap between this and the splats row\'s percentage is roughly how much headroom exact-packing recovers.',
      rate: (v) => v == null ? null : v < 0.9 ? 'good' : v < 0.98 ? 'warn' : 'bad',
    },
    defrag: {
      label: 'defrag', ref: 'moves, no growth',
      help: '?defrag=1 (exact-pack only): cumulative slide count, and how many are currently queued. Should show occasional activity after a fragmented alloc failure, never runaway/climbing growth — a queue that keeps growing without landing moves means slides aren\'t keeping up with demand.',
      rate: (d) => d == null ? null : d.queued > 20 ? 'bad' : d.queued > 0 ? 'warn' : 'good',
    },
    lodDist: {
      label: 'lodDist', ref: 'per-scene',
      help: 'Base LOD distance (world units) and the geometric multiplier between level bands. Derived per scene from chunk size, or overridden with ?lodDist= / ?lodMult=. Bigger = detail stays sharp further from the camera, at the cost of more resident splats. These are the two streaming-LOD sliders in the Quality modal.',
      rate: () => null,
    },
    solverMode: {
      label: 'solver', ref: 'knapsack / exact',
      help: 'Which budget solver and pool allocator are active for this load: legacy (global distance-scale search) or knapsack (?knapsack=1, per-leaf value-ranked greedy), and buddy (power-of-two rounding) or exact (?exactPack=1, no rounding). Both new modes are opt-in Stage-1 flags — see STREAMING-SOG-STATUS.md.',
      rate: () => null,
    },
    baseL: {
      label: 'prewarm', ref: '→ shell ready',
      help: 'Coarse-first prewarm (?prewarm=0 to disable). Every leaf\'s coarsest LOD is requested on startup, not just visible ones, so the whole scene shows a blurry-but-correct shell immediately instead of the grey clear wherever nothing has streamed in yet — normal culling/freeing still applies to everything once it\'s actually been seen. "prewarm N left" = the startup sweep is still loading N leaves; "shell ready" = the whole scene has coarse coverage and only detail refinement remains; "capped (budget)" = prewarm hit PREWARM_BUDGET_FRACTION (15% of budget) and stopped early — some far leaves (often distant sky) will stay grey permanently at this budget; a higher budget gives prewarm more room before capping.',
      rate: (v) => v == null ? null : v === 'off' || v === 'capped (budget)' ? 'warn' : v === 'shell ready' ? 'good' : null,
    },
    sSplats: {
      label: 'splats', ref: 'fixed cost',
      help: 'Total splat count in the model. The whole model is resident at once (no LOD) — this is the fixed memory cost. Larger models rely on the memory fallbacks below.',
      rate: () => null,
    },
    sh: {
      label: 'SH', ref: 'deg ≤1 on mobile',
      help: 'Spherical-harmonics degree in use (or off), plus whether low-memory mode kicked in. SH adds view-dependent shading but roughly doubles GPU memory. lowMem true means the device could not fit the full model and the loader dropped SH and/or splat density.',
      rate: () => null,
    },
    gpuEst: {
      label: 'GPU est', ref: '< device VRAM',
      help: 'Estimated VRAM for the splat textures (position, covariance, colour, SH). If this is close to or above the device budget, expect the reactive fallback to .splat or a crash-and-retry with a lighter setting.',
      rate: (v) => v == null ? null : v < 500 ? 'good' : v < 1000 ? 'warn' : 'bad',
    },
    cpuData: {
      label: 'CPU data', ref: 'drops after load',
      help: 'RAM held by the CPU-side copies of the texture data during load. Freed once the textures are materialised, so a high value that drops shortly after load is expected. One that stays high = the buffers are not being released.',
      rate: () => null,
    },
    loading: {
      label: 'loading', ref: 'downloaded / total',
      help: 'Raw byte progress of the model download before textures exist, matching the on-screen loading ring.',
      rate: () => null,
    },
  };

  // Render order; '__spacer' separates the always-on group from the
  // path-specific (streamed / static / loading) group.
  const DH_ORDER = ['fps', 'jank', 'jankWhy', 'heap', 'draws', 'pixelR', '__spacer',
    'splats', 'leaves', 'lod', 'plan', 'sort', 'queue', 'fetch', 'allocFail', 'poolFree', 'poolUtil', 'defrag', 'lodDist', 'solverMode', 'baseL',
    'sSplats', 'sh', 'gpuEst', 'cpuData', 'loading'];

  const dhRows = {};
  let dhPinnedKey = null;
  let dhPinnedQ = null;
  const dhPlaceTip = (q) => {
    const r = q.getBoundingClientRect();
    const left = Math.min(r.right + 8, window.innerWidth - dhTip.offsetWidth - 8);
    const top = Math.min(r.top - 4, window.innerHeight - dhTip.offsetHeight - 8);
    dhTip.style.left = `${Math.max(8, left)}px`;
    dhTip.style.top = `${Math.max(8, top)}px`;
  };
  const dhShowTip = (key, q) => {
    const m = DH_METRICS[key];
    if (!m) return;
    dhTip.innerHTML = `<b>${m.label}.</b> ${m.help}` +
      `<span class="dh-tip-ref">Best: ${m.ref}</span>`;
    dhTip.hidden = false;
    dhPlaceTip(q);
  };
  const dhHideTip = () => { if (dhPinnedKey == null) dhTip.hidden = true; };
  const dhUnpin = () => {
    if (dhPinnedQ) dhPinnedQ.classList.remove('pin');
    dhPinnedKey = null; dhPinnedQ = null; dhTip.hidden = true;
  };

  DH_ORDER.forEach((key) => {
    if (key === '__spacer') {
      const sp = document.createElement('div');
      sp.className = 'dh-spacer';
      sp.hidden = true;
      dbgHud.appendChild(sp);
      dhRows.__spacer = { row: sp };
      return;
    }
    const m = DH_METRICS[key];
    const row = document.createElement('div');
    row.className = 'dh-row';
    row.hidden = true;
    const label = document.createElement('span');
    label.className = 'dh-label';
    label.textContent = m.label;
    const mid = document.createElement('span');
    mid.className = 'dh-mid';
    const val = document.createElement('span');
    val.className = 'dh-v';
    val.textContent = '—';
    const ref = document.createElement('span');
    ref.className = 'dh-ref';
    ref.textContent = m.ref;
    mid.append(val, ref);
    const q = document.createElement('span');
    q.className = 'dh-q';
    q.textContent = '?';
    q.tabIndex = 0;
    q.setAttribute('aria-label', `${m.label} — what this means`);
    q.addEventListener('mouseenter', () => { if (dhPinnedKey == null) dhShowTip(key, q); });
    q.addEventListener('mouseleave', dhHideTip);
    q.addEventListener('focus', () => { if (dhPinnedKey == null) dhShowTip(key, q); });
    q.addEventListener('blur', dhHideTip);
    q.addEventListener('click', (e) => {
      e.stopPropagation();
      if (dhPinnedKey === key) { dhUnpin(); return; }
      dhUnpin();
      dhPinnedKey = key; dhPinnedQ = q;
      q.classList.add('pin');
      dhShowTip(key, q);
    });
    row.append(label, mid, q);
    dbgHud.appendChild(row);
    dhRows[key] = { row, val };
  });
  // Tap / click anywhere else dismisses a pinned tooltip.
  onWindow('click', () => { if (dhPinnedKey != null) dhUnpin(); });

  // text: the value string. rateArg: passed to the metric's rate() unless
  // `rating` is given explicitly. Shows the row.
  const dhSet = (key, text, rateArg) => {
    const e = dhRows[key];
    if (!e) return;
    e.row.hidden = false;
    e.val.textContent = text;
    const m = DH_METRICS[key];
    const cls = m.rate ? m.rate(rateArg) : null;
    e.val.className = 'dh-v' + (cls ? ' ' + cls : '');
  };
  const dhHide = (...keys) => keys.forEach((k) => {
    if (dhRows[k]) dhRows[k].row.hidden = true;
  });
  // Visible only for sysAdmin users (QualityModal sets __debugHudAllowed once
  // auth resolves) AND when the flag is set. Recomputed every tick so it
  // reacts to auth loading late and to the toggle, with no stale state.
  let dbgOn = null; // null until the first sync so the initial state is applied
  const _dbgWanted = () => {
    if (!window.__debugHudAllowed) return false;
    if (queryParams.get('debugHud') === '1') return true;
    try { return localStorage.getItem('debugHud') === '1'; } catch (e) { return false; }
  };
  const _syncDbgVisibility = () => {
    const want = _dbgWanted();
    if (want === dbgOn) return;
    dbgOn = want;
    dbgHud.style.display = dbgOn ? 'block' : 'none';
    if (dbgOn) positionDbgHud();
    else dhUnpin();
  };
  onWindow('debugHudToggle', _syncDbgVisibility);

  const _mb = (n) => (n != null && Number.isFinite(n)) ? (n / 1048576).toFixed(0) : '—';
  const _grp2Streamed = ['splats', 'leaves', 'lod', 'plan', 'sort', 'queue', 'fetch', 'allocFail', 'poolFree', 'poolUtil', 'defrag', 'lodDist', 'solverMode', 'baseL'];
  const _grp2Static = ['sSplats', 'sh', 'gpuEst', 'cpuData'];
  // Stutter readout state (DEBUG overlay + [perf] console line), declared
  // before the timers that read it. var: animate() further down writes it.
  var _jankLog = []; // eslint-disable-line no-var
  var _prevWork = null; // eslint-disable-line no-var
  var _framesDrawn = 0, _framesSkipped = 0, _streakBreaks = 0; // eslint-disable-line no-var
  // WebGPU only: time from submitting a frame until the GPU finished it
  // (queue.onSubmittedWorkDone). WebGPU never makes the page wait for the
  // GPU, so the rAF-based fps can look fine while frames pile up on the GPU
  // and reach the screen unevenly — this is the real GPU frame time.
  var _gpuDoneMs = [], _gpuInFlight = 0; // eslint-disable-line no-var
  // WebGPU frame rate as the GPU really delivers it. WebKit takes frames far
  // faster than its GPU finishes them, so the frame interval alone read ~50
  // fps on a stuttering iPad — and adaptive quality never lowered anything.
  // Every GPU_PROBE_EVERY drawn frames, ask when the GPU has finished; the
  // time between two answers / GPU_PROBE_EVERY is the real time per frame
  // (just the frame interval when the GPU keeps up). Not every frame: that
  // made an iPad stutter more (see ?gpuTime below).
  var GPU_PROBE_EVERY = 4; // eslint-disable-line no-var
  // iPad tests (WebGPU): ?gpuProbe=0 turns the measurement above off (does
  // it cost anything there?); ?gpuPace=1 draws a new frame only once the GPU
  // has finished the previous one — no frames queued in WebKit, and the
  // frame time then includes the wait.
  var _gpuParams = new URLSearchParams(window.location.search); // eslint-disable-line no-var
  // ?adaptive=0 also stops every per-frame measurement (GPU probe, fps,
  // slow-frame log, ?gpuTime): to see whether the measuring itself slows the
  // scene. The DEBUG panel then shows fps as off.
  // Also off with Settings → Quality → Adaptive quality off (live, see
  // window.__setAdaptiveEnabled).
  var FRAME_MONITOR = _gpuParams.get('adaptive') !== '0' && isAdaptiveOn(); // eslint-disable-line no-var
  var GPU_PROBE = FRAME_MONITOR && _gpuParams.get('gpuProbe') !== '0'; // eslint-disable-line no-var
  var GPU_PACE = _gpuParams.get('gpuPace') === '1'; // eslint-disable-line no-var
  var _gpuPacePending = false; // eslint-disable-line no-var
  var _gpuProbeCount = 0, _gpuProbeStreak = 0, _gpuProbeLastAt = 0, _gpuProbeLastStreak = -1, _gpuFrameMs = 0; // eslint-disable-line no-var
  // Canvas size changes (resolution / navigation-quality switches) per log line.
  var _resizes = 0, _resizesTotal = 0, _lastCanvasW = 0, _lastCanvasH = 0; // eslint-disable-line no-var
  // [perf] console line every 2 s while the scene draws — on while the DEBUG
  // overlay is on, or with ?perfLog=1 (easy to copy from a tablet / phone:
  // chrome://inspect → Start logging, or Safari Web Inspector).
  {
    let perfAt = 0;
    let announced = false;
    let lastWrites = null, lastWritten = 0, lastEvicts = 0; // octree totals at the previous line
    every(() => {
      if (!(dbgOn || queryParams.get('perfLog') === '1')) { perfAt = 0; announced = false; return; }
      const now = performance.now();
      if (!announced) { announced = true; perfAt = now; _framesDrawn = 0; _framesSkipped = 0; _streakBreaks = 0; console.log('[perf] logging on — a line every 2 s while the scene draws'); return; }
      if (now - perfAt < 2000) return;
      const secs = (now - perfAt) / 1000;
      perfAt = now;
      if (_framesDrawn === 0) { _framesSkipped = 0; _streakBreaks = 0; return; } // standing still
      const w = _jankLog.filter((j) => now - j.at <= 2000).reduce((a, b) => (!a || b.dt > a.dt ? b : a), null);
      const slow = _jankLog.filter((j) => now - j.at <= 2000).length;
      const o = window.__splatOctreeStats || {};
      const f = (v) => (v == null ? '-' : Number(v).toFixed(0));
      const parts = [
        `fps ${_fps.toFixed(0)} (${_frameMs.toFixed(1)}ms)`,
        // Drawn vs skipped (nothing changed) frames per second — skipped
        // frames while moving would show as stop-go.
        `drawn ${(_framesDrawn / secs).toFixed(0)}/s skipped ${(_framesSkipped / secs).toFixed(0)}/s streak-breaks ${_streakBreaks}`,
        `slow ${(slow / secs).toFixed(1)}/s`,
        w ? `worst ${f(w.dt)}ms = cam ${f(w.cam)} stream ${f(w.stream)} res ${f(w.res)} draw ${f(w.draw)} sort ${f(w.sort)}${w.sorts ? `+${w.sorts}` : ''} wait ${f(w.wait)}` : 'no slow frames',
        `pixelR ${renderer.getPixelRatio().toFixed(2)} ${renderer.domElement.width}x${renderer.domElement.height}`,
        `resizes ${_resizes} (total ${_resizesTotal})`,
        ...(_gpuDoneMs.length ? (() => {
          const a = [..._gpuDoneMs].sort((x, y) => x - y);
          const q = (p) => a[Math.min(a.length - 1, Math.floor(p * a.length))];
          return [`gpu done median ${q(0.5).toFixed(0)}ms p90 ${q(0.9).toFixed(0)}ms max ${a[a.length - 1].toFixed(0)}ms`];
        })() : []),
        ...(window.__perfNoStream ? ['TEST no-stream'] : []), ...(window.__perfNoSort ? ['TEST no-sort'] : []),
      ];
      if (o.residentSplats != null) {
        parts.push(`splats ${(o.residentSplats / 1e6).toFixed(2)}M/${((o.budget || 0) / 1e6).toFixed(1)}M`,
          `sorted ${o.sortedSplats != null ? (o.sortedSplats / 1e6).toFixed(2) + 'M' : '-'} in ${f(o.sortMs)}ms`,
          `queue ${o.writeQueueLen ?? 0}/${o.pendingDecodes ?? 0}`, `allocFail ${o.allocFailures ?? 0}`, `detail ${o.detailPct ?? 100}%`);
        // Churn: chunks written / evicted since the last line. Fragmentation:
        // largest free block vs all free slots — a falling ratio = the pool
        // breaking into pieces too small to use.
        if (o.writesTotal != null) {
          if (lastWrites != null) {
            parts.push(`swaps ${o.writesTotal - lastWrites} chunks (${((o.writtenSplatsTotal - lastWritten) / 1e6).toFixed(2)}M) evicts ${(o.evictsTotal ?? 0) - lastEvicts}`);
          }
          lastWrites = o.writesTotal; lastWritten = o.writtenSplatsTotal; lastEvicts = o.evictsTotal ?? 0;
        }
        if (o.totalFree != null) {
          const k = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : `${(n / 1e3).toFixed(0)}k`);
          parts.push(`free ${k(o.totalFree)} largest ${k(o.largestFreeBlock ?? 0)} (${o.totalFree ? Math.round((o.largestFreeBlock ?? 0) / o.totalFree * 100) : 0}%)`,
            `leaves ${o.visibleLeaves ?? '?'}vis/${o.residentLeaves ?? '?'}res`);
        }
      }
      console.log(`[perf] ${parts.join(' · ')}`);
      _framesDrawn = 0; _framesSkipped = 0; _streakBreaks = 0; _resizes = 0; _gpuDoneMs = [];
    }, 250);
  }
  every(() => {
    _syncDbgVisibility();
    if (!dbgOn) return;
    positionDbgHud();

    // Fps of sustained drawing; "idle" while nothing on screen changes.
    if (!FRAME_MONITOR) dhSet('fps', 'off (adaptive=0)', 60);
    else if (_renderStreak < ADAPTIVE_MIN_STREAK) dhSet('fps', `idle (last ${_fps.toFixed(0)})`, 60);
    else dhSet('fps', `${_fps.toFixed(0)}  ${_frameMs.toFixed(1)}ms`, _fps);
    {
      const now = performance.now();
      while (_jankLog.length && now - _jankLog[0].at > 2000) _jankLog.shift();
      if (_renderStreak < ADAPTIVE_MIN_STREAK && !_jankLog.length) { dhHide('jank', 'jankWhy'); }
      else {
        const perSec = _jankLog.length / 2;
        const w = _jankLog.reduce((a, b) => (!a || b.dt > a.dt ? b : a), null);
        dhSet('jank', w ? `${perSec.toFixed(1)} slow/s · worst ${w.dt.toFixed(0)}ms` : '0 slow/s', Math.round(perSec));
        if (w) {
          const f = (v) => v.toFixed(0);
          dhSet('jankWhy', `cam ${f(w.cam)} · stream ${f(w.stream)} · res ${f(w.res)} · draw ${f(w.draw)} · sort ${f(w.sort)}${w.sorts ? `+${w.sorts}` : ''} · wait ${f(w.wait)}`);
        } else dhHide('jankWhy');
      }
    }
    const mem = performance.memory;
    if (mem) {
      dhSet('heap', `${_mb(mem.usedJSHeapSize)} / ${_mb(mem.jsHeapSizeLimit)}MB`,
        mem.usedJSHeapSize / mem.jsHeapSizeLimit);
    } else dhHide('heap');

    const ri = renderer.info && renderer.info.render;
    // Per-frame draw calls: WebGPURenderer's render.calls counts every
    // render since start (its per-frame figure is drawCalls); WebGL's
    // render.calls is already per frame.
    const drawCalls = ri && (ri.drawCalls ?? ri.calls);
    if (ri) dhSet('draws', `${drawCalls}  ${(ri.triangles / 1e6).toFixed(1)}M tri`, drawCalls);
    else dhHide('draws');

    dhSet('pixelR', `${renderer.getPixelRatio().toFixed(2)}  ${renderer.domElement.width}×${renderer.domElement.height}`);

    const o = window.__splatOctreeStats;
    const s = window.__splatDebugStats;
    if (o && o.residentSplats != null) {
      dhRows.__spacer.row.hidden = false;
      const budget = o.budget || 0;
      dhSet('splats', `${(o.residentSplats / 1e6).toFixed(2)}M / ${(budget / 1e6).toFixed(1)}M`,
        budget ? o.residentSplats / budget : null);
      dhSet('leaves', `${o.visibleLeaves ?? '?'} vis / ${o.residentLeaves ?? '?'} res`,
        { vis: o.visibleLeaves ?? 0, res: o.residentLeaves ?? 0 });
      if (o.leavesByLevel) {
        dhSet('lod', Object.keys(o.leavesByLevel).sort().map((k) => `L${k}:${o.leavesByLevel[k]}`).join(' '));
      } else dhHide('lod');
      const pl = window.__splatOctree?.lastPlan;
      if (pl) {
        const m = (n) => Number.isFinite(n) ? `${(n / 1e6).toFixed(1)}M` : '∞';
        dhSet('plan', `cap L${pl.floor}${pl.relief ? `(${pl.relief}↑)` : ''} ×${pl.scale.toFixed(1)} use ${m(pl.usable)}/${m(pl.drawUsable)} p${pl.pressure.toFixed(2)} d${pl.detail} ${pl.idle ? 'rest' : 'move'} e${pl.visibleEmpty} f${(pl.fov || 1).toFixed(2)}`);
      } else dhHide('plan');
      dhSet('sort', `${o.sortMs != null ? o.sortMs + 'ms' : '—'}${o.sortedSplats != null ? '  ' + (o.sortedSplats / 1e6).toFixed(2) + 'M' : ''}`,
        { ms: o.sortMs, frame: _frameMs });
      dhSet('queue', `${o.writeQueueLen ?? 0} · ${o.pendingDecodes ?? 0} · ${o.decodeCacheSize ?? 0}${o.decodeStalls ? ` · ${o.decodeStalls} stalled` : ''}`,
        { wr: o.writeQueueLen ?? 0 });
      const fe = o.fetch;
      if (fe && fe.files > 0) {
        const sec = (ms) => `${(ms / 1000).toFixed(1)}s`;
        dhSet('fetch', `${fe.mbps.toFixed(1)} MB/s · dl ${sec(fe.dlMs)} · wait ${sec(fe.waitMs)} · dec ${sec(fe.decMs)}`, fe);
      } else dhHide('fetch');
      dhSet('allocFail', `${o.allocFailures ?? 0}`, o.allocFailures ?? 0);
      if (o.totalFree != null) {
        const fk = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
        dhSet('poolFree', `${fk(o.largestFreeBlock ?? 0)} largest / ${fk(o.totalFree)} total`,
          { largest: o.largestFreeBlock ?? 0, total: o.totalFree });
      } else dhHide('poolFree');
      if (o.poolUtilization != null) dhSet('poolUtil', `${(o.poolUtilization * 100).toFixed(0)}%`, o.poolUtilization);
      else dhHide('poolUtil');
      if (o.defragEnabled) {
        dhSet('defrag', `${o.defragMoves ?? 0} moved · ${o.defragQueueLen ?? 0} queued`, { queued: o.defragQueueLen ?? 0 });
      } else dhHide('defrag');
      if (o.baseDistance) dhSet('lodDist', `${o.baseDistance}  ×${o.lodMultiplier}`);
      else dhHide('lodDist');
      if (o.knapsackMode) dhSet('solverMode', `${o.knapsackMode} / ${o.poolMode ?? 'buddy'}`);
      else dhHide('solverMode');
      const baseTxt = o.prewarmEnabled === false ? 'off'
        : o.prewarmCapped ? 'capped (budget)'
        : o.prewarmLeft ? `prewarm ${o.prewarmLeft} left` : 'shell ready';
      dhSet('baseL', baseTxt, baseTxt);
      dhHide(..._grp2Static, 'loading');
    } else if (s && s.numSplats != null) {
      dhRows.__spacer.row.hidden = false;
      dhSet('sSplats', `${(s.numSplats / 1e6).toFixed(2)}M`);
      dhSet('sh', `${s.hasSH ? 'deg ' + s.shDegree : 'off'}  lowMem ${!!s.isLowMemory}`);
      dhSet('gpuEst', `${s.gpuEstMB != null ? s.gpuEstMB.toFixed(0) + ' MB' : '—'}`, s.gpuEstMB);
      dhSet('cpuData', `${s.cpuTotalMB != null ? s.cpuTotalMB.toFixed(0) + ' MB' : '—'}`);
      dhHide(..._grp2Streamed, 'loading');
    } else if (s && s.phase) {
      dhRows.__spacer.row.hidden = false;
      dhSet('loading', `${s.phase}  ${_mb(s.downloadedMB * 1048576)} / ${_mb(s.totalMB * 1048576)} MB`);
      dhHide(..._grp2Streamed, ..._grp2Static);
    } else {
      dhRows.__spacer.row.hidden = true;
      dhHide(..._grp2Streamed, ..._grp2Static, 'loading');
    }

    if (dhPinnedKey != null && dhPinnedQ) dhPlaceTip(dhPinnedQ);
  }, 250);

  // Update the top captures link with the model ID
  //document.getElementById('viewer-top-captures').href = "/captures?modelId=" + modelId;

  // Initial camera nad target positions
  let initialCameraPosition = [0, 0, 2];
  let initialTargetPosition = [0, 0, 0];

  // Initial home point positions
  let initialCameraPositionHome = [0, 0, 2];
  let initialTargetPositionHome = [0, 0, 0];

  // Set default panning speed
  let panningSpeed = 0.2;
  // let pointsTreshold = 0.3;
  // let pointSize = 0.8;
  let pointsTreshold = 0.1;
  let pointSize = 0.4;
  let aspectRatio;

  // Bounding sphere of the hidden picking point-cloud, cached. Used to decide
  // whether a raycast hit is a real point on the model or a stray floater
  // vertex far outside it. Keyed to the model itself (NOT camera<->target
  // distance, which goes unreliable the moment the custom translate/dolly
  // controls push the camera through a wall and leave the orbit target
  // behind).
  let _pickSphere;
  function getPickCloudSphere() {
    if (_pickSphere !== undefined) return _pickSphere;
    const pts = scene.children.find(o => o && o.isPoints === true);
    const geom = pts && pts.geometry;
    if (!geom) return null; // not loaded yet — don't cache, retry next pick
    if (!geom.boundingSphere) geom.computeBoundingSphere();
    const bs = geom.boundingSphere;
    _pickSphere = (bs && Number.isFinite(bs.radius) && bs.radius > 0) ? bs : null;
    return _pickSphere;
  }

  // Add event listener for aspect radio change
  onWindow('aspectRatioChange', (event) => {
    const { activeRatio } = event.detail;
    aspectRatio = activeRatio;
    if (aspectRatio == 'None') aspectRatio = null;
    // Only the overlay (AspectRatioControls.js) and the capture crop follow
    // it — never the lens / field of view (see applyLens).
  });

  // Set custom (hardcoded) camera, target positions and panning speed
  if (modelId == 'Fabrika_EXT') {
    panningSpeed = 0.03;
    pointsTreshold = 0.01;
    pointSize = 0.02;
  // Pirtiņa vasara
  } else if (modelId == 'a85a771a-bbc6-4fcf-80aa-bec3db0d3308') {
    panningSpeed = 0.3;
    initialCameraPosition = [30, 15, 7];
    initialTargetPosition = [3, 4, -2];
  // Pirtiņa ziema
  } else if (modelId == '96c704a1-f567-4609-a866-b304337f76d1') {
    panningSpeed = 0.3;
    initialCameraPosition = [30, 15, 7];
    initialTargetPosition = [3, 4, -2];
  // Jaunā fabrika
  } else if (modelId == 'Fabrika_NEW') {
    initialCameraPosition = [46, 12, -1];
    initialTargetPosition = [12, 12, 3];
  // Latvijas gāze
  } else if (modelId == '906cc584-07e5-47a4-9b9c-41063ae3b06d') {
    panningSpeed = 0.2;
    pointSize = 0.3;
    initialCameraPosition = [15, 6, 1];
    initialTargetPosition = [4, 4, -1];
  // Lielvārdes pils
  } else if (modelId == '181fcf26-c88a-464e-bacf-da2cd18a4f88') {
    panningSpeed = 0.2;
    initialCameraPosition = [-46, 13, -5];
    initialTargetPosition = [9, 2, 0];
  // Kuģis
  } else if (modelId == '41b25d73-dd5b-4a2d-9cd3-2b33697b4520') {
    panningSpeed = 0.05;
    initialCameraPosition = [1, 0, 7];
    initialTargetPosition = [0, 0, 0];
  // Liepāja drons
  } else if (modelId == 'caa5cecf-162f-4095-a1a2-615a7cd61519') {
    pointSize = 0.2;
    panningSpeed = 0.1;
    initialCameraPosition = [21, 9, 10];
    initialTargetPosition = [9, 3, 5];
  // Latvijas Nafta
  } else if (modelId == '2ad118d3-2412-4a1c-8fc2-257c479739f1') {
    pointSize = 0.7;
    panningSpeed = 0.3;
    initialCameraPosition = [8, 11, 48];
    initialTargetPosition = [1, 3, 14];
  // Berga Bazārs Vasara
  } else if (modelId == '71546887-f33d-4a63-b78b-cc18cee2cd4c') {
    panningSpeed = 0.2;
    initialCameraPosition = [-21, 5, 18];
    initialTargetPosition = [-16, 4, 14];
  // Kubs
  } else if (modelId == '01e6a342-1dbd-40a2-9e09-0745b2f55509') {
    panningSpeed = 0.1;
    initialCameraPosition = [12, 5, 3];
    initialTargetPosition = [0, 2, 0];
  // Berga Bazārs Rudens
  } else if (modelId == '6bd25895-a5f2-4e3f-ac46-0c74b288fdb4') {
    panningSpeed = 0.2;
    initialCameraPosition = [-19, 6, 5];
    initialTargetPosition = [-4, 3, -7];
  // Mežs
  } else if (modelId == '558f5b3a-f5bf-4d10-8cd4-3a4d78d6ec9b') {
    panningSpeed = 0.2;
    initialCameraPosition = [-25, 6, -5];
    initialTargetPosition = [-6, 4, 0];
  // Fabrika NEW Lite
  } else if (modelId == '5f3289ef-0749-425e-afa7-bcebc8ef4aae') {
    panningSpeed = 0.3;
    initialCameraPosition = [47, 10, -9];
    initialTargetPosition = [5, 5, -3];
  }

  // Update home positions based on hardcoded values
  initialCameraPositionHome = initialCameraPosition;
  initialTargetPositionHome = initialTargetPosition;

  // Manual per-model background-colour override (Settings → General, admin-
  // set) — read alongside the camera default below (same DynamoDB item, one
  // round trip already happening on every load) and applied once
  // scene.background gets its default value further down, so it always wins
  // over that default. Also read by the streamed branches to skip the
  // automatic sky-colour estimate entirely when a manual value is set.
  let manualBgColorHex = null;

  // Read from database and override hardcoded values if available
  try {
    const response = await authFetch(
      `${API_URL}?id=${modelId}&defaultPosition=true`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors'
      }
    );

    const data = await response.json();

    if (data.CameraPosition && data.TargetPosition) {
      initialCameraPosition = data.CameraPosition.split(',').map(Number);
      initialTargetPosition = data.TargetPosition.split(',').map(Number);
      initialCameraPositionHome = data.CameraPosition.split(',').map(Number);
      initialTargetPositionHome = data.TargetPosition.split(',').map(Number);
    }
    if (data.BackgroundColorEnabled && data.BackgroundColor) {
      manualBgColorHex = data.BackgroundColor;
    }

  } catch (err) {
    console.error('Error getting default position in DynamoDB:', err);
  }

  // Lens, sensor, rotation and translation in the URL (Captures → Scene,
  // view links) — read here, before the params are stripped below, and
  // applied once their variables exist (see restoreViewFromUrl).
  const urlView = {
    fl: queryParams.get('fl'),
    ss: queryParams.get('ss'),
    ptr: queryParams.get('ptr'),
    ldo: queryParams.get('ldo'),
  };

  // If target positions available in URL, update the initial positions
  let cp = queryParams.get('cp');
  let tp = queryParams.get('tp');
  if (cp && tp) {
    initialCameraPosition = cp.split(',').map(Number);
    initialTargetPosition = tp.split(',').map(Number);
    // Remove cp/tp and the viewer state params that scene.js also cleans up —
    // both run pushState so whichever goes last wins; delete all here to be safe.
    queryParams.delete('cp');
    queryParams.delete('tp');
    queryParams.delete('ss');
    queryParams.delete('ar');
    queryParams.delete('fl');
    queryParams.delete('ptr');
    queryParams.delete('ldo');

    // If defaultPosition is set to true, store position in database
    if (queryParams.get('defaultPosition')) {
      console.info('Storing default camera and target positions in database');
      try {
        const response = await authFetch(
          `${API_URL}?id=${modelId}&setDefaultPosition=true&cp=${cp}&tp=${tp}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            mode: 'cors'
          }
        );

        const data = await response.json();
        shouldCaptureThumbnail = true;

      } catch (err) {
        console.error('Error setting default position in DynamoDB:', err);
      }
      queryParams.delete('defaultPosition');
    }

    // Update the URL without reloading the page
    const newUrl = `${window.location.pathname}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    history.pushState(null, '', newUrl);
  }

  // Height of the bottom bar
  let bottomBarAdjustment = 138;

  // ─── Rendering quality config ────────────────────────────────────────────────
  // Resolution (?resolution=) and Sharpness (?sharpness=) — see below.
  // Splat count is a SEPARATE control: ?splats=100|75|50 (default 50 on
  // phones/iPad, 100 on desktop) — see the SplatLoader call below.
  // Override individual values with ?covClamp=0.15&pixelRatio=1.5
  //
  // COV_CLAMP — added to the diagonal of each gaussian's projected 2D covariance.
  //   Prevents zero-size gaussians from causing shader instability.
  //   Lower = sharper, tighter splats. Higher = softer, more blurred edges.
  //   Useful range: 0.05 (very sharp, may shimmer) → 0.5 (noticeably blurry)
  //
  // PIXEL_RATIO — how many physical pixels are rendered per CSS pixel.
  //   On a retina screen devicePixelRatio=2, so 0.5× = quarter of native pixels.
  //   Cost is quadratic: doubling the ratio = 4× the GPU work per frame.
  // ────────────────────────────────────────────────────────────────────────────

  const dpr = window.devicePixelRatio;
  // Two independent controls (Settings → Quality), each Auto or manual —
  // Auto only means the app picks the value; the control means the same:
  //  - Resolution: one scale on every device — 100% = 75% of the screen's
  //    native pixels, up to 150% (pixel ratio capped at 3). ?resolution=<pct>.
  //    Auto starts at 100% on desktops / laptops, 90% on tablets, 80% on
  //    phones; on a phone the size-based tier below may pick less for a very
  //    large model; adaptive quality lowers it live.
  //  - Sharpness: a 0–100 position on the old preset scale (Low 0 ·
  //    Balanced 33 · High 67 · Best 100, utils/renderQuality.js) → covClamp
  //    only. ?sharpness=<pos or preset name>. Auto is always High: it barely
  //    changes the cost (softer = bigger splats, if anything slower), so
  //    neither the tier nor adaptive quality moves it.
  // ?pixelRatio= / ?covClamp= are raw overrides.
  const RESOLUTION_MAX_PR = 3;
  const parseResolutionPct = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) && n >= 10 && n <= 300 ? Math.round(n) : null;
  };
  // Resolution 100% = 75% of native (hard to tell from native on high-density
  // screens, and much cheaper); ~133% = every screen pixel.
  const RESOLUTION_REF_PR = dpr * 0.75;
  const RESOLUTION_MAX_PCT = Math.max(100, Math.floor(Math.min(150, RESOLUTION_MAX_PR / RESOLUTION_REF_PR * 100) / 5) * 5);
  window.__resolutionMaxPct = RESOLUTION_MAX_PCT;
  const resolutionPctOfPreset = (pos) => Math.round(qualitySettings(pos, dpr).pixelRatio / RESOLUTION_REF_PR * 100);
  const pixelRatioForPct = (pct) => +Math.min(RESOLUTION_REF_PR * pct / 100, RESOLUTION_MAX_PR).toFixed(3);
  const resolutionParamPct = parseResolutionPct(queryParams.get('resolution'));
  const sharpnessParamPos = qualityPos(queryParams.get('sharpness'));
  // What Auto resolves to (the tier below may lower the resolution once the
  // model size is known) — the menu's Auto switch returns here.
  let autoResolutionPct = isPhoneDevice ? 80 : isLowMemoryDevice ? 90 : 100;
  const AUTO_SHARPNESS_POS = QUALITY_POS.high;
  let resolutionPct = resolutionParamPct ?? autoResolutionPct;
  let sharpnessPos = sharpnessParamPos ?? AUTO_SHARPNESS_POS;
  window.__resolutionLivePct = resolutionPct;
  window.__sharpnessPos = sharpnessPos;
  // Adaptive quality changes only what the user didn't set explicitly.
  const resolutionManual = resolutionParamPct != null || queryParams.has('pixelRatio');

  // Splat count — decoupled from render quality. ?splats=10..100 (%) is a manual
  // Quality-menu pick and always wins; with no param the phone tier below
  // decides from the model size (iPad/desktop → full 100%).
  const SPLAT_PCT_MIN = 10;
  const splatsParam = queryParams.get('splats');
  const splatsNum = parseInt(splatsParam, 10);
  const splatDensityParam = splatsNum >= SPLAT_PCT_MIN && splatsNum <= 100 ? splatsNum : undefined;
  // ?splats= is the share DRAWN (static models). What's LOADED is the
  // device's Auto amount, or more if ?splats= asks for more — so the share
  // can go down and back up to that live, without a reload. ?splatsLoad=
  // caps what's loaded (set by Scene.js's crash recovery).
  const splatsLoadNum = parseInt(queryParams.get('splatsLoad'), 10);
  const splatsLoadCap = splatsLoadNum >= SPLAT_PCT_MIN && splatsLoadNum <= 100 ? splatsLoadNum : undefined;

  // ?covClamp and ?pixelRatio override the preset when provided
  // ?sh=0|1|2|3 overrides the SH degree (0 disables SH entirely); default
  // (no ?sh= param): on for desktop, off for mobile (iPad excluded), capped
  // to degree 1 on phones/iPad — see SplatLoader's constructor in Splat.ts
  const shParam = queryParams.get('sh');
  const shDegreeParam = shParam !== null && ['0', '1', '2', '3'].includes(shParam) ? parseInt(shParam, 10) : undefined;
  // ?sh= is the degree DRAWN (live, see setLiveShDegree). Loading uses at
  // least the device's Auto degree, so it can go down and back up without a
  // reload — more only if ?sh= asks for more than Auto.
  const autoShDegree = isMobileDevice ? 0 : (isLowMemoryDevice ? 1 : 3);
  const shLoadOverride = shDegreeParam !== undefined && shDegreeParam > autoShDegree ? shDegreeParam : undefined;

  const renderConfig = {
    covClamp:   queryParams.has('covClamp')   ? parseFloat(queryParams.get('covClamp'))   : qualitySettings(sharpnessPos, dpr).covClamp,
    pixelRatio: queryParams.has('pixelRatio') ? parseFloat(queryParams.get('pixelRatio')) : pixelRatioForPct(resolutionPct),
  };

  const formatParam = queryParams.get('format');

  // Set by resolveSplatUrl whenever a .splat alternative exists for this
  // model, whether or not it was actually chosen — used as a reactive
  // safety net (see onError below) if a chosen .sog crashes/fails despite
  // the proactive memory check thinking it was safe.
  let splatFallbackUrl = null;
  let triedSplatFallback = false;

  // Full getModel response (presigned URLs per format + point-cloud URL),
  // cached so the point-cloud loader below can reuse it without a second
  // round trip. Model data is no longer public-read on S3 — every URL here
  // is short-lived and only ever obtained via this canView-gated call.
  let modelData = null;

  // Always ask the backend which formats exist and get a presigned URL for
  // each. ?format= still lets a caller request a specific format (e.g. right
  // after upload, when only the just-uploaded format exists yet), but it's
  // now just a preference applied to the backend's response, not a way to
  // skip the auth check and build a URL client-side.
  // One getModel request per scene load, shared: the renderer choice below
  // needs to know whether the model is streamed (lodMetaUrl) before the
  // renderer is created, and resolveSplatUrl reuses the same response.
  let modelDataPromise = null;
  function fetchModelData() {
    if (!modelDataPromise) {
      modelDataPromise = authFetch(`${API_URL}?getModel=true&id=${modelId}`).then(async (res) => {
        if (!res.ok) throw new Error(`Failed to resolve model (status ${res.status})`);
        const data = await res.json();
        // For the Settings menu: streamed (no File format choice), known
        // before the octree exists.
        window.__streamedModel = !!data?.lodMetaUrl;
        return data;
      });
    }
    return modelDataPromise;
  }

  let captureAutoSplatUrl = null;
  async function resolveSplatUrl() {
    const data = await fetchModelData();
    modelData = data;

    const autoUrl = resolveAutoSplatUrl(data);
    captureAutoSplatUrl = autoUrl;
    if (formatParam && data.formats?.[formatParam]?.url) {
      return data.formats[formatParam].url;
    }
    return autoUrl;
  }

  // The format Auto loads on this device — see resolveSplatUrl.
  function resolveAutoSplatUrl(data) {
    // Prefer sog, but if decoding it would likely exceed this device's
    // available memory, fall back to .splat instead — proactively, before
    // downloading anything, using actual file size (not just a blanket
    // device-class check: a small sog is fine even on a phone). Only
    // applies if a .splat alternative actually exists for this model.
    const sogSize = data.formats?.sog?.size;
    const splatAvailable = !!data.formats?.splat?.url;
    if (splatAvailable) splatFallbackUrl = data.formats.splat.url;
    if (data.defaultFormat === 'sog' && splatAvailable && sogSize) {
      const estimatedDecodedBytes = sogSize * SOG_DECODE_MEMORY_MULTIPLIER;
      const availableBytes = estimateAvailableMemoryBytes();
      const SAFETY_MARGIN = 0.5; // don't plan to use more than half of estimated available memory
      if (estimatedDecodedBytes > availableBytes * SAFETY_MARGIN) {
        console.warn(
          `[Format fallback] sog decode estimated at ${(estimatedDecodedBytes / 1024 / 1024).toFixed(0)}MB, ` +
          `exceeding this device's safe memory budget — loading splat instead`
        );
        return data.formats.splat.url;
      }
    }

    return data.s3Url;
  }

  const scene = new THREE.Scene();
  const raycaster = new Raycaster();
  const camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 10000);

  // ?webgpu=1 (opt-in, Stage 1 of the WebGPU migration — see
  // /Users/uldis.liepins/.claude/plans/warm-sleeping-barto.md). Feature-
  // detected via navigator.gpu; falls through to the existing WebGLRenderer
  // whenever unavailable or not requested. WebGPURenderer needs an async
  // .init() before first use — initializeScene() is already `async
  // function`, so this just awaits inline, no restructuring needed.
  // SplatMaterial (Splat.ts) is still raw GLSL at this stage and will NOT
  // render correctly under WebGPURenderer yet — that's Stage 2's job, not
  // this one. Stage 1 only proves the renderer itself constructs,
  // initializes, and drives the existing animate() loop without throwing.
  //
  // Which renderer: Quality menu → Renderer (?renderer=webgpu|webgl, else
  // Auto) — see requestedRenderer/autoRenderer in Splat.ts. A manual pick
  // wins on every device. Static and streamed models both support WebGPU
  // (streamed pools write via GPUQueue.writeTexture — three/poolTextureWrite.js).
  // Auto differs for streamed models (autoRendererStreamed: WebGPU on
  // desktop/tablet Chrome or Firefox) — so for Auto, find out first whether
  // this model is streamed. A failed lookup just means Auto = the static
  // default here; resolveSplatUrl surfaces the error as before.
  let rendererChoice = requestedRenderer(queryParams);
  if (!rendererChoice) {
    let streamed = queryParams.get('stream') === '1';
    if (!streamed && modelId) {
      try { streamed = !!(await fetchModelData())?.lodMetaUrl; } catch (e) { /* handled in resolveSplatUrl */ }
    }
    rendererChoice = streamed ? autoRendererStreamed : autoRenderer;
  }
  const wantsWebGPU = rendererChoice === 'webgpu';
  const hasWebGPU = hasWebGPUSupport;
  let renderer;
  if (wantsWebGPU && hasWebGPU) {
    // Request the adapter's real buffer limits: WebGPU's defaults
    // (128 MiB per storage binding, 256 MiB per buffer) are too small for
    // large models' addon buffers (e.g. degree-3 SH at 6M splats ≈ 144 MB).
    const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
    const requiredLimits = adapter ? {
      maxStorageBufferBindingSize: adapter.limits.maxStorageBufferBindingSize,
      // Streamed pools' SH texture at an 8M budget is 11,724 rows (default limit 8192).
      maxTextureDimension2D: adapter.limits.maxTextureDimension2D,
      maxBufferSize: adapter.limits.maxBufferSize,
    } : undefined;
    renderer = new WebGPURenderer({
      antialias: false,
      alpha: true,
      powerPreference: 'high-performance',
      requiredLimits,
    });
    await renderer.init();
    // Linear output = no intermediate linear framebuffer: draw straight to
    // the canvas and let the splat material sRGB-encode its colour, so
    // blending happens in sRGB space exactly like WebGL. (With the default
    // sRGB output, three blends into a linear buffer first — measured
    // visibly hazier, with more floaters.) Backgrounds: see sceneColor().
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    console.log('[WebGPU] renderer initialized, backend:', renderer.backend?.isWebGPUBackend ? 'webgpu' : 'webgl2-fallback');
  } else {
    if (wantsWebGPU && !hasWebGPU) console.warn('[WebGPU] WebGPU requested but navigator.gpu is unavailable — falling back to WebGLRenderer');
    renderer = new THREE.WebGLRenderer({
      antialias: false,       // MSAA is costly and visually useless for soft alpha-blended splats
      alpha: true,
      powerPreference: 'high-performance',  // must be here at context creation, not in getContext()
    });
  }

  renderer.setPixelRatio(renderConfig.pixelRatio);

  //renderer.setSize(window.innerWidth, 500);
  container.appendChild(renderer.domElement); // Append the renderer
  // Teardown: release the GPU (WebGL: lose the context — frees every buffer
  // and texture at once; WebGPU: dispose releases the device).
  onDispose(() => {
    renderer.setAnimationLoop?.(null);
    renderer.dispose();
    if (!renderer.isWebGPURenderer) renderer.forceContextLoss?.();
    renderer.domElement.remove();
  });

  const controls = new OrbitControls(camera, renderer.domElement);

  // Allow to drag scene with right mouse click
  //controls.enablePan = true;

  // Enable/Disable controls inertia
  controls.enableDamping = true;
  controls.dampingFactor = 0.5; // Default 0.05

  // Adjust mouse drag sensitivity
  controls.rotateSpeed = 0.25;  // Default is 1.0, lower value for less sensitivity
  controls.zoomSpeed = 0.25;    // (unused — zoom is handled by zoomBy below)
  controls.panSpeed = 0.25;     // Adjust pan sensitivity

  // Zoom is handled entirely by our own wheel/pinch -> zoomBy() below, not by
  // OrbitControls. OrbitControls' dolly is multiplicative on the
  // camera<->target radius, so it decelerates to a crawl as you approach the
  // pivot and never lets you push past it — the opposite of what a
  // walkthrough viewer wants. zoomBy() gives a near-constant step instead.
  controls.enableZoom = false;

  // manualBgColorHex (set above from the same DynamoDB item as the camera
  // default — Settings → General) wins over the per-model hardcoded default.
  const defaultBgHex = modelId === 'f1524dbd-8978-4555-a669-4baa5c894b28' ? '#000000' : '#20252b';
  // Under WebGPU the renderer outputs LINEAR (so splats blend in sRGB space
  // like WebGL — see where WebGPURenderer is created), so colours written
  // straight to the canvas, like the background, must be sRGB-encoded first.
  const sceneColor = (c) => (renderer.isWebGPURenderer ? c.clone().convertLinearToSRGB() : c);
  scene.background = sceneColor(new THREE.Color(manualBgColorHex || defaultBgHex));
  // Live-apply hook for the Settings modal (General tab) — lets a change
  // take effect immediately in a session that's already loaded, without a
  // reload. hex === null/undefined resets to this model's default.
  window.__setSceneBackground = (hex) => { scene.background = sceneColor(new THREE.Color(hex || defaultBgHex)); };

  // Default camera parameters
  let rotateAngleX = 0;
  let rotateAngleY = 0;
  let rotateAngleZ = 0;

  let translatePan = 0;
  let translateTilt = 0;
  let translateDolly = 0;

  let focalLength = 18;

  // Lens: three's standard 35 mm film over the screen's longer side, so the
  // field of view follows the screen (resizing re-applies it). The chosen
  // aspect ratio never changes it — that's only the overlay drawn on the
  // screen (AspectRatioControls.js) and exactly the area a capture saves
  // (renderCaptureBlob crops the same rectangle of the view).
  const FILM_GAUGE_MM = 35;
  function applyLens() {
    camera.filmGauge = FILM_GAUGE_MM;
    camera.setFocalLength(focalLength); // also updates the projection matrix
  }
  let sensorSize = 1;

  // Apply the URL's lens, sensor, rotation and translation directly.
  // scene.js also sends them as events, but on timers (0 / 500 ms) that
  // fire before this function's listeners for them exist whenever loading
  // takes a while (they're registered after the awaits above) — the
  // indicators showed the restored values while the camera kept the
  // defaults until the next change.
  (function restoreViewFromUrl() {
    const fl = parseInt(urlView.fl, 10);
    if (fl > 0) focalLength = fl;
    const ss = Number(urlView.ss);
    if (urlView.ss && ss > 0) sensorSize = ss;
    const triple = (v) => {
      const parts = (v || '').split(',').map(Number);
      return parts.length === 3 && parts.every(Number.isFinite) ? parts : null;
    };
    const ptr = triple(urlView.ptr);
    if (ptr) [rotateAngleX, rotateAngleY, rotateAngleZ] = ptr;
    const ldo = triple(urlView.ldo);
    if (ldo) [translatePan, translateTilt, translateDolly] = ldo;
  })();

  let pointerMove = false;
  let isPinching = false;
  let isTouchDragging = false;
  let touchStartX = 0, touchStartY = 0;
  const TAP_MOVE_TOLERANCE = 14; // px
  let shouldClearCircleOnDrag = false;

  // Labeling: single-click/tap placement mode, armed by the LabelsPanel
  // React component via a 'labelPlacementMode' CustomEvent while "+ Add" is
  // active. One-shot — turns itself off after a single successful pick.
  let labelPlacementActive = false;
  const labelMarkerElements = new Map(); // labelId -> div
  let currentLabels = [];
  // Markers only show while the bottom-toolbar Labeling tool is the active
  // selection (scene.js dispatches this on every tool button click) — not
  // whenever labels happen to be loaded, which used to leave them visible
  // regardless of which tool was selected.
  let labelToolActive = false;
  onWindow('labelToolSelected', (event) => {
    labelToolActive = !!event.detail;
    updateLabelMarkerPositions();
  });

  // Which marker (if any) shows the "selected" red border — set by
  // LabelsPanel on marker click or list-row click.
  let selectedLabelId = null;
  onWindow('labelSelected', (event) => {
    selectedLabelId = event.detail?.labelId || null;
    updateLabelMarkerPositions();
  });

  function onMouseDown(event) {
    if (event.button !== 0) return;   // left click only
    pointerMove = false;
    shouldClearCircleOnDrag = true;   // mark for desktop drag
  }

  function onMouseUp(event) {
    const isDbl = event.type === 'dblclick' || (typeof event.detail === 'number' && event.detail >= 2);

    // Normalize clientX/clientY for touch
    if (event.clientX === undefined || event.clientY === undefined) {
      if (event.changedTouches && event.changedTouches.length > 0) {
        event.clientX = event.changedTouches[0].clientX;
        event.clientY  = event.changedTouches[0].clientY;
      } else if (event.touches && event.touches.length > 0) {
        event.clientX = event.touches[0].clientX;
        event.clientY  = event.touches[0].clientY;
      }
    }

    if (isPinching) { return; }

    // Allow dblclicks to proceed even if slight movement was detected
    if (!isDbl && (isTouchDragging || pointerMove)) {
      return;
    }
    if (event.changedTouches && event.changedTouches.length > 1) { return; }

    // On iPad with external mouse, a "firm" double click can report a non-left button.
    // For dblclick we skip the button==0 requirement.
    const blockForMouseButton = (!isDbl && event.type !== 'touchstart' && event.button !== undefined && event.button !== 0);
    if (blockForMouseButton) return;

    if (npl) { return; }
    if (spinnerActive) { return; }
    if (document.body.classList.contains('modal-open')) return;

    const targetElement = event.target;
    const hitUI = !!(targetElement && (targetElement.closest('#top-nav') || targetElement.closest('#bottom-nav')));
    if (hitUI) return;

    if (!isDbl && pointerMove) { return; }

    const clickedPoint = pickScenePoint(event.clientX, event.clientY);
    if (!clickedPoint) {
      // No usable surface under the cursor. Never a dead click: log it and
      // blip the feedback circle so the user sees the double-click landed.
      console.warn('[Raycaster] Focal-point pick — no surface under the cursor; double-click registered, view unchanged');
      showTapCircle(event.clientX, event.clientY);
      return;
    }
    applyFocalPoint(clickedPoint, { screenX: event.clientX, screenY: event.clientY });
  }

  // Raycasts against the splat mesh (falling back to the hidden low-poly
  // Points proxy cloud, since Gaussian splats aren't cleanly raycastable) at
  // the given screen coordinates. Shared by the double-click focal-point
  // recenter (onMouseUp above) and the labeling "click a point" placement
  // flow (onLabelPlacementClick below) — same pick, two different things
  // done with the resulting point.
  function pickScenePoint(clientX, clientY) {
    // --- NDC from renderer DOMRect (Safari toolbar safe) ---
    const rb = renderer.domElement.getBoundingClientRect();
    const ndc = {
      x: ((clientX - rb.left) / rb.width) * 2 - 1,
      y: -((clientY - rb.top) / rb.height) * 2 + 1
    };

    // Raycaster threshold scales a little with view distance — points look
    // sparser the further out you are, so a fixed threshold left gaps between
    // the decimated picking points that a click could fall straight through.
    // Capped at a few * pointSize so flying far away doesn't inflate it to
    // where the ray punches through near geometry and lands on a far wall.
    const viewDist = camera.position.distanceTo(controls.target) || 1;
    const distTerm = Math.min(viewDist * 0.012, pointSize * 6);
    raycaster.params.Points.threshold = Math.max(pointsTreshold, pointSize * 0.75, distTerm);
    raycaster.setFromCamera(new THREE.Vector3(ndc.x, ndc.y, 1), camera);

    // Primary: use your mesh (scene.children[1]) as before
    if (!scene.children[1]) { return null; }
    const secondObject = scene.children[1];

    let intersectsFocus = raycaster.intersectObjects([secondObject], true);

    // Fallback A (iPad quirk): if no hit, try a hidden Points layer (temporarily show)
    if (intersectsFocus.length === 0) {
      const pointsLayer = scene.children.find(o => o && o.isPoints === true) || null;
      if (pointsLayer && pointsLayer.visible === false) {
        pointsLayer.visible = true;
        try {
          const ptsHit = raycaster.intersectObjects([pointsLayer], true);
          if (ptsHit.length) intersectsFocus = ptsHit;
        } finally {
          pointsLayer.visible = false;
        }
      }
    }

    // Fallback B: try all visible raycastable children
    if (intersectsFocus.length === 0) {
      const candidates = scene.children.filter(o => o && typeof o.raycast === 'function' && o.visible !== false);
      const alt = raycaster.intersectObjects(candidates, true);
      if (alt.length) intersectsFocus = alt;
    }

    // Hits are sorted near→far along the ray. Prefer the first one that lands
    // inside the model's own bounding sphere (skips corrupt vertices and
    // stray floaters), then any finite hit that isn't sitting right on the
    // camera.
    //
    // If none of that yields a usable point — no hit at all, only corrupt
    // hits, or we're inside the cloud so every hit is ~0 away — return a
    // point ALONG the pick ray at a sane pivot distance instead of null. A
    // double-click then still re-aims the camera straight down that ray,
    // i.e. it centers whatever is under the cursor exactly like always; only
    // the pivot depth is approximate. A double-click must never do nothing.
    const finite = (p) => p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);
    const sphere = getPickCloudSphere();
    // "Reject a hit sitting essentially on the camera lens." Was
    // sphere.radius * 0.02 outright — fine when the cloud radius is ~10
    // (=> ~0.2), but on a large scene (radius ~700 => ~14) it rejected
    // perfectly good close-range ground picks, dropping them to the
    // far-away ray fallback below (the "every other double-click lands far"
    // bug). Cap it against view distance so it can only ever get SMALLER
    // than before: on a small scene `max(0.2, viewDist*0.1)` always wins the
    // min() and the value is unchanged; on a large scene it's held to a
    // sane fraction of how far you're currently looking.
    const minUseful = sphere
      ? Math.min(sphere.radius * 0.02, Math.max(0.2, viewDist * 0.1))
      : 0.05;
    const usable = (h) => finite(h.point) && h.distance > minUseful;
    const inModel = (h) => usable(h) && (!sphere || h.point.distanceTo(sphere.center) <= sphere.radius * 1.4);

    const hit = intersectsFocus.find(inModel) || intersectsFocus.find(usable);

    const pivotDist = Math.max(
      camera.position.distanceTo(controls.target),
      sphere ? sphere.radius * 0.15 : 1
    );
    const fallbackPoint = raycaster.ray.origin.clone().addScaledVector(raycaster.ray.direction, pivotDist);
    const result = hit ? hit.point : fallbackPoint;

    if (queryParams.get('pickDebug') === '1') {
      const fmt = (v) => v && v.toArray ? v.toArray().map((n) => +n.toFixed(2)) : v;
      const pc = scene.children.find(o => o && o.isPoints === true);
      const info = {
        camPos: fmt(camera.position),
        target: fmt(controls.target),
        viewDist: +viewDist.toFixed(1),
        rayOrigin: fmt(raycaster.ray.origin),
        rayDir: fmt(raycaster.ray.direction),
        camNearFar: [camera.near, camera.far],
        threshold: +raycaster.params.Points.threshold.toFixed(2),
        minUseful: +minUseful.toFixed(2),
        sphere: sphere ? { c: fmt(sphere.center), r: +sphere.radius.toFixed(1) } : null,
        pcXform: pc ? { pos: fmt(pc.position), scale: fmt(pc.scale), mwId: pc.matrixWorld.equals(new THREE.Matrix4()) } : null,
        children: scene.children.map((o) => `${o.type}${o.name ? '#' + o.name : ''}${o.isPoints ? '(P)' : ''} vis=${o.visible}`),
        rawHits: intersectsFocus.slice(0, 6).map((h) => ({ d: +h.distance.toFixed(1), dRay: h.distanceToRay != null ? +h.distanceToRay.toFixed(2) : null, p: fmt(h.point), obj: h.object && h.object.name })),
        totalHits: intersectsFocus.length,
        chosen: hit ? 'RAYCAST HIT' : 'RAY FALLBACK (missed / all rejected)',
        result: fmt(result),
        resultDist: +camera.position.distanceTo(result).toFixed(1),
      };
      window.__lastPick = info;
      console.log('[pick]', JSON.stringify(info));
    }

    return result;
  }

  // Guard against corrupt/outlier vertices in the source point cloud (NaN,
  // Infinity, or a stray point far outside the model). Applying one of these
  // directly to camera.position/controls.target permanently corrupts
  // OrbitControls' internal spherical state — every subsequent frame's
  // controls.update() builds on the already-NaN value with no way to recover
  // short of an explicit .set() (see the 'h' / resetCamera handler).
  // Exposed on window so the guard can be exercised from the console without
  // needing to find/re-click an actual bad vertex, e.g.:
  //   window.__testBadFocalPoint(NaN, 0, 0)
  //   window.__testBadFocalPoint(1e8, 1e8, 1e8)
  function applyFocalPoint(clickedPoint, { showCircle = true, screenX, screenY } = {}) {
    if (!clickedPoint) {
      return false;
    }

    const isFinitePoint = Number.isFinite(clickedPoint.x) && Number.isFinite(clickedPoint.y) && Number.isFinite(clickedPoint.z);
    const pickDistance = isFinitePoint ? camera.position.distanceTo(clickedPoint) : NaN;

    // A double-click is NEVER a dead click and NEVER changes the zoom level.
    // If the pick isn't usable — a non-finite vertex, or a point sitting
    // essentially on the camera (you've flown through the cloud), which
    // can't be aimed at without collapsing OrbitControls' spherical state —
    // we log why, blip the feedback circle so the user sees the click
    // registered, and leave the view exactly as it is. No rejection, no
    // silent nothing.
    if (!isFinitePoint || pickDistance < 1e-3) {
      const why = !isFinitePoint ? 'non-finite vertex' : 'pick landed on the camera';
      console.warn(`[Raycaster] Focal-point pick unusable (${why}); double-click registered, view unchanged`);
      if (showCircle) showTapCircle(screenX, screenY);
      return false;
    }

    // Reset roll and translations (unchanged)
    rotateAngleX = 0; rotateAngleY = 0; rotateAngleZ = 0;
    translatePan = 0; translateTilt = 0; translateDolly = 0;
    window.dispatchEvent(new CustomEvent('resetRollAndTranslate'));

    // The camera stays exactly where it is; only the orbit target moves onto
    // the clicked point (zoom level never changes on a double-click).
    const newPosition = camera.position.clone();
    const newTarget = clickedPoint.clone();
    if (showCircle) updateOrCreateCircle(clickedPoint);
    // Own, independent, faster duration — NOT startCameraTween's default,
    // which is tuned for label-to-label navigation (1000-1600ms) and made
    // this recenter feel sluggish as a side effect of unrelated label-speed
    // feedback. Original pre-labeling-feature feel: 200ms floor, 900ms
    // ceiling.
    const focalTweenDuration = computeTweenDuration(camera.position, controls.target, newPosition, newTarget, 200, 900, 260);
    startCameraTween(newPosition, newTarget.clone(), { duration: focalTweenDuration, turnInPlace: true });
    // The camera doesn't actually reach newPosition until the tween
    // finishes, so callers that need "the resulting camera position" (the
    // labeling flow, to save alongside a new label) must use this return
    // value rather than reading camera.position synchronously afterward.
    return newPosition;
  }

  window.__testBadFocalPoint = (x, y, z) => applyFocalPoint(new THREE.Vector3(x, y, z));

  // World point the visible .circle currently marks, re-projected to screen
  // space every frame (see updateActiveCirclePosition) so it stays glued to
  // that point as the camera tweens toward centering it — instead of
  // staying put at its initial click-time pixel position while the actual
  // point it's supposed to mark visibly slides away toward center.
  let activeCircleWorldPoint = null;

  // Fade/remove timers for the .circle element, tracked so a stale pair from
  // an earlier point can be cancelled when the circle is reused for a new
  // one — otherwise an old removal timer can fire mid-life on the freshly
  // shown circle and yank it away with zero fade (the "disappears abruptly"
  // bug: every call used to schedule its own uncancelled setTimeout chain).
  let circleFadeTimer = null;
  let circleRemoveTimer = null;
  function cancelCircleFadeTimers() {
    if (circleFadeTimer) { clearTimeout(circleFadeTimer); circleFadeTimer = null; }
    if (circleRemoveTimer) { clearTimeout(circleRemoveTimer); circleRemoveTimer = null; }
  }

  function updateOrCreateCircle(newFocalPoint, isResize = false) {
    let circle = document.querySelector('.circle');
    if (!circle) {
      // Create the circle element if it doesn't exist
      circle = document.createElement('div');
      circle.classList.add('circle');
      document.body.appendChild(circle);
    }

    // Project the new focal point into screen space
    const screenPosition = new THREE.Vector3().copy(newFocalPoint).project(camera);

    // Get the renderer's DOM element dimensions
    const rendererBounds = renderer.domElement.getBoundingClientRect();

    // Calculate the position of the circle relative to the renderer
    const circleSize = 40; // Adjust size as needed
    const windowX = (screenPosition.x + 1) * 0.5 * rendererBounds.width + rendererBounds.left - (circleSize / 2);
    const windowY = (-screenPosition.y + 1) * 0.5 * rendererBounds.height + rendererBounds.top - (circleSize / 2);

    if (windowX == -20 && windowY == -20) return;
    if (!windowX || !windowY || isNaN(windowX) || isNaN(windowY)) {
      return;
    }

    // Update the circle's position
    circle.style.left = `${windowX}px`;
    circle.style.top = `${windowY}px`;

    // Set circle size
    circle.style.width = `${circleSize}px`;
    circle.style.height = `${circleSize}px`;

    // Set circle border
    const borderWidth = 2; // Adjust border width as needed
    circle.style.borderWidth = `${borderWidth}px`;
    circle.style.borderStyle = 'solid';
    circle.style.borderColor = '#69edf9'; // Adjust border color as needed

    // Set circle opacity — snap instantly (no transition), cancelling any
    // in-flight fade so a reused circle always starts fully, abruptly visible.
    cancelCircleFadeTimers();
    circle.style.transition = 'none';
    circle.style.opacity = '1'; // Adjust opacity as needed

    activeCircleWorldPoint = newFocalPoint.clone();

    // Fade out the circle after 1 second
    circleFadeTimer = setTimeout(() => {
      circle.style.transition = 'opacity 1s';
      circle.style.opacity = '0';
      circleRemoveTimer = setTimeout(() => {
        // Remove the circle from the DOM after fading out
        circle.remove();
        circleFadeTimer = null;
        circleRemoveTimer = null;
      }, 1000);
    }, 1000);

  }

  // Feedback-only circle drawn at raw screen coordinates — used when a
  // double-click / tap did NOT produce a usable focal point (no surface hit,
  // or the pick landed on the camera). It just tells the user "your
  // double-click registered, the view isn't moving". Not world-anchored, so
  // it does not get re-projected each frame (activeCircleWorldPoint is
  // cleared); it simply blips and fades where the finger/cursor was.
  function showTapCircle(clientX, clientY) {
    if (clientX == null || clientY == null || isNaN(clientX) || isNaN(clientY)) return;
    let circle = document.querySelector('.circle');
    if (!circle) {
      circle = document.createElement('div');
      circle.classList.add('circle');
      document.body.appendChild(circle);
    }
    const circleSize = 40;
    circle.style.left = `${clientX - circleSize / 2}px`;
    circle.style.top = `${clientY - circleSize / 2}px`;
    circle.style.width = `${circleSize}px`;
    circle.style.height = `${circleSize}px`;
    circle.style.borderWidth = '2px';
    circle.style.borderStyle = 'solid';
    circle.style.borderColor = '#69edf9';
    cancelCircleFadeTimers();
    circle.style.transition = 'none';
    circle.style.opacity = '1';
    activeCircleWorldPoint = null; // pure screen blip, don't track/re-project it
    circleFadeTimer = setTimeout(() => {
      circle.style.transition = 'opacity 1s';
      circle.style.opacity = '0';
      circleRemoveTimer = setTimeout(() => {
        circle.remove();
        circleFadeTimer = null;
        circleRemoveTimer = null;
      }, 1000);
    }, 1000);
  }

  // Ticked every animate() frame — cheap no-op once the circle is gone
  // (checked live rather than tracked through every removal call site:
  // fade timeout above, drag-clear in onPointerMove, camera-move clear in
  // the controls 'change' listener further down).
  function updateActiveCirclePosition() {
    if (!activeCircleWorldPoint) return;
    const circle = document.querySelector('.circle');
    if (!circle) {
      activeCircleWorldPoint = null;
      return;
    }

    const screenPosition = new THREE.Vector3().copy(activeCircleWorldPoint).project(camera);
    const rendererBounds = renderer.domElement.getBoundingClientRect();
    const circleSize = 40;
    const windowX = (screenPosition.x + 1) * 0.5 * rendererBounds.width + rendererBounds.left - (circleSize / 2);
    const windowY = (-screenPosition.y + 1) * 0.5 * rendererBounds.height + rendererBounds.top - (circleSize / 2);
    if (!Number.isFinite(windowX) || !Number.isFinite(windowY)) return;

    circle.style.left = `${windowX}px`;
    circle.style.top = `${windowY}px`;
  }

  function onPointerMove(event) {
    pointerMove = true;
    if (event.pointerType === 'touch') isTouchDragging = true;
    if (event.buttons === 1 && shouldClearCircleOnDrag) {
      clearCircle();
      shouldClearCircleOnDrag = false;
    }
  }

  // --- Labeling ---------------------------------------------------------

  // Single click/tap while label placement mode is armed: pick a point with
  // the same raycast used for focal-point recentering, center the camera on
  // it (applyFocalPoint, reused as-is), then hand the picked point + the
  // camera position it produced to the React LabelsPanel so it can open the
  // title/description form. One-shot: disarms itself after a successful pick.
  function onLabelPlacementClick(event) {
    if (!labelPlacementActive) return;
    if (event.button !== undefined && event.button !== 0) return;
    if (document.body.classList.contains('modal-open')) return;
    // Same drag-vs-click distinction the double-click focal-point recenter
    // relies on: pointerMove is set the moment the mouse moves after
    // mousedown, so a click-drag-release (orbiting the splat) never lands
    // here as a point pick — only a genuine short click does.
    if (pointerMove || isTouchDragging) return;

    const targetElement = event.target;
    const hitUI = !!(targetElement && (targetElement.closest('#top-nav') || targetElement.closest('#bottom-nav') || targetElement.closest('.labels-panel')));
    if (hitUI) return;

    const point = pickScenePoint(event.clientX, event.clientY);
    if (!point) return;

    // showCircle: false — a label pick shows a real numbered marker at this
    // exact spot instead (via LabelsPanel's 'labelsUpdated' preview entry
    // once the create form opens), so the plain focal-point circle would
    // just be redundant/wrong here rather than something to clean up after.
    const resultingCameraPosition = applyFocalPoint(point, { showCircle: false });
    if (!resultingCameraPosition) return;

    setLabelPlacementActive(false);
    window.dispatchEvent(new CustomEvent('labelPointPicked', {
      // resultingCameraPosition (not camera.position) — the camera is still
      // mid-tween toward it at this exact moment, not there yet.
      detail: { position: point.clone(), cameraPosition: resultingCameraPosition.clone() }
    }));
  }

  // Fires on lift rather than touch-down — a touchstart-based trigger would
  // place a label the instant a finger lands, before the user even gets a
  // chance to drag/orbit. isTouchDragging (tracked by handleTouchMove below)
  // gives the same short-tap-vs-drag distinction the mouse path gets from
  // pointerMove.
  function onLabelPlacementTouchEnd(event) {
    if (!labelPlacementActive || isTouchDragging || isPinching) return;
    if (!event.changedTouches || event.changedTouches.length !== 1) return;
    const t = event.changedTouches[0];
    onLabelPlacementClick({ button: 0, clientX: t.clientX, clientY: t.clientY, target: event.target });
  }

  let labelPlacementCursor = null;
  function onLabelPlacementCursorMove(event) {
    if (!labelPlacementCursor) return;
    labelPlacementCursor.style.left = `${event.clientX}px`;
    labelPlacementCursor.style.top = `${event.clientY}px`;
  }

  function setLabelPlacementActive(active) {
    labelPlacementActive = active;
    document.body.classList.toggle('label-placement-mode', active);
    if (active && !labelPlacementCursor) {
      labelPlacementCursor = document.createElement('div');
      labelPlacementCursor.className = 'label-placement-cursor';
      document.body.appendChild(labelPlacementCursor);
      onWindow('mousemove', onLabelPlacementCursorMove);
    } else if (!active && labelPlacementCursor) {
      window.removeEventListener('mousemove', onLabelPlacementCursorMove);
      labelPlacementCursor.remove();
      labelPlacementCursor = null;
    }
  }

  onWindow('labelPlacementMode', (event) => setLabelPlacementActive(!!event.detail));

  // Numbered on-screen markers, one per label — same screen-space projection
  // as the single focal-point .circle above, but persistent (re-projected
  // every animate() frame) and clickable.
  function renderLabelMarkers(labels) {
    currentLabels = Array.isArray(labels) ? labels : [];

    const seenIds = new Set(currentLabels.map(l => l.labelId));
    for (const [labelId, el] of labelMarkerElements) {
      if (!seenIds.has(labelId)) {
        el.remove();
        labelMarkerElements.delete(labelId);
      }
    }

    currentLabels.forEach((label, index) => {
      let el = labelMarkerElements.get(label.labelId);
      if (!el) {
        el = document.createElement('div');
        el.className = 'label-marker';
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent('labelMarkerClicked', { detail: { labelId: label.labelId } }));
        });
        // #root is position:fixed (index.html), which — unlike
        // position:relative/absolute — always establishes a new stacking
        // context even with z-index:auto. Appending to document.body put
        // the marker OUTSIDE that context, so no z-index on the header/
        // footer/panel (all inside #root) could ever out-stack it, no
        // matter how high. Appending inside #root instead puts the marker
        // in the same stacking context as everything else, so normal
        // z-index comparison finally applies.
        (document.getElementById('root') || document.body).appendChild(el);
        labelMarkerElements.set(label.labelId, el);
      }
      el.textContent = String(index + 1);
    });

    updateLabelMarkerPositions();
  }

  // Title/description tooltip that follows the selected marker around —
  // built once, then just repositioned/re-shown each frame in
  // updateLabelMarkerPositions below (same pattern as the markers
  // themselves). Lives inside #root for the same stacking-context reason
  // markers do (see the note on appendChild below).
  //
  // Body + tail are ONE clip-path('path(...)') shape on this single element
  // rather than a rounded rect plus a separate arrow element: two elements
  // each independently blurring their own sliver of backdrop kept producing
  // a visibly different shade right at the seam/tip, however the overlap or
  // per-element blur was tuned. One shape = one paint operation, so a seam
  // is structurally impossible.
  const TOOLTIP_RADIUS = 8;
  const TOOLTIP_TAIL_W = 16;
  const TOOLTIP_TAIL_H = 8;
  const TOOLTIP_GAP = 6;
  // Keep the tooltip this far from the viewport edge; if it's too tall to
  // fit above OR below the marker within that margin, cap its height to the
  // roomier side and scroll the description.
  const TOOLTIP_SCREEN_MARGIN = 10;
  const TOOLTIP_MIN_H = 96;

  let selectedTooltipEl = null;
  function getSelectedTooltipEl() {
    if (selectedTooltipEl) return selectedTooltipEl;
    selectedTooltipEl = document.createElement('div');
    selectedTooltipEl.className = 'label-tooltip';
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'label-tooltip-close';
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      selectedLabelId = null;
      // Without this, re-selecting the SAME label (the only way to reopen
      // it when there's just one label) leaves tooltipCacheKey unchanged,
      // so updateSelectedTooltip's "content changed" branch — the only
      // place that sets display back to 'block' — never runs again, and
      // the tooltip stays hidden for good.
      tooltipCacheKey = null;
      tooltipCache = null;
      updateLabelMarkerPositions();
      // Keeps LabelsPanel's own selection state (list row highlight) in
      // sync, mirroring the 'labelSelected' event it dispatches on select.
      window.dispatchEvent(new CustomEvent('labelDeselected'));
    });
    const title = document.createElement('div');
    title.className = 'label-tooltip-title';
    const desc = document.createElement('div');
    desc.className = 'label-tooltip-desc';
    selectedTooltipEl.appendChild(closeBtn);
    selectedTooltipEl.appendChild(title);
    selectedTooltipEl.appendChild(desc);
    (document.getElementById('root') || document.body).appendChild(selectedTooltipEl);
    return selectedTooltipEl;
  }

  // SVG path for a rounded rect with a triangular tail cut into one edge —
  // traced clockwise from the top-left corner. tailSide is where the tail
  // points FROM (i.e. 'bottom' = tail hangs off the bottom edge, box sits
  // above the marker; 'top' = tail off the top edge, box sits below it).
  // tailCenterX/tailW/tailH describe the tail itself; the box's own
  // (w, h) already includes the tail's protrusion on that one side.
  function roundedRectWithTailPath(w, h, r, tailSide, tailCenterX, tailW, tailH) {
    const half = tailW / 2;
    if (tailSide === 'bottom') {
      const bodyH = h - tailH;
      return `M ${r} 0 H ${w - r} A ${r} ${r} 0 0 1 ${w} ${r} V ${bodyH - r} A ${r} ${r} 0 0 1 ${w - r} ${bodyH} `
        + `H ${tailCenterX + half} L ${tailCenterX} ${h} L ${tailCenterX - half} ${bodyH} `
        + `H ${r} A ${r} ${r} 0 0 1 0 ${bodyH - r} V ${r} A ${r} ${r} 0 0 1 ${r} 0 Z`;
    }
    return `M ${r} ${tailH} H ${tailCenterX - half} L ${tailCenterX} 0 L ${tailCenterX + half} ${tailH} `
      + `H ${w - r} A ${r} ${r} 0 0 1 ${w} ${tailH + r} V ${h - r} A ${r} ${r} 0 0 1 ${w - r} ${h} `
      + `H ${r} A ${r} ${r} 0 0 1 0 ${h - r} V ${tailH + r} A ${r} ${r} 0 0 1 ${r} ${tailH} Z`;
  }

  // markerX/Y/Size are the already-computed screen rect of the selected
  // marker for this frame (or null if it isn't currently visible), so the
  // tooltip always sits right next to it, flipping above/below and
  // clamping horizontally to stay on screen.
  // Which label the cached tw/th/fitsBelow below belong to — lets repeated
  // calls for the SAME selection (every animate() frame, e.g. throughout
  // the whole camera tween) skip the expensive part entirely.
  // Keyed on labelId PLUS title/description (not just labelId) — editing a
  // label's text while it's still the selected one is a same-id update, and
  // an id-only key left the tooltip showing the stale pre-edit text until
  // the selection changed and came back. Content changing is exactly what
  // should invalidate this cache, same as the id itself changing.
  let tooltipCacheKey = null;
  let tooltipCache = null; // { tw, th, fitsBelow }

  function updateSelectedTooltip(markerX, markerY, markerSize) {
    const label = selectedLabelId && currentLabels.find(l => l.labelId === selectedLabelId);
    if (!label || markerX === null) {
      if (selectedTooltipEl) selectedTooltipEl.style.display = 'none';
      tooltipCacheKey = null;
      tooltipCache = null;
      return;
    }

    // Stay hidden for the whole camera flight rather than tracking the
    // marker's in-between screen position every frame — that tracking is
    // exactly what read as "sliding" (worse on mobile, where the per-frame
    // reflow this used to also do made it doubly obvious). It reappears
    // instantly, already correctly positioned, the moment the tween ends.
    // Checks the independent deadline (see cameraTweenActiveUntil above),
    // not `cameraTween` itself.
    if (performance.now() < cameraTweenActiveUntil) {
      if (selectedTooltipEl) selectedTooltipEl.style.display = 'none';
      return;
    }

    const tip = getSelectedTooltipEl();
    const markerCenterX = markerX + markerSize / 2;

    // Always restore visibility here, independent of the cache-skip check
    // below — display:'none' can be set above (during a camera tween) even
    // when the label id hasn't changed, e.g. re-clicking an already-selected
    // marker still fires a fresh (if short) goToLabel tween. Tying the
    // display:'block' restore to the "label changed" branch alone left the
    // tooltip hidden forever after any such reselect.
    tip.style.display = 'block';

    // Content, size, and which side it sits on only ever need recomputing
    // when the SELECTED LABEL changes — not every frame. Toggling padding
    // and reading offsetWidth/offsetHeight forces a synchronous layout
    // reflow; doing that 60x/second for the entire duration a tooltip is
    // open (including throughout the ~700ms camera tween between labels)
    // was the actual cause of the sluggish, sliding-looking movement on
    // mobile — desktop's faster CPU/GPU just absorbed it invisibly.
    // innerHeight is in the key so a resize / device rotation re-runs the
    // fit-and-cap math below rather than reusing a now-wrong height.
    const cacheKey = `${label.labelId}::${label.title || ''}::${label.description || ''}::${window.innerHeight}`;
    if (cacheKey !== tooltipCacheKey) {
      tip.querySelector('.label-tooltip-title').textContent = label.title || '';
      const descEl = tip.querySelector('.label-tooltip-desc');
      if (label.description) {
        descEl.textContent = label.description;
        descEl.style.display = 'block';
      } else {
        descEl.style.display = 'none';
      }

      // Clear any prior height cap, then measure the content box at its
      // natural height (symmetric 12px padding, no tail yet).
      descEl.style.maxHeight = '';
      descEl.style.overflowX = '';
      descEl.style.overflowY = '';
      tip.style.paddingTop = '12px';
      tip.style.paddingBottom = '12px';
      const neededH = tip.offsetHeight + TOOLTIP_TAIL_H; // once the tail padding lands

      // The tooltip must stay clear of the footer (it overlays the 3D view,
      // z-index 10, Scene.js) or it and its scrollbar end up hidden behind
      // it. Measured here, inside the cache-miss branch, so the per-frame
      // path stays free of layout reads.
      const footerEl = document.getElementById('html-container2');
      const footerRect = footerEl ? footerEl.getBoundingClientRect() : null;
      const bottomEdge = footerRect && footerRect.height > 0 ? footerRect.top : window.innerHeight;

      // Always below the marker — never flipped above, where it overlaps
      // the Labels panel at the top (on a phone especially; the flip used to
      // happen whenever the tooltip didn't fit below — a low marker or a
      // long description). If it's taller than the room below, its height
      // is capped to that room (at least TOOLTIP_MIN_H) and the description
      // scrolls inside.
      const roomBelow = bottomEdge - (markerY + markerSize) - TOOLTIP_GAP - TOOLTIP_SCREEN_MARGIN;
      const fitsBelow = true;
      const capH = neededH > roomBelow ? Math.max(TOOLTIP_MIN_H, Math.floor(roomBelow)) : 0;

      if (fitsBelow) {
        tip.style.paddingTop = `${12 + TOOLTIP_TAIL_H}px`;
      } else {
        tip.style.paddingBottom = `${12 + TOOLTIP_TAIL_H}px`;
      }

      if (capH) {
        // Shrink the description so the whole tooltip ends up exactly capH
        // tall; chromeH is everything else (title, paddings incl. tail, gap).
        const chromeH = tip.offsetHeight - descEl.offsetHeight;
        descEl.style.maxHeight = `${Math.max(32, capH - chromeH)}px`;
        descEl.style.overflowX = 'hidden';
        descEl.style.overflowY = 'auto';
      }

      tooltipCache = { tw: tip.offsetWidth, th: tip.offsetHeight, fitsBelow };
      tooltipCacheKey = cacheKey;
      tip.classList.toggle('tail-top', fitsBelow);
    }

    // Cheap from here on: no layout reads, just arithmetic + style writes
    // (position + a clip-path recompute, a repaint rather than a reflow) so
    // the tail keeps tracking the marker horizontally every frame.
    const { tw, th, fitsBelow } = tooltipCache;
    const top = fitsBelow ? markerY + markerSize + TOOLTIP_GAP : markerY - th - TOOLTIP_GAP;

    let left = markerCenterX - tw / 2;
    left = Math.max(8, Math.min(window.innerWidth - tw - 8, left));

    const minTailX = TOOLTIP_RADIUS + TOOLTIP_TAIL_W / 2 + 2;
    const maxTailX = tw - TOOLTIP_RADIUS - TOOLTIP_TAIL_W / 2 - 2;
    const tailCenterX = Math.max(minTailX, Math.min(maxTailX, markerCenterX - left));

    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
    tip.style.clipPath = `path('${roundedRectWithTailPath(tw, th, TOOLTIP_RADIUS, fitsBelow ? 'top' : 'bottom', tailCenterX, TOOLTIP_TAIL_W, TOOLTIP_TAIL_H)}')`;
  }

  function updateLabelMarkerPositions() {
    if (!labelMarkerElements.size) return;
    // Hide every marker (not just the tooltip) while a point pick is armed
    // — whether "+ Add" or an existing label's "Select new point" — so the
    // numbered circles aren't cluttering/overlapping the exact spot the
    // admin is trying to click on the model.
    if (!labelToolActive || labelPlacementActive) {
      labelMarkerElements.forEach((el) => { el.style.display = 'none'; });
      if (selectedTooltipEl) selectedTooltipEl.style.display = 'none';
      return;
    }
    const rendererBounds = renderer.domElement.getBoundingClientRect();
    const markerSize = 26;
    let selectedX = null, selectedY = null;

    currentLabels.forEach((label) => {
      const el = labelMarkerElements.get(label.labelId);
      if (!el || !label.position) return;

      const worldPos = new THREE.Vector3(label.position.x, label.position.y, label.position.z);
      const screenPosition = worldPos.project(camera);

      // Behind the camera — hide instead of projecting to a bogus screen spot.
      if (screenPosition.z > 1) {
        el.style.display = 'none';
        return;
      }

      const x = (screenPosition.x + 1) * 0.5 * rendererBounds.width + rendererBounds.left - (markerSize / 2);
      const y = (-screenPosition.y + 1) * 0.5 * rendererBounds.height + rendererBounds.top - (markerSize / 2);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        el.style.display = 'none';
        return;
      }

      el.style.display = 'flex';
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.width = `${markerSize}px`;
      el.style.height = `${markerSize}px`;
      el.style.background = label.labelId === selectedLabelId ? '#FF2323' : '#000';
      // Dim every other marker while one is selected, so the selected one
      // (and its number) reads as clearly picked out — back to fully
      // visible for everyone once nothing is selected.
      el.style.opacity = selectedLabelId && label.labelId !== selectedLabelId ? '0.45' : '1';

      if (label.labelId === selectedLabelId) {
        selectedX = x;
        selectedY = y;
      }
    });

    updateSelectedTooltip(selectedX, selectedY, markerSize);
  }

  onWindow('labelsUpdated', (event) => renderLabelMarkers(event.detail));

  // Smoothly flies the camera to a new position/target instead of snapping
  // instantly — shared by both the double-click focal-point recenter
  // (applyFocalPoint) and label-to-label navigation (goToLabel below).
  // Ticked every animate() frame (see updateCameraTween below), and
  // cancelled the moment the user takes over the controls themselves (see
  // the 'start' listener further down).
  let cameraTween = null;
  // Independent wall-clock deadline, set alongside `cameraTween` but never
  // read or written by updateCameraTween — a plain, no-branches "has enough
  // time passed" check the tooltip visibility relies on (see
  // updateSelectedTooltip), instead of the `cameraTween` object's own
  // lifecycle. The two SHOULD always agree, but keeping the "is it safe to
  // show the tooltip yet" signal decoupled means a bug in the tween's own
  // completion detection can no longer leave the tooltip permanently
  // hidden — worst case it just waits out this fixed deadline regardless.
  let cameraTweenActiveUntil = 0;

  // Scales a tween's duration with how far the camera actually has to
  // travel, relative to the CURRENT view distance rather than raw world
  // units — keeps it working the same regardless of the model's own scale
  // (a huge building vs. a small object). floorMs/ceilingMs are caller-
  // specific: label-to-label navigation and the focal-point circle
  // (double-click recenter) want very different pacing and are tuned
  // independently (see their respective call sites) — sharing one duration
  // curve between them meant every round of feedback on label-flight speed
  // also slowed down the focal-point circle as a side effect.
  // Symmetric on purpose: averages the START point's own zoom distance with
  // the END point's zoom distance (not just the start's) so A->B and B->A
  // compute the identical travelRatio, and therefore the same duration,
  // regardless of direction.
  // exponent > 1 makes the duration ramp up progressively with distance
  // (far flights get disproportionately more time); exponent 1 = linear.
  //
  // "Travel" is NOT just |startCamPos - endCamPos|. What actually makes a
  // flight feel fast (and outruns splat streaming, showing grey) is how far
  // the *view* swings — two labels can be metres apart yet sit on opposite
  // sides of the model, so the camera barely translates but the whole scene
  // whips 180° across the screen. Conversely a long dolly straight down the
  // same view direction covers lots of world distance but barely changes
  // what's on screen and should feel quick. So travelRatio blends a
  // down-weighted linear term with the angle between the start and end view
  // directions (a 90° swing counts as ~one reference distance of travel).
  function computeTweenDuration(startCamPos, startTarget, endCamPos, endTarget, floorMs, ceilingMs, coefficient = 520, exponent = 1) {
    const startRefDistance = startCamPos.distanceTo(startTarget) || 1;
    const endRefDistance = endCamPos.distanceTo(endTarget) || 1;
    const referenceDistance = (startRefDistance + endRefDistance) / 2;

    const linearRatio = startCamPos.distanceTo(endCamPos) / referenceDistance;

    const startDir = new THREE.Vector3().subVectors(startTarget, startCamPos);
    const endDir = new THREE.Vector3().subVectors(endTarget, endCamPos);
    const turn = (startDir.lengthSq() > 1e-12 && endDir.lengthSq() > 1e-12)
      ? startDir.angleTo(endDir) : 0; // radians, 0..π
    const turnRatio = turn / (Math.PI / 3); // 60° swing ≈ 1 reference distance (180° ≈ 3)

    const travelRatio = linearRatio * 0.5 + turnRatio;
    const scaled = exponent === 1 ? travelRatio : Math.pow(travelRatio, exponent);
    return Math.max(floorMs, Math.min(ceilingMs, 200 + scaled * coefficient));
  }

  // Yaw / pitch (radians, about the world up) and length of target − camera.
  function viewAngles(camPos, target) {
    const d = new THREE.Vector3().subVectors(target, camPos);
    const len = d.length() || 1e-6;
    return { yaw: Math.atan2(d.x, d.z), pitch: Math.asin(THREE.MathUtils.clamp(d.y / len, -1, 1)), len };
  }

  function startCameraTween(endCamPos, endTarget, { duration, onComplete, turnInPlace = false } = {}) {
    const startCamPos = camera.position.clone();
    const startTarget = controls.target.clone();

    // A fixed duration made short hops feel artificially slow and long
    // flights feel abrupt (and, since the tooltip waits for the tween to
    // finish, made it visibly lag behind on short moves for no reason).
    // This default (used by label-to-label navigation, goToLabel below): a
    // 650ms floor with a 1650ms ceiling, ramped with exponent 1.2 so short
    // hops are quick while big view swings (opposite-side labels, ~180°
    // turns) still get enough time for splat streaming to keep up. The
    // motion is constant-speed (see updateCameraTween), so a given duration
    // now feels quicker than it did under ease-in/out.
    // NOT used by the focal-point circle, which passes its own, faster
    // explicit duration (see applyFocalPoint).
    if (duration === undefined) {
      duration = computeTweenDuration(startCamPos, startTarget, endCamPos, endTarget, 650, 1650, 310, 1.2);
    }

    // turnInPlace (the focal point: camera stays put, only the target moves):
    // turn the view by its yaw and pitch evenly instead of sliding the target
    // through space. After looking up with the arrows the target sits high
    // above, and a straight slide from there swung the upright camera
    // sideways near the top — the picture went one way, the focal circle the
    // other, before landing (2026-10-28).
    let turn = null;
    if (turnInPlace) {
      // Start from where the camera actually LOOKS, not from the target: a
      // tilt / pan on top of the orbit (rotateAngleX/Y — the arrows' pitch
      // before 2026-10-28, the tilt control) is reset by the focal point, and
      // starting from the target made the view jump first, then turn.
      const lookLen = startTarget.distanceTo(startCamPos) || 1;
      const lookAt = startCamPos.clone().addScaledVector(camera.getWorldDirection(new THREE.Vector3()), lookLen);
      const a = viewAngles(startCamPos, lookAt);
      const b = viewAngles(endCamPos, endTarget);
      let dYaw = b.yaw - a.yaw;
      dYaw -= Math.round(dYaw / (2 * Math.PI)) * 2 * Math.PI; // the short way round
      turn = { a, dYaw, dPitch: b.pitch - a.pitch, dLen: b.len - a.len };
    }
    cameraTween = {
      startCamPos,
      startTarget,
      endCamPos,
      endTarget,
      turn,
      startTime: performance.now(),
      duration,
      onComplete,
    };
    cameraTweenActiveUntil = performance.now() + duration;
  }
  function updateCameraTween() {
    if (!cameraTween) return;
    const t = Math.min((performance.now() - cameraTween.startTime) / cameraTween.duration, 1);
    // Constant speed for the whole flight — no ease-in, so it doesn't feel
    // like it starts slow then lunges at the end. Only the last 20% eases
    // out (matching slope at the join, decelerating to a stop) so it settles
    // instead of jolting to a dead halt.
    let eased;
    if (t <= 0.8) {
      eased = t;
    } else {
      const u = (t - 0.8) / 0.2;
      eased = 0.8 + 0.2 * (u + u * u - u * u * u);
    }
    camera.position.lerpVectors(cameraTween.startCamPos, cameraTween.endCamPos, eased);
    const turn = cameraTween.turn;
    if (turn && t < 1) {
      const yaw = turn.a.yaw + turn.dYaw * eased;
      const pitch = turn.a.pitch + turn.dPitch * eased;
      const len = turn.a.len + turn.dLen * eased;
      controls.target.set(
        camera.position.x + Math.sin(yaw) * Math.cos(pitch) * len,
        camera.position.y + Math.sin(pitch) * len,
        camera.position.z + Math.cos(yaw) * Math.cos(pitch) * len,
      );
    } else {
      controls.target.lerpVectors(cameraTween.startTarget, cameraTween.endTarget, eased);
    }
    if (t >= 1) {
      const { onComplete } = cameraTween;
      cameraTween = null;
      if (onComplete) onComplete();
    }
  }

  // Restores a label's exact saved view — both camera position and target
  // were captured together at creation time, so this is a direct copy, no
  // direction/distance recomputation needed (unlike applyFocalPoint, which
  // has to preserve the *current* view distance for an arbitrary new target).
  onWindow('goToLabel', (event) => {
    const { position, cameraPosition } = event.detail || {};
    if (!position || !cameraPosition) return;
    const endCamPos = new THREE.Vector3(cameraPosition.x, cameraPosition.y, cameraPosition.z);
    const endTarget = new THREE.Vector3(position.x, position.y, position.z);

    // Reselecting the label the camera is already parked on (e.g. clicking
    // its marker again, or Cancel snapping back to a label you never left)
    // has nothing to travel — computeTweenDuration's floor (tuned for how a
    // REAL flight should feel) still applied here even at zero distance,
    // forcing the tooltip to sit out a ~1s+ wait for a trip that wasn't
    // happening. Measured relative to the current view distance so it holds
    // regardless of model scale; below the threshold, skip the tween
    // entirely and drop the camera straight onto the saved view so the
    // tooltip (gated on cameraTweenActiveUntil, see updateSelectedTooltip)
    // shows instantly instead of waiting out a fake flight.
    const referenceDistance = camera.position.distanceTo(controls.target) || 1;
    const camMoveRatio = camera.position.distanceTo(endCamPos) / referenceDistance;
    const targetMoveRatio = controls.target.distanceTo(endTarget) / referenceDistance;
    if (camMoveRatio < 0.01 && targetMoveRatio < 0.01) {
      cameraTween = null;
      cameraTweenActiveUntil = 0;
      camera.position.copy(endCamPos);
      controls.target.copy(endTarget);
      controls.update();
      return;
    }

    // Duration left unset — startCameraTween scales it to the actual
    // distance being travelled (see there). The tooltip waits for this to
    // finish before it shows itself, so it now naturally tracks that too:
    // quick for a nearby label, a bit longer for a distant one.
    startCameraTween(endCamPos, endTarget);
  });

  function initialize() {
    const width = window.innerWidth;
    const height = window.innerHeight - bottomBarAdjustment;
    camera.position.set(initialCameraPosition[0], initialCameraPosition[1], initialCameraPosition[2]); // Initial camera position
    camera.filmGauge = 35;
    camera.zoom = sensorSize;
    camera.aspect = width / height;
    applyLens();
    // Update renderer size
    renderer.setSize(width, height);
    // Zoom in limit
    //controls.minDistance = 2;
    // Zoom out limit
    //controls.maxDistance = 10;
    controls.target.set(initialTargetPosition[0], initialTargetPosition[1], initialTargetPosition[2]);
    //controls.enableDamping = true;
    controls.update();
  }

  // Quaternion for custom camera rotations
  const customQuaternion = new THREE.Quaternion();

  // Function to update the camera rotation
  function updateCameraRotation() {
    // Create quaternions for tilt, pan, and roll
    const tiltQuaternion = new THREE.Quaternion();
    const panQuaternion = new THREE.Quaternion();
    const rollQuaternion = new THREE.Quaternion();

    // Set tilt (rotate around X-axis)
    tiltQuaternion.setFromAxisAngle(new THREE.Vector3(1, 0, 0), THREE.MathUtils.degToRad(rotateAngleX));

    // Set pan (rotate around Y-axis)
    panQuaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(rotateAngleY));

    // Set roll (rotate around Z-axis)
    rollQuaternion.setFromAxisAngle(new THREE.Vector3(0, 0, 1), THREE.MathUtils.degToRad(rotateAngleZ));

    // Combine tilt, pan, and roll quaternions
    customQuaternion.identity(); // Reset to identity quaternion
    customQuaternion.multiply(panQuaternion);
    customQuaternion.multiply(tiltQuaternion);
    customQuaternion.multiply(rollQuaternion);

    // Apply the custom rotation to the camera
    camera.quaternion.copy(controls.object.quaternion).multiply(customQuaternion);
  }

  let pendingThumbnailResolve = null;

  // --- Zoom (wheel + pinch), fully custom; OrbitControls' own zoom is off. ---
  //
  // `notches` > 0 zooms in (glide forward along the view direction), < 0 out.
  // The per-notch step is `max(radiusToPivot, floor) * ZOOM_RATE`:
  //  - Far from the pivot it's proportional to the gap, so pulling way back
  //    takes big steps and closing in tapers smoothly — the natural feel.
  //  - Once within `floor` of the pivot the step levels off at a constant
  //    `floor * ZOOM_RATE` and the target is re-planted `floor` ahead of the
  //    camera, so you keep gliding forward at a steady rate (through geometry
  //    if you want) instead of stalling. No discontinuity at the boundary —
  //    the two regimes meet at exactly `floor * ZOOM_RATE`.
  //
  // ZOOM_RATE ~0.017 = ~1.7% closer per notch — a touch more sensitive than
  // the old OrbitControls feel (which was ~1.27% at zoomSpeed 0.25).
  const _zoomToPivot = new THREE.Vector3();
  const ZOOM_RATE = 0.017;
  // Per-user multiplier on every zoom step (Settings → General → Zoom
  // speed) — see utils/speedPrefs.js. Local copy first so the very first
  // notch uses it, then the user's DB value. The settings slider drives it
  // live via window.__setZoomSpeed while dragging.
  let zoomSpeed = readCachedSpeed(ZOOM_SPEED);
  fetchSpeed(ZOOM_SPEED).then((v) => { if (v != null) zoomSpeed = v; });
  window.__setZoomSpeed = (v) => { zoomSpeed = v; };
  // Zoom-out limit, in model radii from the model centre — shared by zoomBy
  // (wheel/pinch) and keepWithinZoomOutLimit (W/A/S/D/Q/E keys, translate tools).
  const ZOOM_OUT_LIMIT_RADII = 4;
  // Streamed models: the whole splat scene's extent (lod-meta.json's bound,
  // set once loaded — setStreamedZoomBound). The picking point cloud can be
  // far smaller than the splats (Forest Village: ~180 m radius vs splats out
  // to ~3 km), which capped zoom-out well inside the scene. The limit then
  // also reaches far enough to see the whole scene: with this 35° lens a
  // sphere fills the view from ~3.3 radii away.
  const ZOOM_OUT_SCENE_RADII = 3.5;
  let streamedSceneSphere = null;
  function setStreamedZoomBound(box) {
    if (!box || box.isEmpty()) return;
    streamedSceneSphere = box.getBoundingSphere(new THREE.Sphere());
    // Far plane past the farthest point visible from the limit.
    const far = streamedSceneSphere.radius * (ZOOM_OUT_SCENE_RADII + 2);
    if (far > camera.far) { camera.far = far; camera.updateProjectionMatrix(); }
  }
  // Farthest the camera may be from `sph.center` (the point cloud's centre).
  function zoomOutLimit(sph) {
    let maxOut = sph.radius * ZOOM_OUT_LIMIT_RADII;
    if (streamedSceneSphere) {
      maxOut = Math.max(maxOut, sph.center.distanceTo(streamedSceneSphere.center) + streamedSceneSphere.radius * ZOOM_OUT_SCENE_RADII);
    }
    return maxOut;
  }
  const _limitPos = new THREE.Vector3();
  // Moves that translate camera AND target together (W/A/S/D/Q/E keys, the
  // translate tools) must not carry the camera past the zoom-out limit
  // either. `prevDist` = camera's distance from the model centre before the
  // move: only movement OUTWARD past the limit is undone — sliding along it
  // or moving back in is untouched, and a camera that's already outside
  // isn't snapped. The correction shifts camera and target by the same
  // amount, so the view direction doesn't change.
  function keepWithinZoomOutLimit(prevDist) {
    const sph = getPickCloudSphere();
    if (!sph) return;
    const d = camera.position.distanceTo(sph.center);
    const allowed = Math.max(zoomOutLimit(sph), prevDist);
    if (d <= allowed) return;
    _limitPos.copy(camera.position).sub(sph.center).multiplyScalar(allowed / d).add(sph.center).sub(camera.position);
    camera.position.add(_limitPos);
    controls.target.add(_limitPos);
  }
  function zoomBy(notches) {
    if (!notches || cameraTween) return;
    const sph = getPickCloudSphere();
    // Constant-glide floor for the step size once you're close to the pivot,
    // so zoom never slows to a crawl approaching the focal point. Back at
    // 15% of the model radius (aaa09403's value): 494ff91b cut it to 2%
    // because the OLD dolly re-planted the target every notch, but that
    // dolly was replaced in the same commit — the target now only moves
    // once you actually reach it (below) — so the cut just brought the
    // near-pivot slowdown back with nothing gained.
    const floor = sph ? Math.max(sph.radius * 0.15, 0.1) : 0.5;
    const radius = camera.position.distanceTo(controls.target) || 1;
    const step = Math.max(radius, floor) * ZOOM_RATE * zoomSpeed * notches;

    // Dolly straight along the camera->target line, changing ONLY the
    // distance while there's still a gap — controls.target stays exactly
    // where it is, so a focal point set by double-click survives zoom
    // (wheel and pinch). The old version translated along the view
    // direction and re-planted the target ahead of the camera on every
    // notch, which threw the focal point away immediately.
    _zoomToPivot.subVectors(camera.position, controls.target); // target -> camera
    const dist = _zoomToPivot.length() || 1;
    _zoomToPivot.multiplyScalar(1 / dist);
    const minGap = Math.max(radius * 0.02, 0.05);
    const newDist = dist - step; // step > 0 = zoom in

    if (newDist >= minGap || step < 0) {
      // Still short of (or moving away from) the pivot — pure distance change.
      camera.position.copy(controls.target).addScaledVector(_zoomToPivot, newDist);
    } else {
      // Reached the pivot: keep gliding forward THROUGH it, and only now
      // carry the target along so it stays minGap ahead of the camera and
      // orbit still works. Not disturbed until you actually get there.
      const overshoot = minGap - newDist;
      camera.position.copy(controls.target).addScaledVector(_zoomToPivot, newDist);
      controls.target.addScaledVector(_zoomToPivot, -overshoot);
    }

    // Zoom-out limit: never let the camera get more than a few model radii
    // from the model centre, or you zoom off into empty space and lose the
    // splat entirely with no way back. Clamp the camera onto that shell.
    if (sph && step < 0) {
      const maxOut = zoomOutLimit(sph);
      const d = camera.position.distanceTo(sph.center);
      if (d > maxOut) camera.position.sub(sph.center).multiplyScalar(maxOut / d).add(sph.center);
    }
  }
  function onWheelZoom(event) {
    event.preventDefault();
    const px = event.deltaMode === 1 ? event.deltaY * 16
      : event.deltaMode === 2 ? event.deltaY * window.innerHeight
      : event.deltaY;
    zoomBy(Math.max(-8, Math.min(8, -px / 100)));
  }
  renderer.domElement.addEventListener('wheel', onWheelZoom, { passive: false });

  // ─── Lower resolution while the camera moves ────────────────────────────
  // Splat rendering on a Retina-sized canvas is fill-rate bound (measured:
  // WebGL/WebGPU/addon all ~15-22 fps at 3456×2234 on a 3.3M-splat model).
  // While the camera moves, render at navScale × the quality preset's pixel
  // ratio; as soon as it stops, return to full resolution — the still image
  // is exactly the preset's. Settings → Quality → Navigation quality
  // (?navQuality=1|0.7|0.5|0.25; 1 = off; legacy ?motionScale= still read).
  // Auto (no param) follows the model's splat count — autoNavQuality() in
  // Splat.ts: < 5M full, 5-10M 70%, > 10M 50% — resolved once the count is
  // known (full until then). window.__setNavQuality(scale | null) applies a
  // manual pick, or Auto with null, live.
  //
  // "Stopped" = no camera change for MOTION_SETTLE_MS (~6 frames): switching
  // on the very first still frame would flip resolution back and forth
  // mid-drag (frames without a mouse event), resizing the canvas every
  // frame. Sub-perceptual drift (OrbitControls damping tail) counts as still.
  const navParam = parseFloat(queryParams.get('navQuality') ?? queryParams.get('motionScale'));
  let navIsAuto = !Number.isFinite(navParam);
  // Auto: Full (autoNavQuality), lowered by adaptive quality as its last
  // step via navAdaptiveCap (down to 70%). A manual navigation-quality pick
  // always wins and takes it off adaptive control.
  let navScale = navIsAuto ? autoNavQuality(null) : navParam;
  let navAutoByCount = autoNavQuality(null);
  let navAdaptiveCap = 1;
  window.__navQualityAuto = navAutoByCount;
  const refreshNavAuto = () => {
    window.__navQualityAuto = Math.min(navAutoByCount, navAdaptiveCap);
    if (navIsAuto) { navScale = window.__navQualityAuto; if (motionLowRes) setMotionResolution(true); }
  };
  const setNavAdaptiveCap = (cap) => {
    navAdaptiveCap = Math.max(0.1, Math.min(1, cap));
    refreshNavAuto();
  };
  const setNavAutoFromSplatCount = (count) => {
    navAutoByCount = autoNavQuality(count);
    refreshNavAuto();
  };
  const MOTION_SETTLE_MS = 100;
  // Sharpening fade: when switching back to full resolution, the last
  // low-resolution frame is copied onto an overlay canvas over the scene and
  // faded out while the sharp frame renders underneath — a soft transition
  // instead of a snap. ?navFade=0 disables.
  const SHARPEN_FADE_MS = 180;
  const navFade = queryParams.get('navFade') !== '0';
  let _sharpenOverlay = null;
  const hideSharpenOverlay = () => {
    if (!_sharpenOverlay) return;
    _sharpenOverlay.style.transition = 'none';
    _sharpenOverlay.style.opacity = '0';
  };
  const showSharpenOverlay = () => {
    const src = renderer.domElement;
    // Lives INSIDE #three-container, right after the canvas, with no
    // z-index: it paints over the canvas only, and every UI element (label
    // markers z-index 5, labels panel 500, header/footer 10 — all in #root's
    // stacking context) stays above it. (A body-level overlay with z-index 1
    // sat above all of #root and made labels/menus flash on every stop.)
    // Recreated per scene visit along with the container itself.
    if (!_sharpenOverlay || _sharpenOverlay.previousSibling !== src) {
      _sharpenOverlay?.remove();
      _sharpenOverlay = document.createElement('canvas');
      Object.assign(_sharpenOverlay.style, { position: 'absolute', pointerEvents: 'none', opacity: '0' });
      src.parentNode?.insertBefore(_sharpenOverlay, src.nextSibling);
    }
    const ov = _sharpenOverlay;
    if (ov.width !== src.width || ov.height !== src.height) { ov.width = src.width; ov.height = src.height; }
    // Same offset parent as the canvas (siblings), so its offsets apply as-is.
    Object.assign(ov.style, {
      left: `${src.offsetLeft}px`, top: `${src.offsetTop}px`,
      width: `${src.clientWidth}px`, height: `${src.clientHeight}px`,
    });
    // Same task as the render that produced it — the drawing buffer is
    // still valid (no preserveDrawingBuffer needed, WebGL or WebGPU).
    ov.getContext('2d').drawImage(src, 0, 0);
    ov.style.transition = 'none';
    ov.style.opacity = '1';
    void ov.offsetWidth; // commit opacity 1 before starting the fade
    ov.style.transition = `opacity ${SHARPEN_FADE_MS}ms ease-out`;
    ov.style.opacity = '0';
  };
  const _motionLastPos = new THREE.Vector3();
  const _motionLastQuat = new THREE.Quaternion();
  let motionLowRes = false;
  let motionLastMoveAt = 0;
  // Resizing the canvas clears it. A resize between one frame's draw and the
  // next — adaptive quality decides right after the draw, menu clicks and
  // timers run between frames — got that cleared canvas shown: one black
  // frame (on a slow iPad, every time adaptive quality stepped). So a
  // resolution change is only recorded here, and syncCanvasSize() applies it
  // at the start of the next frame, right before that frame is drawn.
  let canvasSizeDirty = false;
  let canvasResizeFull = false; // also re-run setSize / aspect / lens (resolution %)
  const setMotionResolution = (low) => {
    motionLowRes = low;
    canvasSizeDirty = true;
  };
  const syncCanvasSize = () => {
    if (!canvasSizeDirty) return;
    canvasSizeDirty = false;
    const full = renderConfig.pixelRatio;
    renderer.setPixelRatio(motionLowRes ? Math.max(full * navScale, Math.min(full, 0.4)) : full);
    if (canvasResizeFull) {
      canvasResizeFull = false;
      renderer.setSize(window.innerWidth, window.innerHeight - bottomBarAdjustment);
      camera.aspect = window.innerWidth / (window.innerHeight - bottomBarAdjustment);
      applyLens();
    }
  };
  const updateMotionResolution = () => {
    camera.updateMatrixWorld();
    const now = performance.now();
    const tol = Math.max(camera.position.distanceTo(controls.target), 0.01) * 1e-4;
    const moved = camera.position.distanceTo(_motionLastPos) > tol
      || 1 - Math.abs(camera.quaternion.dot(_motionLastQuat)) > 1e-8;
    if (moved) {
      _motionLastPos.copy(camera.position);
      _motionLastQuat.copy(camera.quaternion);
      motionLastMoveAt = now;
    }
    if (!(navScale < 1)) {
      if (motionLowRes) setMotionResolution(false);
      return;
    }
    if (moved) {
      if (!motionLowRes) { hideSharpenOverlay(); setMotionResolution(true); }
    } else if (motionLowRes && now - motionLastMoveAt >= MOTION_SETTLE_MS) {
      if (navFade && !document.hidden) {
        // Re-render the (unchanged) view at low resolution so the canvas
        // holds a frame to copy in this same task, then fade it out over
        // the sharp frame that animate() renders right after this.
        syncCanvasSize();
        renderer.render(scene, camera);
        try { showSharpenOverlay(); } catch (e) { hideSharpenOverlay(); }
      }
      setMotionResolution(false);
    }
  };
  window.__setNavQuality = (scale) => {
    navIsAuto = !(Number.isFinite(scale) && scale > 0);
    // Manual pick → adaptive stops changing it; Auto → adaptive manages it.
    if (navIsAuto) adaptiveQuality?.resume?.('nav');
    else adaptiveQuality?.freeze?.('nav');
    navScale = navIsAuto ? window.__navQualityAuto : Math.min(scale, 1);
    if (motionLowRes) setMotionResolution(true);
  };
  // Screenshots must never be taken at the reduced motion resolution (or
  // with the fade overlay up).
  // (Applied at once — the capture renders right after, in the same task.)
  const ensureFullResolution = () => { hideSharpenOverlay(); if (motionLowRes) setMotionResolution(false); syncCanvasSize(); };

  // Live (static and streamed): pixel ratio is renderer state and covClamp a
  // material uniform (WebGL and WebGPU), so both apply without a reload.
  const applyResolutionPct = (pct) => {
    resolutionPct = pct;
    renderConfig.pixelRatio = pixelRatioForPct(pct);
    canvasResizeFull = true;
    setMotionResolution(motionLowRes); // applied by syncCanvasSize before the next draw
    window.__resolutionLivePct = pct;
    window.dispatchEvent(new CustomEvent('renderQualityChange', { detail: { resolution: pct } }));
  };
  const applySharpnessPos = (pos) => {
    sharpnessPos = pos;
    renderConfig.covClamp = qualitySettings(pos, dpr).covClamp;
    if (liveSplatMesh?.material && 'covClamp' in liveSplatMesh.material) liveSplatMesh.material.covClamp = renderConfig.covClamp;
    window.__sharpnessPos = pos;
    window.dispatchEvent(new CustomEvent('renderQualityChange', { detail: { sharpness: pos } }));
  };
  // Settings → Quality → Adaptive quality (saved by the menu on Apply). Off
  // stops adapting (levers back to their defaults) and the per-frame
  // measuring that feeds it; on starts both again.
  window.__setAdaptiveEnabled = (on) => {
    FRAME_MONITOR = !!on && queryParams.get('adaptive') !== '0';
    GPU_PROBE = FRAME_MONITOR && queryParams.get('gpuProbe') !== '0';
    adaptiveQuality?.setEnabled?.(!!on);
  };
  // Quality menu. Resolution: a % is a manual pick (adaptive quality stops
  // moving it); '' / null = Auto — back to adaptive quality, or to the Auto
  // value when adaptation isn't running.
  window.__setResolution = (value) => {
    const pct = value === '' || value == null ? null : parseResolutionPct(value);
    if (pct == null) {
      const resumed = adaptiveQuality?.resumeQuality?.();
      if (resumed == null) applyResolutionPct(autoResolutionPct);
      return;
    }
    adaptiveQuality?.freezeQuality?.();
    applyResolutionPct(pct);
  };
  // Sharpness: a position / preset name, or '' / null = Auto (High).
  window.__setSharpness = (value) => {
    const pos = value === '' || value == null ? null : qualityPos(value);
    applySharpnessPos(pos ?? AUTO_SHARPNESS_POS);
  };

  // ─── Draw only when the picture can have changed ─────────────────────
  // The scene used to be re-rendered every frame even with nothing moving —
  // the GPU at full load non-stop (heat → thermal throttling on fanless
  // tablets → slower navigation too). Now each frame builds a cheap
  // fingerprint of everything the image depends on; an unchanged one skips
  // the draw. Same picture, no GPU work while idle.
  //   camera pose + lens · canvas size / pixel ratio · background ·
  //   every visible object's matrix, buffers (attribute versions,
  //   instanceCount, drawRange), material version + uniform values /
  //   texture versions · the streamed pool's write counter (its writes go
  //   straight to the GPU, past three's own version tracking).
  // Safety net: at least one draw per RENDER_IDLE_MAX_MS; anything that
  // knows it changed the picture another way can call
  // window.__requestRender().
  const RENDER_IDLE_MAX_MS = 1000;
  let _lastRenderSig = '';
  let _lastRenderAt = 0;
  let _forceRenderFrames = 3;
  // Consecutive drawn frames. Adaptive quality and the DEBUG fps only use
  // sustained drawing (≥ ADAPTIVE_MIN_STREAK in a row): the short idle
  // bursts (safety redraw, follow-up frames, a streamed chunk) are cheap —
  // no sorting or streaming going on — and would read as "120 fps".
  // var (hoisted): the DEBUG overlay's timer can read these before this
  // point of initializeScene has run (temporal dead zone otherwise).
  var ADAPTIVE_MIN_STREAK = 8; // eslint-disable-line no-var
  // Stutter readout (DEBUG overlay): recent slow frames and what the frame
  // before each one spent its time on. var: read by the HUD timer above.
  var _renderStreak = 0; // eslint-disable-line no-var
  var _drawnFrameMs = 16.7; // eslint-disable-line no-var
  window.__requestRender = (frames = 2) => { _forceRenderFrames = Math.max(_forceRenderFrames, frames); };
  const _sigParts = [];
  const _sigValue = (v) => {
    if (v == null) return '';
    if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'string') return String(v);
    if (v.isTexture) return `t${v.id}.${v.version}`;
    if (typeof v.toArray === 'function') return v.toArray().join(',');
    if (Array.isArray(v)) return v.map(_sigValue).join(',');
    return '';
  };
  function renderSignature() {
    _sigParts.length = 0;
    _sigParts.push(camera.matrixWorld.elements.join(','), camera.projectionMatrix.elements.join(','),
      renderer.getPixelRatio(), renderer.domElement.width, renderer.domElement.height,
      _sigValue(scene.background), activeOctree?.pool?.version ?? '');
    scene.traverseVisible((o) => {
      _sigParts.push(o.id, o.matrixWorld.elements.join(','));
      const g = o.geometry;
      if (g) {
        _sigParts.push(g.instanceCount ?? '', g.drawRange.start, g.drawRange.count, g.index ? g.index.version : '');
        for (const k in g.attributes) _sigParts.push(g.attributes[k].version);
      }
      const mats = Array.isArray(o.material) ? o.material : (o.material ? [o.material] : []);
      for (const m of mats) {
        _sigParts.push(m.version);
        if (m.uniforms) for (const k in m.uniforms) _sigParts.push(_sigValue(m.uniforms[k].value));
      }
    });
    return _sigParts.join('|');
  }

  function animate() {
    if (sceneDisposed) return; // left the scene: the loop ends here
    requestAnimationFrame(animate);
    const nowF = performance.now();
    const dt = nowF - _frameT;
    _frameT = nowF;
    // Stutter readout (DEBUG): sort results taken in since the last frame.
    const _sortIn = window.__jankAcc ? window.__jankAcc.ms : 0;
    const _sortsIn = window.__jankAcc ? window.__jankAcc.n : 0;
    if (window.__jankAcc) { window.__jankAcc.ms = 0; window.__jankAcc.n = 0; }
    // (_frameMs / _fps: drawn frames only — updated below.)
    updateCameraTween();
    updateJoystick();
    controls.update();
    updateCameraRotation();
    // Streamed-SOG start pose: force the exact captured camera transform for
    // the first frames, AFTER every control system has had its say (tween,
    // OrbitControls, roll/pitch). Otherwise an initial roll-control value or
    // OrbitControls' own re-orientation overrides ?camPos and the view comes
    // back rotated / "upside down". Released after `framesLeft` so normal
    // navigation takes over.
    if (_streamPoseLock) {
      camera.position.copy(_streamPoseLock.pos);
      camera.quaternion.copy(_streamPoseLock.quat);
      controls.target.copy(_streamPoseLock.target);
      camera.updateMatrixWorld();
      if (--_streamPoseLock.framesLeft <= 0) _streamPoseLock = null;
    }
    // Streamed models: decide what's visible from the camera's FINAL pose —
    // after updateCameraRotation's tilt / pan / roll and the start-pose
    // lock. It used to run right after controls.update(), i.e. on the orbit
    // direction alone, so looking up with tilt (arrows / look joystick) never
    // counted the sky as visible: it stayed grey until the orbit itself
    // happened to face it.
    const _tCam = performance.now();
    activeOctree?.update(camera);
    const _tStream = performance.now();
    updateMotionResolution();
    syncCanvasSize(); // pending resolution changes, right before this frame's draw
    const _tRes = performance.now();
    const sig = renderSignature();
    // A change → also draw the next 2 frames: some results land a frame
    // later (worker / GPU sort for the new view), and the settled frame
    // must show them.
    if (sig !== _lastRenderSig) _forceRenderFrames = Math.max(_forceRenderFrames, 3);
    const mustRender = sig !== _lastRenderSig || _forceRenderFrames > 0 || !!pendingThumbnailResolve
      || nowF - _lastRenderAt >= RENDER_IDLE_MAX_MS;
    // ?gpuPace=1: the GPU is still on the last frame — draw this one later.
    const paceHold = GPU_PACE && _gpuPacePending;
    const sinceDraw = nowF - _lastRenderAt;
    if (mustRender && !paceHold) {
      const cv = renderer.domElement;
      if (cv.width !== _lastCanvasW || cv.height !== _lastCanvasH) {
        if (_lastCanvasW) { _resizes++; _resizesTotal++; }
        _lastCanvasW = cv.width; _lastCanvasH = cv.height;
      }
      const _tDraw0 = performance.now();
      renderer.render(scene, camera);
      const _tDraw1 = performance.now();
      // A slow frame is blamed on the PREVIOUS frame's work plus what ran
      // between the two (sort results); the rest is waiting (GPU, browser).
      if (FRAME_MONITOR && _renderStreak >= ADAPTIVE_MIN_STREAK && dt > 24 && dt > _drawnFrameMs * 1.5 && dt < 1000 && _prevWork) {
        const p = _prevWork;
        _jankLog.push({ at: nowF, dt, cam: p.cam, stream: p.stream, res: p.res, draw: p.draw, sort: _sortIn, sorts: _sortsIn,
          wait: Math.max(0, dt - p.cam - p.stream - p.res - p.draw - _sortIn) });
        if (_jankLog.length > 240) _jankLog.shift();
      }
      _prevWork = { cam: _tCam - nowF, stream: _tStream - _tCam, res: _tRes - _tStream, draw: _tDraw1 - _tDraw0 };
      _framesDrawn++;
      const gpuDevice = renderer.isWebGPURenderer && renderer.backend && renderer.backend.device;
      if (gpuDevice && GPU_PACE) {
        _gpuPacePending = true;
        gpuDevice.queue.onSubmittedWorkDone().then(() => { _gpuPacePending = false; }, () => { _gpuPacePending = false; });
      }
      if (gpuDevice && GPU_PROBE && ++_gpuProbeCount % GPU_PROBE_EVERY === 0) {
        const streak = _gpuProbeStreak;
        gpuDevice.queue.onSubmittedWorkDone().then(() => {
          const at = performance.now();
          // Only two answers from one unbroken run of drawn frames measure
          // frame time (a pause in drawing would count as GPU time).
          if (streak === _gpuProbeLastStreak && _gpuProbeLastAt) {
            const perFrame = (at - _gpuProbeLastAt) / GPU_PROBE_EVERY;
            if (perFrame > 0 && perFrame < 1000) _gpuFrameMs = _gpuFrameMs ? _gpuFrameMs + (perFrame - _gpuFrameMs) * 0.3 : perFrame;
            window.__gpuFrameMs = +_gpuFrameMs.toFixed(1); // for testing / Web Inspector
          }
          _gpuProbeLastAt = at;
          _gpuProbeLastStreak = streak;
        }, () => {});
      }
      // Opt-in (?gpuTime=1): asking after every frame made the stutter much
      // worse on an iPad (WebKit), so it's off unless asked for.
      if (FRAME_MONITOR && gpuDevice && _gpuInFlight < 8 && queryParams.get('gpuTime') === '1') {
        const tSubmit = performance.now();
        _gpuInFlight++;
        gpuDevice.queue.onSubmittedWorkDone().then(() => {
          _gpuInFlight--;
          _gpuDoneMs.push(performance.now() - tSubmit);
          if (_gpuDoneMs.length > 600) _gpuDoneMs.shift();
        }, () => { _gpuInFlight--; });
      }
      _lastRenderSig = sig;
      _lastRenderAt = nowF;
      if (_forceRenderFrames > 0) _forceRenderFrames--;
      _renderStreak++;
      if (FRAME_MONITOR && _renderStreak >= ADAPTIVE_MIN_STREAK && dt > 0 && dt < 1000) {
        // WebGPU: never faster than the GPU really finishes frames (above).
        const frameDt = Math.max(dt, _gpuFrameMs, GPU_PACE ? sinceDraw : 0);
        _drawnFrameMs += (frameDt - _drawnFrameMs) * 0.1;
        _frameMs = _drawnFrameMs;
        _fps = 1000 / _frameMs;
        // Paused while a capture forces best quality (withBestCaptureQuality).
        if (!captureInProgress) adaptiveQuality?.onFrame(frameDt);
      }
    } else if (!paceHold) {
      _framesSkipped++;
      if (_renderStreak) {
        _streakBreaks++;
        _renderStreak = 0;
        _gpuProbeStreak++; // the GPU frame-time measurement restarts with drawing
        _gpuFrameMs = 0;
        adaptiveQuality?.idle?.();
      }
    }
    updateSmoothMovement();
    updateLabelMarkerPositions();
    updateActiveCirclePosition();

    // Capture immediately after render so the canvas is guaranteed to have content
    if (pendingThumbnailResolve) {
      const resolve = pendingThumbnailResolve;
      pendingThumbnailResolve = null;
      resolve(renderer.domElement);
    }
  }

  // Streamed LOD trees through this environment's CloudFront (the
  // /app/uploads/*/* behavior, infra/stack/cloudfront.tf) instead of
  // straight from S3: HTTP/2 + edge caching for the hundreds of chunks.
  // The backend returns the S3 address; same path on the CDN.
  function lodTreeUrl(url) {
    const s3Base = `${S3_UPLOADS_BASE_URL}/`;
    if (typeof url === 'string' && url.startsWith(s3Base) && CAPTURES_CDN_URL) {
      return `${CAPTURES_CDN_URL}/app/uploads/${url.slice(s3Base.length)}`;
    }
    return url;
  }

  async function app() {
    // Streamed SOG dev route — entirely separate from, and returns before
    // touching, everything below (resolveSplatUrl, quality tiers,
    // sceneLoadGuard). It DOES use the same focal-point + label pick layer
    // as the static path: a decimated `_point_cloud.ply` raycast proxy (see
    // the `?pc=` block below), loaded through the same fetch/parse/decimate
    // helpers. Local-fixture testing only for this phase:
    // `?stream=1&model=/test-fixtures/lod-demo/lod-meta.json`.
    const streamModelUrl = queryParams.get('stream') === '1' ? queryParams.get('model') : null;
    if (streamModelUrl) {
      spinner(true);
      const budgetParam = parseInt(queryParams.get('budget'), 10);
      // Default budget is device-scaled — see the ?modelId= streamed
      // branch's own comment for the fuller history. Back at 1M (matching
      // PlayCanvas's own mobile guidance) as of 2026-09-14: the 400k cut
      // was defensive, made while the crash's real cause (see
      // ply-parse-worker.js's tryFastParse) was still unidentified, and
      // turned out to be too small a budget for this scene's density —
      // confirmed live: at 400k the scene got mostly stuck grey even under
      // active camera demand (zooming into a region didn't resolve it
      // either), on a device where the SAME solve code fills in fine at
      // desktop's much larger default.
      const budget = Number.isFinite(budgetParam) && budgetParam > 0
        ? budgetParam
        : (isLowMemoryDevice ? 1_000_000 : 4_000_000);
      // ?lodDist= : world-unit base distance for LOD 0 (bigger = detail
      //   stays sharp farther from the camera). 0/absent = auto from chunk size.
      // ?lodMult= : geometric step between LOD level distance bands. 0/absent = 2.6.
      const lodDist = parseFloat(queryParams.get('lodDist')) || 0;
      const lodMult = parseFloat(queryParams.get('lodMult')) || 0;
      // On by default — coarse-first prewarm, see splatOctree.js's
      // SplatOctree constructor comment. ?prewarm=0 disables.
      const prewarm = queryParams.get('prewarm') !== '0';
      // ?knapsack=1 / ?exactPack=1 / ?defrag=1 — opt-in Stage 1 flags for the
      // "match PlayCanvas" streaming rewrite (see STREAMING-SOG-STATUS.md and
      // /Users/uldis.liepins/.claude/plans/floating-launching-blum.md). All
      // default OFF; the legacy solver/allocator stay the default so this is
      // an instant-revert lever, not a replacement. ?defrag= only has any
      // effect when ?exactPack=1 is also set (see SplatOctree's constructor).
      const knapsack = queryParams.get('knapsack') === '1';
      // Exact-size allocator is the DEFAULT as of 2026-10-03 (?exactPack=0
      // reverts to the buddy allocator). Measured headless: the buddy
      // allocator's power-of-two rounding wasted ~29% of the pool (pool
      // "full" at 71% utilisation, allocFailures climbing ~190/s forever,
      // chunks left with no data at all = permanent grey areas) and its
      // budget accounting counted tiny chunks as 512 slots, so far less
      // detail loaded. Exact: 99% utilisation, 0 failures, every chunk gets
      // data, and more detail at default settings (Concert Hall SS 1.73M →
      // 2.39M, Cinevilla SS 1.17M → 1.81M resident), WebGL and WebGPU.
      const exactPack = queryParams.get('exactPack') !== '0';
      const defrag = queryParams.get('defrag') === '1';
      const streamLoader = new SplatLoader(renderer, 250000, shLoadOverride, splatDensityParam);
      try {
        const shared = await streamLoader.loadStreamed(streamModelUrl, budget, { lodDist, lodMult, prewarm, knapsack, exactPack, defrag });
        const mainSplat = new Splat(shared, camera, { covClamp: renderConfig.covClamp });
        scene.add(mainSplat);
        activeOctree = shared.octree;

        // Focal-point + label pick layer — the SAME approach and the SAME
        // helpers the static path uses (see the `modelData.pointCloudUrl`
        // block below): fetch a real `_point_cloud.ply`, parse it off-thread
        // (parsePLYInWorker), decimate it (decimateGeometry) and add it as a
        // hidden Points layer that pickScenePoint raycasts against. Dev
        // source: `?pc=`. Deferred, not awaited: a double-click before it
        // lands still recenters via pickScenePoint's along-the-ray fallback.
        //
        // pointSize is left at the module default — the same value the
        // static path uses — because pickScenePoint derives the raycast
        // threshold from it (pointSize * 0.75), and anything larger makes
        // the pick ray fat enough to sweep up thousands of stray/behind
        // points (verified: pointSize 3.8 -> threshold 2.83 -> 3000+ bogus
        // hits, vs 0.3 / ~40 clean hits on the static path). `?pointSize=`
        // still overrides if a specific scene needs tuning.
        const psParam = parseFloat(queryParams.get('pointSize'));
        if (Number.isFinite(psParam) && psParam > 0) pointSize = psParam;
        // The rendered dot size is cosmetic only — pickScenePoint's raycast
        // threshold comes from the `pointSize` variable above, never from
        // the material. So render the dots bigger (scaled to the scene) for
        // a legible debug view without touching pick behaviour. `?dotSize=`
        // overrides the visual only.
        const dsParam = parseFloat(queryParams.get('dotSize'));
        const streamDotSize = Number.isFinite(dsParam) && dsParam > 0 ? dsParam : 0.6;
        // Same decimation cap as the static path; `?pickPoints=` overrides.
        const ppParam = parseInt(queryParams.get('pickPoints'), 10);
        const streamPickPoints = Number.isFinite(ppParam) && ppParam > 0 ? ppParam : MAX_PICKING_POINTS;
        const streamPcUrl = queryParams.get('pc');
        if (streamPcUrl) {
          (async () => {
            try {
              const response = await fetchWithModelCache(streamPcUrl, null);
              if (!response.ok || !response.body) throw new Error(`point cloud fetch failed: ${response.status}`);
              const reader = response.body.getReader();
              const chunks = [];
              let received = 0;
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                received += value.length;
              }
              const bytes = new Uint8Array(received);
              let off = 0;
              for (const c of chunks) { bytes.set(c, off); off += c.length; }

              const ply = await parsePLYInWorker(bytes.buffer, streamPickPoints);
              const material = new THREE.PointsMaterial({ color: 0xffffff, size: streamDotSize });
              const points = new THREE.Points(decimateGeometry(ply, streamPickPoints), material);
              points.name = 'pickCloud';
              if (!debug) points.visible = false;
              scene.add(points);

              ply.computeBoundingBox();
              const b = ply.boundingBox;
              console.log('[stream] pick cloud loaded:', points.geometry.attributes.position.count, 'pts  bbox',
                b.min.toArray().map((n) => +n.toFixed(1)), '->', b.max.toArray().map((n) => +n.toFixed(1)));
              console.log('[stream] octree render bound', shared.bound.min.toArray().map((n) => +n.toFixed(1)),
                '->', shared.bound.max.toArray().map((n) => +n.toFixed(1)),
                ' contentCenter', shared.contentCenter.toArray().map((n) => +n.toFixed(1)));
            } catch (err) {
              console.warn('[stream] pick cloud load failed', err);
            }
          })();
        } else {
          console.log('[stream] no ?pc= point cloud — focal point / labels fall back to computed pick points only');
        }

        // Live resolution / sharpness changes (window.__setResolution /
        // __setSharpness, defined above animate()) — point them at this mesh.
        liveSplatMesh = mainSplat;

        // Press 'c' (canvas focused) to copy the current view URL fragment.
        // window.__streamCam is defined further down, once controls exist.
        onWindow('keydown', (ev) => {
          if (ev.key === 'c' && !ev.metaKey && !ev.ctrlKey && window.__streamCam) {
            const s = window.__streamCam();
            console.log('[stream] view →', s);
            try { navigator.clipboard.writeText(s); } catch (e) { /* ignore */ }
          }
        });

        // This dev route has none of the normal per-model camera defaults
        // (those come from backend model data / hardcoded modelId cases in
        // initialize(), none of which apply to an arbitrary lod-meta.json).
        // Camera start, in priority order:
        //   ?camPos=x,y,z  [&camTarget=x,y,z]   explicit (RENDER-space coords;
        //                                        read them off window.__streamCam())
        //   ?start=inside                        stand in the middle of the scene
        //                                        at mid-height, looking level
        //   (default)                            frame the whole scene bound
        const parseVec = (s) => {
          const p = (s || '').split(',').map(Number);
          return p.length === 3 && p.every(Number.isFinite) ? new THREE.Vector3(p[0], p[1], p[2]) : null;
        };
        const camPosParam = parseVec(queryParams.get('camPos'));
        const camTargetParam = parseVec(queryParams.get('camTarget'));
        const camRotParam = parseVec(queryParams.get('camRot')); // rotateAngle X,Y,Z (deg)
        const startMode = queryParams.get('start');
        // Content-weighted centre/radius (from parseLodMeta) — NOT the raw
        // bound centre, which stray far-field splats push out into empty
        // space (the "starts outside everything" problem).
        const center = (shared.contentCenter && shared.contentCenter.clone()) ||
          (shared.bound && !shared.bound.isEmpty() ? shared.bound.getCenter(new THREE.Vector3()) : new THREE.Vector3());
        const radius = shared.contentRadius || 50;
        console.log('[stream] content center', center.toArray().map((n) => +n.toFixed(1)), 'radius', +radius.toFixed(1));

        if (camPosParam) {
          camera.position.copy(camPosParam);
          controls.target.copy(camTargetParam || center);
          // This app's camera has extra pitch/pan/roll state (rotateAngleX/Y/Z)
          // applied every frame on top of OrbitControls in updateCameraRotation()
          // — OrbitControls itself can't hold a rolled/pitched view. Without
          // restoring these, a copied view comes back level & "upside down"
          // relative to where it was captured.
          if (camRotParam) {
            rotateAngleX = camRotParam.x;
            rotateAngleY = camRotParam.y;
            rotateAngleZ = camRotParam.z;
          }
        } else if (startMode === 'inside') {
          // Stand AT the content centre, look horizontally toward +X. A
          // short forward offset for the target so movement/LOD is testable.
          camera.position.copy(center);
          controls.target.set(center.x + Math.max(5, radius * 0.05), center.y, center.z);
        } else {
          // Pull back along +Z far enough to see the bulk of the content.
          camera.position.set(center.x, center.y + radius * 0.35, center.z + radius * 2.4);
          controls.target.copy(center);
        }
        controls.update();
        updateCameraRotation(); // fold in any restored roll/pitch now

        // When restoring an explicit view, hard-lock the exact camera
        // transform for the first ~40 frames (see animate()) so nothing —
        // OrbitControls re-orienting, an initial roll-control value, a
        // stray tween — can knock it off the captured pose before you take
        // over.
        if (camPosParam) {
          _streamPoseLock = {
            pos: camera.position.clone(),
            quat: camera.quaternion.clone(),
            target: controls.target.clone(),
            framesLeft: 40,
          };
        }

        // Log the full camera view whenever it changes. Copy the WHOLE line
        // (three params) into the URL. window.__streamCamParts logs each
        // part separately in case the console truncates.
        window.__streamCam = () =>
          `camPos=${camera.position.toArray().map((n) => +n.toFixed(2)).join(',')}` +
          `&camTarget=${controls.target.toArray().map((n) => +n.toFixed(2)).join(',')}` +
          `&camRot=${[rotateAngleX, rotateAngleY, rotateAngleZ].map((n) => +(n || 0).toFixed(2)).join(',')}`;
        window.__streamCamParts = () => {
          console.log('camPos  =', camera.position.toArray().map((n) => +n.toFixed(2)).join(','));
          console.log('camTarget=', controls.target.toArray().map((n) => +n.toFixed(2)).join(','));
          console.log('camRot  =', [rotateAngleX, rotateAngleY, rotateAngleZ].map((n) => +(n || 0).toFixed(2)).join(','));
        };
        let _lastLogged = '';
        every(() => {
          const s = window.__streamCam();
          if (s === _lastLogged) return;
          _lastLogged = s;
          console.log('[stream] view:  ' + s);
        }, 1000);

        animate();
        spinner(false);
      } catch (err) {
        console.error('[stream] load failed', err);
        window.dispatchEvent(new CustomEvent('sceneLoadFailed', { detail: String(err?.message ?? err) }));
      }
      return;
    }

    spinner(true);
    const loader = new SplatLoader(renderer, 250000, shLoadOverride, undefined);
    animate();
    let lastSplatPercent = -1;
    let mainSplatLoaded = false;
    let lastSplatUpdate = Date.now();
    let splatLoadErrored = false;
    let splatOnLoad = false;
    // The actual SharedState object (Splat.ts) behind the loaded splat —
    // kept so the watcher below can poll its real, authoritative .loaded/
    // .loadError flags instead of guessing from a timing heuristic. See
    // that watcher for why this matters.
    let splatSharedState = null;

    // Combined download progress across both files (main splat + background
    // point cloud) weighted by each file's own byte size — otherwise the
    // indicator sits at 99% for however long the (often 100+ MB) point cloud
    // keeps downloading after the main splat already finished.
    let splatBytesLoaded = 0, splatBytesTotal = 0;
    let plyBytesLoaded = 0, plyBytesTotal = 0;
    let lastCombinedPercent = -1;
    const CIRCUMFERENCE = 163.36;
    // Static loads: real per-stage progress covering download AND
    // processing (SOG decode, splat push) — see utils/loadProgress.js.
    // Streamed loads keep the byte/fill formula below (set in the
    // lodMetaUrl branch).
    const loadProgress = createLoadProgress();
    loader.onStageProgress = (stage, fraction) => loadProgress.stage('splat', stage, fraction);
    let isStreamedLoad = false;
    // Streamed only: share (0..1) of the starting view loaded at full
    // detail, and whether it has finished — see the lodMetaUrl branch.
    let streamViewFraction = 0;
    let streamViewWant = 0; // splats the starting view wants (sizes its share vs the point cloud)
    let streamViewReady = false;
    let streamDisplayed = 0; // eased ring value — see updateLoadingPercent
    let loadProgressTicker = null;
    const renderLoadingPercent = (percent) => {
      // Superseded by a newer scene load — stop driving the shared overlay.
      if (!isCurrentLoad()) {
        if (loadProgressTicker) { clearInterval(loadProgressTicker); loadProgressTicker = null; }
        return;
      }
      const ring = document.getElementById('loading-ring');
      const arc = document.getElementById('loading-ring-arc');
      const el = document.getElementById('loading-percent');
      if (ring && !ring.classList.contains('is-determinate')) {
        ring.classList.add('is-determinate');
        arc.setAttribute('stroke-dasharray', CIRCUMFERENCE);
      }
      if (arc) arc.setAttribute('stroke-dashoffset', CIRCUMFERENCE * (1 - percent / 100));
      if (el) el.textContent = `${percent}%`;
    };
    const updateLoadingPercent = () => {
      if (!isStreamedLoad) {
        const percent = loadProgress.percent();
        if (percent == null || percent === lastCombinedPercent) return;
        lastCombinedPercent = percent;
        renderLoadingPercent(percent);
        return;
      }
      // Don't compute a percent until BOTH totals are actually known (or we
      // know there's no point cloud for this model at all, via npl/
      // modelData.pointCloudUrl) — computing one against a still-partial
      // denominator (e.g. only the point cloud's total known so far, while
      // the main splat — often much larger — hasn't reported its own total
      // yet) produces a wildly premature, artificially-high percent.
      // Combined with the never-decrease clamp below (needed for a
      // different, legitimate reason — see its own comment), that
      // premature value then gets permanently "locked in" for the rest of
      // the load: real progress on the actually-dominant file can't ever
      // pull the display back down to reflect it, so it just sits frozen
      // at that early, wrong number (often ~99%) until the real percent
      // finally, genuinely catches back up to it right at the very end —
      // e.g. the point cloud finishing almost instantly from a local cache
      // hit while the (much larger, uncached) main splat hasn't even
      // reported its own total yet.
      const hasPointCloud = !npl && !!modelData?.pointCloudUrl;
      // Streamed: the splat share is the starting view's real load state
      // (octree.viewStatus(): resident detail + in-flight download/decode
      // progress), not bytes — there's no byte total. The point cloud's
      // share is sized by bytes against the view's estimated download
      // (~20 bytes a splat), kept within 10-40%. The ring eases toward the
      // real value (called every 50 ms) so a chunk landing reads as a quick
      // sweep, not a jump; it never goes backwards and holds below 100
      // until the view is genuinely done.
      if (isStreamedLoad) {
        const plyFrac = !hasPointCloud ? 1 : plyFinished ? 1 : plyBytesTotal > 0 ? plyBytesLoaded / plyBytesTotal : 0;
        const viewFrac = streamViewReady ? 1 : streamViewFraction;
        const viewBytes = streamViewWant * 20;
        const plyShare = !hasPointCloud ? 0
          : plyBytesTotal > 0 && viewBytes > 0 ? Math.min(0.4, Math.max(0.1, plyBytesTotal / (plyBytesTotal + viewBytes))) : 0.25;
        const target = Math.min(99, ((1 - plyShare) * viewFrac + plyShare * plyFrac) * 100);
        if (target > streamDisplayed) streamDisplayed = Math.min(target, streamDisplayed + Math.max(0.3, (target - streamDisplayed) * 0.12));
        const percent = Math.max(Math.floor(streamDisplayed), lastCombinedPercent);
        if (percent === lastCombinedPercent) return;
        lastCombinedPercent = percent;
        renderLoadingPercent(percent);
        return;
      }
      const splatTotalKnown = splatBytesTotal > 0;
      const plyTotalKnown = !hasPointCloud || plyBytesTotal > 0;
      if (!splatTotalKnown || !plyTotalKnown) return;

      const totalKnown = splatBytesTotal + plyBytesTotal;
      if (totalKnown <= 0) return;
      // Clamped to never go backwards: if one file (often the cached,
      // near-instant splat) reports "done" before the other file's total is
      // known yet, the combined total can grow once that second total
      // arrives, which would otherwise *lower* the computed percent. Rather
      // than show that dip, hold at the last displayed value until real
      // progress pushes past it again.
      const rawPercent = Math.min(Math.floor(((splatBytesLoaded + plyBytesLoaded) / totalKnown) * 100), 99);
      const percent = Math.max(rawPercent, lastCombinedPercent);
      if (percent === lastCombinedPercent) return;
      lastCombinedPercent = percent;
      renderLoadingPercent(percent);
    };

    let splatUrl;
    try {
      splatUrl = await resolveSplatUrl();
    } catch (err) {
      window.dispatchEvent(new CustomEvent('sceneLoadFailed', { detail: String(err?.message ?? err) }));
      return;
    }
    // A streamed-SOG model can have no flat format at all — resolveSplatUrl
    // then returns null and the flat-file path below is skipped entirely
    // (see the modelData.lodMetaUrl branch). Code between here and there that
    // assumes a string splatUrl is guarded with `splatUrl ? … : …`.

    // Size-based phone tier: now that resolveSplatUrl has fetched the model,
    // scale the render preset and/or splat count to the .splat's point count
    // so a large model loads instead of crash-looping. Each half is skipped if
    // the user set that control explicitly (?resolution= / ?splats=), and both
    // are a no-op on iPad/desktop (autoTierFor → 100% / High there).
    // The tier's preset becomes the Auto resolution (sharpness stays High) —
    // applied unless resolution was set by hand.
    const applyAutoPreset = (pos) => {
      autoResolutionPct = Math.min(autoResolutionPct, resolutionPctOfPreset(pos)); // never raises it
      if (resolutionManual) return;
      resolutionPct = window.__resolutionLivePct = autoResolutionPct;
      renderConfig.pixelRatio = pixelRatioForPct(resolutionPct);
      renderer.setPixelRatio(renderConfig.pixelRatio);
      renderer.setSize(window.innerWidth, window.innerHeight - bottomBarAdjustment);
    };
    // autoTierFor reads modelData.formats.splat.size — a STREAMED-only model
    // (modelData.lodMetaUrl set, no flat .splat/.sog uploaded) has no such
    // size to read, so splatCount comes out 0 and this always resolved to
    // "high" — full native pixel ratio — even on a phone, regardless of how
    // it's actually loading. PlayCanvas's own Gaussian-splat performance
    // guidance calls out reducing device pixel ratio as one of the two
    // biggest mobile levers (the other being GPU sort, which is a much
    // bigger rewrite); this is the streamed path's equivalent of what
    // autoTierFor already does for the static one.
    const isStreamedNoFlatFormat = !!modelData?.lodMetaUrl && !modelData?.formats?.splat?.size;
    if (isStreamedNoFlatFormat && isLowMemoryDevice) {
      applyAutoPreset(QUALITY_POS.balanced);
    } else {
      const splatCount = Math.floor((modelData?.formats?.splat?.size || 0) / 32);
      const tier = autoTierFor(splatCount);
      if (tier.quality !== 'high') {
        applyAutoPreset(QUALITY_POS[tier.quality] ?? QUALITY_POS.high);
      }
    }

    // Static: how much to load (see splatDensityParam / splatsLoadCap).
    if (!modelData?.lodMetaUrl) {
      const autoLoad = autoTierFor(Math.floor((modelData?.formats?.splat?.size || 0) / 32)).splats;
      let loadPct = Math.max(autoLoad, splatDensityParam ?? 0);
      if (splatsLoadCap) loadPct = Math.min(loadPct, splatsLoadCap);
      loader.splatDensity = loadPct;
    } else if (queryParams.has('splatsLoad')) {
      // Streamed SOG ignores it — don't leave a dead setting in the URL.
      const params = new URLSearchParams(window.location.search);
      params.delete('splatsLoad');
      const query = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    }

    // Crash guard — if the tab OOMs during load or first render, sessionStorage
    // survives the reload; Scene.js reads this on remount and retries with a
    // lighter setting instead of crash-looping. Cleared on genuine completion.
    try {
      sessionStorage.setItem('sceneLoadGuard', JSON.stringify({
        modelId,
        format: splatUrl ? splatUrl.split(/[?#]/)[0].split('.').pop().toLowerCase() : 'lod',
        splats: loader.splatDensity ?? 100,
        // Resolution loading now — crash recovery (Scene.js) retries lower.
        resolution: resolutionPct,
        ts: Date.now(),
      }));
    } catch (e) { /* private mode / disabled storage — skip the guard */ }

    const onProgress = (xhr) => {
      if (xhr.lengthComputable) {
        lastSplatUpdate = Date.now();
        const percent = Math.floor((xhr.loaded / xhr.total) * 100);
        if (percent > lastSplatPercent) {
          lastSplatPercent = percent;
        }
        splatBytesLoaded = xhr.loaded;
        splatBytesTotal = xhr.total;
        loadProgress.download('splat', xhr.loaded, xhr.total);
        updateLoadingPercent();
      }
    };

    // Reactive safety net: if the proactive memory check let a .sog through
    // and it fails/crashes anyway (WASM error, e.g. SOG_MOBILE_UNSUPPORTED),
    // retry once with .splat instead of dead-ending on a failure screen.
    // Only fires if a .splat alternative actually exists for this model
    // (splatFallbackUrl, set in resolveSplatUrl) and we haven't already
    // retried (triedSplatFallback) or weren't already loading splat itself.
    function startSplatLoad(url) {
      const ext = url.split(/[?#]/)[0].split('.').pop().toLowerCase();
      // Under WebGPU's default (addon) path the mesh build + SH packing is
      // an extra real stage — see buildGaussianSplatGeometry.
      loadProgress.resetFile('splat', renderer.isWebGPURenderer && useWebGPUAddon ? `${ext}+build` : ext);
      window.dispatchEvent(new CustomEvent('splatFormatResolved', { detail: ext }));
      if (ext === 'sog') showSogLoadingThumbnail();
      else hideSogLoadingThumbnail();
      // The presigned URL itself changes on every load (a fresh signature
      // each time, by design — see utils/modelCache.js), but modelData
      // (captured from this same, already canView()-gated getModel call)
      // also carries the object's current ETag, letting Splat.ts reuse a
      // locally cached copy instead of re-downloading when it's unchanged.
      const etag = modelData?.formats?.[ext]?.etag;
      loader.load(
        url,
        async (loadedSplat) => {
          // The scene was left while this was loading: free it, don't show it.
          if (sceneDisposed) { freeSplatShared(loadedSplat); return; }
          onDispose(() => freeSplatShared(loadedSplat));
          // WebGPU migration Stage 2 (?webgpu=1) — createWebGPUSplatMesh
          // reuses three.js's own native Gaussian-splat TSL renderer
          // (three/addons/objects/GaussianSplat.js) instead of our GLSL
          // SplatMaterial; see its own comment in Splat.ts. SH0 (flat
          // color) only at this stage — sh1/2/3 is later-stage work. It's
          // async (waits for the real data to finish parsing before
          // building geometry from it — see its own comment), unlike the
          // GLSL `new Splat(...)` path, which can build immediately because
          // its texture-based upload mutates in place.
          // WebGPU: our own renderer (TSL port + GPU sort, sRGB blending —
          // matches WebGL) via `new Splat`; three's addon only with
          // ?webgpuAddon=1. See WEBGPU-TSL-PORT-PLAN.md.
          const mainSplat = renderer.isWebGPURenderer && useWebGPUAddon
            ? await createWebGPUSplatMesh(loadedSplat)
            : new Splat(loadedSplat, camera, { covClamp: renderConfig.covClamp });
          scene.add(mainSplat);
          liveSplatMesh = mainSplat;
          // Live splat share (static): of the model, as a % — drawn from the
          // loaded splats via the material's density, no reload. Can't go
          // above what's loaded.
          staticSplatLoadedPct = loader.splatDensity ?? 100;
          setLiveSplatPct(splatDensityParam ?? staticSplatLoadedPct);
          initLiveSh(loadedSplat);
          // Adaptive quality for static models: SH 3→1, splat share, SH
          // off, render quality. Created once — a .splat
          // fallback reload of this function keeps the same controller.
          if (!adaptiveQuality && queryParams.get('adaptive') !== '0') {
            adaptiveQuality = createAdaptiveQuality({
              modelId,
              // Splat count first (2026-10-25): it eases the GPU most and is
              // the least visible step, so each round starts with it.
              levers: [
                {
                  name: 'primary', label: 'splat count', step: 'scale5',
                  get: () => liveSplatPct, set: setLiveSplatPct,
                  // Adaptive floor: half the model (the Settings slider can
                  // still go to SPLAT_PCT_MIN by hand).
                  min: Math.min(staticSplatLoadedPct, ADAPTIVE_SPLAT_FLOOR_PCT), max: staticSplatLoadedPct, base: staticSplatLoadedPct,
                  adapt: splatDensityParam === undefined, fmt: (v) => `${Math.round(v)}%`,
                },
                shLever(),
                shOffLever(), // continues SH's slot (see adaptiveQuality.js)
                qualityLever(),
                navLever(),
              ],
              ...ADAPTIVE_GATES,
              isReady: () => mainSplatLoaded && !spinnerActive,
            });
            if (!isAdaptiveOn()) adaptiveQuality.setEnabled(false);
          }
          setNavAutoFromSplatCount(loadedSplat.numVertices);
          splatOnLoad = true;
          splatSharedState = loadedSplat;
          lastSplatUpdate = Date.now();
          if (splatBytesTotal > 0) splatBytesLoaded = splatBytesTotal;
          updateLoadingPercent();
        },
        onProgress,
        (err) => {
          const message = String(err?.message ?? err);
          if (!triedSplatFallback && splatFallbackUrl && splatFallbackUrl !== url) {
            triedSplatFallback = true;
            console.warn(`[Format fallback] sog load failed (${message}) — retrying with splat instead`);
            // Reset progress state for the retry; leave mainSplatLoaded/
            // splatLoadErrored untouched so the watcher below keeps waiting
            // for THIS retry's own outcome, not the sog failure.
            splatBytesLoaded = 0; splatBytesTotal = 0;
            lastSplatPercent = -1; lastCombinedPercent = -1;
            lastSplatUpdate = Date.now();
            startSplatLoad(splatFallbackUrl);
            return;
          }
          mainSplatLoaded = true;
          splatLoadErrored = true;
          console.error('Scene loading failed:', err);
          window.dispatchEvent(new CustomEvent('sceneLoadFailed', { detail: message }));
        },
        etag
      );
    }
    if (modelData?.lodMetaUrl) {
      // Streamed-SOG model — the backend returned a LOD-tree URL instead of
      // (or alongside) a flat .sog/.splat. Load the octree in place of the
      // flat file; everything else in app() runs unchanged: the point-cloud
      // pick layer below (modelData.pointCloudUrl), the wait-for-both gate,
      // the spinner, and the camera already framed by initialize() from the
      // model's saved position in app-model-default-positions.
      const budgetParam = parseInt(queryParams.get('budget'), 10);
      // Default budget is device-scaled: 5M resident splats sizes the pool's
      // GPU textures at roughly 80-160MB+ CPU/GPU-side (see SplatPool's
      // constructor), on top of the sort worker's own equally-sized arrays
      // and the decode pipeline's transient/cached memory — none of which
      // previously scaled with device, unlike the static path's
      // autoTierFor/estimateAvailableMemoryBytes (hard-won from the SAME
      // iPhone 13 mini crashing on a .sog — see isLowMemoryDevice's own
      // comment in Splat.ts). Streamed-SOG's whole premise is a bounded
      // resident working set using LESS memory than a static load, which
      // only holds if that bound actually shrinks for a constrained device.
      // decodeWorkerCount and the octree's decode-file cache/write-queue/
      // catch-up-burst request cap are ALL scaled the same way — see
      // loadStreamed() in Splat.ts and the _LOW_MEMORY constants in
      // splatOctree.js.
      //
      // Back at 1M (matching PlayCanvas's own mobile guidance) as of
      // 2026-09-14: it was cut to 400k while the actual crash cause was
      // still unidentified (see ply-parse-worker.js's tryFastParse — the
      // pick-layer point cloud's full-resolution parse, not this budget,
      // turned out to be the real driver). 400k was then confirmed too
      // small a budget for this scene's density on its own: the scene got
      // mostly stuck grey even under active camera demand (zooming into a
      // region didn't resolve it), on a device where the SAME solve code
      // fills in fine at desktop's much larger default.
      // Streaming budget by device: phones 1M, tablets (iPad / Android
      // tablet — still on the low-memory path for SH degree, decode workers
      // and caches) 6M, desktop 8M (fits e.g. Concert Hall SS's 6M
      // full-detail level entirely).
      // 2026-10-16: phones 1.5M, tablets 3.5M, desktop 10M by default; the
      // user (Settings) and adaptive quality can raise it to phones 5M,
      // tablets 10M, desktop the whole model. Both are capped at the
      // model's own splat count once its tree is read (loadStreamed).
      // 2026-10-26: desktop default 8M (was 10M) and nothing automatic goes
      // above it; Settings can still raise it by hand up to the model's size
      // (the SH degree then drops to fit the GPU — see fitShDegree).
      const defaultStreamBudget = isPhoneDevice ? 1_500_000 : isLowMemoryDevice ? 3_500_000 : 8_000_000;
      const maxStreamBudget = isPhoneDevice ? 5_000_000 : isLowMemoryDevice ? 10_000_000 : Infinity;
      const streamBudget = Number.isFinite(budgetParam) && budgetParam > 0 ? Math.min(budgetParam, maxStreamBudget) : defaultStreamBudget;
      // The pool is always at least the device default, so a LOWERED saved
      // ?budget= only limits what's loaded — it used to size the pool itself,
      // which then capped the Settings budget slider at that lowered value
      // forever. A ?budget= above the default still grows the pool.
      // The pool is sized to the budget this page starts with (+ headroom,
      // in loadStreamed) — not the device max. A max-sized pool reserved
      // 3× the GPU memory + RAM on phones/tablets for budget nobody used
      // (and on WebGPU the sort walks the whole pool). Raising the budget
      // above it in Settings reloads with a bigger ?budget= (QualityModal).
      // WebGL: 1.5× the budget, so chunks behind the camera can stay loaded
      // (splatOctree.js OFFSCREEN_HOLD_UNTIL) instead of being evicted and
      // re-streamed every time the view turns. WebGPU stays at the budget —
      // its GPU sort walks the whole pool.
      const streamPoolCapacity = renderer.isWebGPURenderer ? streamBudget : Math.min(Math.round(streamBudget * 1.5), maxStreamBudget);
      window.__streamDeviceMaxBudget = maxStreamBudget;
      // LOD distance: 0 = the octree's automatic value, derived from each
      // scene's own chunk sizes (baseDistance = avg leaf radius × constant).
      // Was hardcoded 710 / 2.7 (tuned for Cinevilla, a scene hundreds of
      // metres across) — in a ~40 m concert hall that made every chunk ask
      // for full detail, over budget, leaving chunks unloaded (grey holes).
      // Verified headless 2026-10-02: auto fixes the hall (no alloc failures)
      // and renders Cinevilla the same as 710 (auto ≈ 485 there).
      // ?lodDist= / ?lodMult= still override.
      const lodDist = parseFloat(queryParams.get('lodDist')) || 0;
      const lodMult = parseFloat(queryParams.get('lodMult')) || 0;
      // Coarse-first prewarm — DEFAULT ON as of 2026-09-14 (2nd attempt).
      // Every leaf's coarsest LOD is requested up front so turning to face a
      // new area shows a blurry-but-correct shell instead of grey — after
      // PlayCanvas's own documented Streamed-SOG behaviour (researched
      // 2026-09-14: "downloads lowest LODs first... appears almost
      // instantly... sharpening progressively"). A first attempt (see
      // STREAMING-SOG-STATUS.md section 9) ALSO made resident leaves
      // permanently unfreeable and regressed real captures; this version
      // only changes REQUEST PRIORITY, not freeing rules — see
      // splatOctree.js's SplatOctree constructor comment for the fix.
      // ?prewarm=0 disables.
      const prewarm = queryParams.get('prewarm') !== '0';
      // ?knapsack=1 / ?exactPack=1 / ?defrag=1 — opt-in Stage 1 flags for the
      // "match PlayCanvas" streaming rewrite (see STREAMING-SOG-STATUS.md and
      // /Users/uldis.liepins/.claude/plans/floating-launching-blum.md). All
      // default OFF; the legacy solver/allocator stay the default so this is
      // an instant-revert lever, not a replacement. ?defrag= only has any
      // effect when ?exactPack=1 is also set (see SplatOctree's constructor).
      const knapsack = queryParams.get('knapsack') === '1';
      // Exact-size allocator is the DEFAULT as of 2026-10-03 (?exactPack=0
      // reverts to the buddy allocator). Measured headless: the buddy
      // allocator's power-of-two rounding wasted ~29% of the pool (pool
      // "full" at 71% utilisation, allocFailures climbing ~190/s forever,
      // chunks left with no data at all = permanent grey areas) and its
      // budget accounting counted tiny chunks as 512 slots, so far less
      // detail loaded. Exact: 99% utilisation, 0 failures, every chunk gets
      // data, and more detail at default settings (Concert Hall SS 1.73M →
      // 2.39M, Cinevilla SS 1.17M → 1.81M resident), WebGL and WebGPU.
      const exactPack = queryParams.get('exactPack') !== '0';
      const defrag = queryParams.get('defrag') === '1';
      // No single upfront byte total for a streamed load, so give
      // updateLoadingPercent something to work with — the octree's fill
      // toward budget — otherwise it early-returns on splatBytesTotal===0
      // and the loading ring never shows a number.
      isStreamedLoad = true;
      loadProgressTicker = every(updateLoadingPercent, 50);
      splatBytesTotal = streamBudget;
      splatBytesLoaded = 0;
      // Download pass for the streamed files (backend getModel's lodQuery — a
      // CloudFront signed query, valid ~2 h): renewed every 30 min while this
      // scene is open; stops once the scene has been left (no teardown here —
      // container.isConnected, as sceneLoadingLock).
      if (modelData.lodQuery) {
        const passRenewal = every(() => {
          if (!container.isConnected) { clearInterval(passRenewal); return; }
          authFetch(`${API_URL}?getModel=true&id=${modelId}`)
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => { if (data?.lodQuery) setAccessQuery(data.lodQuery); })
            .catch(() => { /* next attempt in 30 min; the current pass is still valid */ });
        }, 30 * 60 * 1000);
      }
      loader.loadStreamed(lodTreeUrl(modelData.lodMetaUrl), streamBudget, { lodDist, lodMult, prewarm, knapsack, exactPack, defrag, poolCapacity: streamPoolCapacity, accessQuery: modelData.lodQuery })
        .then((shared) => {
          // The scene was left while this was loading: free it, don't show it.
          if (sceneDisposed) { freeSplatShared(shared); return; }
          onDispose(() => freeSplatShared(shared));
          const mainSplat = new Splat(shared, camera, { covClamp: renderConfig.covClamp });
          scene.add(mainSplat);
          activeOctree = shared.octree;
          // Adaptive quality (three/adaptiveQuality.js): starts measuring
          // once the loading overlay is gone. A manual Quality / pixel-ratio
          // pick freezes resolution, a manual ?budget= freezes the budget;
          // ?adaptive=0 turns it off.
          liveSplatMesh = mainSplat;
          // Budget range: up to the pool (device max, or the whole model).
          // Largest budget allowed (device max or the model) — the pool is a
          // bit bigger than this, as swap headroom.
          const streamPoolSize = shared.octree.budgetMax || shared.octree.pool.capacity;
          const streamAutoBudget = Math.min(defaultStreamBudget, streamPoolSize);
          initLiveSh(shared);
          if (queryParams.get('adaptive') !== '0') {
            adaptiveQuality = createAdaptiveQuality({
              modelId,
              // Detail (fewer splats drawn) first, then SH 3→2→1→off,
              // resolution, navigation quality, in turn (detail first since
              // 2026-10-25: it eases the GPU most and is the least visible).
              levers: [
                {
                  // Level of detail, not the budget (2026-10-21): cutting the
                  // budget below what's loaded forced evictions and
                  // re-streaming (GPU upload stalls on Apple GPUs) while
                  // hardly lowering what's drawn. Coarser LOD everywhere
                  // draws fewer splats at once. 100% = device default, down
                  // to 50% (distance bands ×2). The budget is manual only.
                  name: 'detail', label: 'detail', step: 'tens', settleAfterRaise: true,
                  get: () => shared.octree.detailPct, set: (v) => shared.octree.setDetail(v),
                  min: adaptiveMinDetailPct(shared.octree.budget, shared.octree.modelSplats), max: 100, base: 100, adapt: true,
                  fmt: (v) => `${v}%`,
                },
                shLever(),
                shOffLever(), // continues SH's slot (see adaptiveQuality.js)
                qualityLever(),
                navLever(),
              ],
              ...ADAPTIVE_GATES,
              isReady: () => mainSplatLoaded && !spinnerActive,
            });
            if (!isAdaptiveOn()) adaptiveQuality.setEnabled(false);
            // A budget the user set (?budget=, the menu's saved pick) is used
            // as set: adaptive quality doesn't shrink the detail planned
            // within it (it halved it on an iPad at 3.5M, 2026-10-28).
            if (Number.isFinite(budgetParam) && budgetParam > 0) adaptiveQuality.freezePrimary();
          }
          // Quality menu's budget Auto switch (on = adaptive quality manages
          // the budget from the device default; off = keep what's set).
          window.__setStreamBudgetAuto = (on) => {
            if (!on) { shared.octree.setDetail(100); adaptiveQuality?.freezePrimary?.(); return shared.octree.budget; }
            const b = adaptiveQuality?.resumePrimary?.();
            if (b == null) shared.octree.setTuning({ budget: streamAutoBudget });
            return b ?? streamAutoBudget;
          };
          // Quality menu's Performance switch — applies live.
          window.__setStreamPerformance = (on) => shared.octree.setVisibleCap(streamPerformanceCap(on));
          // Streamed: the resident budget is what's drawn at most.
          setNavAutoFromSplatCount(streamBudget);
          setStreamedZoomBound(shared.bound);
          splatSharedState = shared;
          splatOnLoad = true;

          // The loading overlay stays up until the starting view is FULLY
          // loaded — every visible chunk at the detail the solver wants,
          // including the at-rest refinement, or as much as this device's
          // budget fits (octree.viewStatus() counts a chunk whose detail
          // didn't fit as done) — held for STREAM_SETTLE_MS so a momentary
          // gap between solves doesn't count. Never stuck: if real progress
          // (which includes download bytes) hasn't moved for STREAM_STALL_MS
          // the view is as loaded as it's going to get, so open. After
          // that, streaming carries on as normal.
          const STREAM_SETTLE_MS = 600;
          const STREAM_STALL_MS = 12000;
          let completeSince = 0;
          let bestFraction = -1;
          let progressAt = Date.now();
          shared.octree?.setLoading?.(true);
          const fillPoll = every(() => {
            splatBytesLoaded = Math.min(window.__splatOctreeStats?.residentSplats || 0, streamBudget);
            lastSplatUpdate = Date.now();
            const status = shared.octree?.viewStatus?.() || { fraction: 1, complete: true, want: 0 };
            streamViewFraction = status.fraction;
            streamViewWant = status.want || 0;
            // A hidden tab renders no frames (no GPU writes) — not a stall.
            if (document.hidden || status.fraction > bestFraction + 0.001) { bestFraction = Math.max(bestFraction, status.fraction); progressAt = Date.now(); }
            completeSince = status.complete ? (completeSince || Date.now()) : 0;
            if (!streamViewReady && completeSince && Date.now() - completeSince >= STREAM_SETTLE_MS) {
              streamViewReady = true;
              if (status.limited) console.log('[stream] starting view loaded;', status.limited, 'chunk(s) at reduced detail (device budget)');
            } else if (!streamViewReady && Date.now() - progressAt > STREAM_STALL_MS) {
              console.warn('[stream] starting view made no progress for', STREAM_STALL_MS / 1000, 's at', Math.round(status.fraction * 100), '% — opening');
              streamViewReady = true;
            }
            if (streamViewReady) shared.octree?.setLoading?.(false);
            if (mainSplatLoaded || splatLoadErrored) { shared.octree?.setLoading?.(false); clearInterval(fillPoll); }
          }, 200);

        })
        .catch((err) => {
          mainSplatLoaded = true;
          splatLoadErrored = true;
          console.error('Streamed scene load failed:', err);
          window.dispatchEvent(new CustomEvent('sceneLoadFailed', { detail: String(err?.message ?? err) }));
        });
    } else {
      // Registered up front (not when its fetch starts) so the estimate
      // never jumps when a second file appears mid-load.
      if (!npl && modelData?.pointCloudUrl) loadProgress.addFile('pc', 'pointcloud');
      startSplatLoad(splatUrl);
      // Ticks the display glide/creep between real progress reports.
      loadProgressTicker = every(updateLoadingPercent, 50);
    }

    // Watches for GENUINE completion — not a timing guess. For raw
    // .splat/.cply, the loader's onLoad fires almost immediately (right
    // after fetching HTTP headers); the actual byte streaming only happens
    // later, asynchronously, via lazyLoad() (Splat.ts), kicked off by the
    // mesh's first render. That function is now the single source of truth:
    // it sets shared.loaded = true only once it has actually finished
    // running, and shared.loadError if the stream failed or was truncated
    // partway through. Polling that real flag — instead of inferring
    // "probably done" from "no progress update in N seconds" — means the
    // overlay can never close on a load that hasn't genuinely finished,
    // regardless of what percentage happened to be displayed at the time.
    (async () => {
      while (!mainSplatLoaded && !splatLoadErrored) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        // Streamed: also wait for the starting view to be fully loaded
        // (streamViewReady). Static loads: unchanged.
        if (splatOnLoad && splatSharedState?.loaded && (!isStreamedLoad || streamViewReady)) {
          if (splatSharedState.loadError) {
            mainSplatLoaded = true;
            splatLoadErrored = true;
            console.error('Scene loading failed (stream error):', splatSharedState.loadError);
            window.dispatchEvent(new CustomEvent('sceneLoadFailed', {
              detail: String(splatSharedState.loadError?.message ?? splatSharedState.loadError),
            }));
          } else {
            mainSplatLoaded = true;
            loadProgress.done('splat');
          }
        }
      }
    })();

    // Optional PLY point cloud (unchanged)
    let lastPLYPercent = -1;
    let plyFinished = false;

    if (!npl && modelData?.pointCloudUrl) {
      const pointCloudUrl = modelData.pointCloudUrl;
      const pointCloudEtag = modelData.pointCloudEtag;

      // Fetch (transparently cache-aware, see utils/modelCache.js) manually
      // instead of PLYLoader's own .load() — that's the only way to route
      // this through the local cache, since .load() always does its own
      // internal fetch with no way to substitute a cached Response.
      // Progress reporting is reconstructed by hand here (a streamed read,
      // same {lengthComputable, loaded, total} shape PLYLoader's own
      // onProgress used to hand back) since parsing (see parsePLYInWorker)
      // takes a single, already-fully-buffered ArrayBuffer with no
      // incremental callback.
      (async () => {
        try {
          const response = await fetchWithModelCache(pointCloudUrl, pointCloudEtag);
          if (!response.ok || !response.body) throw new Error(`Point cloud fetch failed with status ${response.status}`);

          const contentLength = response.headers.get('content-length');
          const total = contentLength ? parseInt(contentLength, 10) : 0;
          const reader = response.body.getReader();
          const chunks = [];
          let received = 0;
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            received += value.length;
            if (total > 0) {
              const percent = Math.floor((received / total) * 100);
              if (percent < 100 && percent > lastPLYPercent) {
                lastPLYPercent = percent;
              }
              plyBytesLoaded = received;
              plyBytesTotal = total;
              loadProgress.download('pc', received, total);
              updateLoadingPercent();
            }
          }
          // Download finished — the worker parse below is the processing stage.
          if (total > 0) loadProgress.download('pc', total, total);
          const buffer = new Uint8Array(received);
          let offset = 0;
          for (const chunk of chunks) { buffer.set(chunk, offset); offset += chunk.length; }

          // Off the main thread — see parsePLYInWorker for why.
          const ply = await parsePLYInWorker(buffer.buffer, MAX_PICKING_POINTS);

          plyFinished = true;
          loadProgress.done('pc');
          if (plyBytesTotal > 0) plyBytesLoaded = plyBytesTotal;
          updateLoadingPercent();
          const material = new THREE.PointsMaterial({ color: 0xffffff, size: pointSize });
          const points = new THREE.Points(decimateGeometry(ply, MAX_PICKING_POINTS), material);
          if (!debug) points.visible = false;
          scene.add(points);

          await new Promise(r => requestAnimationFrame(r));
          renderer.render(scene, camera);
          await new Promise(r => requestAnimationFrame(r));
          await new Promise(r => setTimeout(r, 500));
        } catch (err) {
          plyFinished = true;
          loadProgress.done('pc');
          if (plyBytesTotal > 0) plyBytesLoaded = plyBytesTotal;
          updateLoadingPercent();
          console.warn('Point cloud load failed or not available', err);
          await new Promise(r => setTimeout(r, 500));
        }
      })();
    } else {
      console.log('npl present or no point-cloud file for this model — skipping pointcloud load');
      plyFinished = true;
    }

    // Wait for both to finish
    while (!mainSplatLoaded || !plyFinished) {
      await new Promise(r => setTimeout(r, 200));
    }

    // By this point mainSplatLoaded is only ever true because splat.loaded
    // was genuinely confirmed (or a real error was caught — handled below).
    // The displayed percent is a separate, cosmetic, byte-count-based
    // estimate that can lag slightly behind that real signal (e.g. the last
    // few % of a large point-cloud file finishing its own bookkeeping) —
    // never let the overlay disappear while it's still showing some lower
    // number, purely so the transition always reads as a clean finish.
    // Skipped on a genuine load error — nothing to "finish" in that case.
    if (loadProgressTicker) clearInterval(loadProgressTicker);
    if (!splatLoadErrored) {
      // Finish with a short sweep from wherever the ring is to 100% —
      // the estimate can overshoot the real load time (a load that's faster
      // than expected would otherwise snap from e.g. 45% straight to 100%).
      // Skipped in a hidden tab: rAF doesn't run there and would stall
      // the load's completion.
      const from = Math.max(0, lastCombinedPercent);
      lastCombinedPercent = 100;
      if (!document.hidden && from < 100) {
        const duration = 150 + 4 * (100 - from); // 45% → ~370 ms, 95% → ~170 ms
        const sweepStart = performance.now();
        await new Promise((resolve) => {
          const step = () => {
            const k = Math.min(1, (performance.now() - sweepStart) / duration);
            renderLoadingPercent(Math.round(from + (100 - from) * (1 - (1 - k) * (1 - k))));
            if (k < 1) requestAnimationFrame(step); else resolve();
          };
          requestAnimationFrame(step);
        });
      } else {
        renderLoadingPercent(100);
      }
      await new Promise(r => setTimeout(r, 200));
    }

    spinner(false);
    if (!splatLoadErrored) {
      console.log('Scene loading completed');
      // Performance test switches (DEBUG): ?perfNoStream=1 stops writing new
      // streamed chunks to the GPU 5 s after load; ?perfNoSort=1 stops
      // re-sorting / re-uploading the draw order. Both only to find what
      // holds the GPU up — the picture is not kept correct.
      if (queryParams.get('perfNoStream') === '1') setTimeout(() => { window.__perfNoStream = true; console.log('[perf] test: streaming writes stopped'); }, 5000);
      if (queryParams.get('perfNoSort') === '1') { window.__perfNoSort = true; console.log('[perf] test: re-sorting stopped'); }
      // Survived the load — a render-time OOM would hit right about now, so
      // give it a few frames + a beat before clearing the crash guard.
      requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(() => {
        try { sessionStorage.removeItem('sceneLoadGuard'); } catch (e) { /* noop */ }
      }, 1500)));
    }
    if (!splatLoadErrored && shouldCaptureThumbnail) {
      shouldCaptureThumbnail = false;
      captureThumbnail();
    }
  }

  let previousWidth = window.innerWidth;
  let previousHeight = window.innerHeight - bottomBarAdjustment;

  function onWindowResize() {

    // Calculate the new width and height, accounting for adjustments
    const newWidth = window.innerWidth;
    const newHeight = window.innerHeight - bottomBarAdjustment;

    //camera.up.set(0, 1, 0); // Standard Y-up orientation

    // Update aspect ratio for horizontal and vertical resizing
    camera.aspect = newWidth / newHeight;
    applyLens(); // fov follows the lens + frame, not the previous fov

    // Update renderer size (viewport size)
    renderer.setSize(newWidth, newHeight);

    // Optionally update controls if the target shifts
    controls.update();

    // Re-render the scene to reflect updates
    renderer.render(scene, camera);

    // Update focal point and circle (optional)
    if (controls.target) {
      const direction = new THREE.Vector3();
      camera.getWorldDirection(direction);
      const distance = camera.position.distanceTo(controls.target);
      const newFocalPoint = new THREE.Vector3().copy(camera.position).add(direction.multiplyScalar(distance));
      //updateOrCreateCircle(newFocalPoint);
    }

    // Update previous size values for the next resize event
    previousWidth = newWidth;
    previousHeight = newHeight;
  }

  // Convert base64 to Blob
  function base64ToBlob(base64, mimeType) {
    let byteCharacters = atob(base64.split(',')[1]); // Remove `data:image/png;base64,`
    let byteArrays = [];
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
    let byteArray = new Uint8Array(byteArrays);
    return new Blob([byteArray], { type: mimeType });
  }

  // .sog loads buffer the whole file before WASM-decoding (no progressive
  // render like ply/cply/splat), so the viewport sits on the plain dark
  // background for the entire download. Show the model's thumbnail on top
  // of the canvas instead, until loading completes. A CSS background on
  // #three-container itself isn't enough — `scene.background` (set below)
  // is an opaque color that the renderer paints over the whole canvas every
  // frame from the very first render, so it would hide a background-image
  // sitting behind the canvas just as effectively as the plain dark color.
  function showSogLoadingThumbnail() {
    const container = document.getElementById('three-container');
    if (!container) return;
    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }
    let el = document.getElementById('sog-loading-thumbnail');
    if (!el) {
      el = document.createElement('div');
      el.id = 'sog-loading-thumbnail';
      el.style.cssText =
        'position:absolute;inset:0;background-size:cover;background-position:center;background-repeat:no-repeat;pointer-events:none;';
      container.appendChild(el);
    }
    el.style.backgroundImage = `url(${UPLOADS_PUBLIC_URL}/${modelId}.jpg)`;
    el.style.display = 'block';
  }
  function hideSogLoadingThumbnail() {
    const el = document.getElementById('sog-loading-thumbnail');
    if (el) el.style.display = 'none';
  }

  function spinner(active = false) {
    const overlay = document.getElementById('upload-overlay');
    const ring = document.getElementById('loading-ring');
    const arc = document.getElementById('loading-ring-arc');
    const el = document.getElementById('loading-percent');
    if (active) {
      overlay.style.display = 'flex';
      document.body.classList.add('upload-active');
      spinnerActive = true;
      // Reset to indeterminate spinning state
      if (ring) ring.classList.remove('is-determinate');
      if (arc) arc.setAttribute('stroke-dasharray', '40 123.36');
      if (el) el.textContent = '';
    } else {
      overlay.style.display = 'none';
      document.body.classList.remove('upload-active');
      spinnerActive = false;
      if (el) el.textContent = '';
      setOverlayStatus('');
      hideSogLoadingThumbnail();
    }
  }

  async function captureThumbnail() {
    // Streamed-SOG only (activeOctree is null on the static path, so this is
    // a no-op there): the "wait for both" gate that precedes this call only
    // means the FIRST chunk landed, not that the octree has finished
    // resolving the parked thumbnail view. Wait until it's idle — no queued
    // GPU writes, no decodes in flight — stable for a beat, or the thumbnail
    // captures a half-streamed scene. Bounded so a stalled stream can't hang
    // the capture forever.
    if (activeOctree) {
      const settleStart = Date.now();
      let stableMs = 0;
      while (Date.now() - settleStart < 25000) {
        await new Promise(r => setTimeout(r, 200));
        const s = window.__splatOctreeStats || {};
        const idle = (s.writeQueueLen || 0) === 0 && (s.pendingDecodes || 0) === 0;
        stableMs = idle ? stableMs + 200 : 0;
        if (stableMs >= 1200) break;
      }
    }

    // Wait for the scene to settle (splat sorting is async)
    await new Promise(r => setTimeout(r, 800));

    // Ask the animate loop to hand us the canvas on its next render —
    // this guarantees we read the canvas in the same JS tick as renderer.render(),
    // before WebGL clears the buffer for the next frame.
    ensureFullResolution();
    const originalCanvas = await new Promise(resolve => {
      pendingThumbnailResolve = resolve;
    });

    // Use the canvas's actual backing-store resolution directly — NOT
    // divided by window.devicePixelRatio. The canvas was sized via
    // renderer.setPixelRatio(renderConfig.pixelRatio), which is scaled
    // *below* devicePixelRatio for every quality preset except 'high'/'best'
    // (e.g. 'medium' renders at dpr*0.5). Dividing by the wrong (larger)
    // devicePixelRatio here under-reported the true resolution — e.g. at
    // 'medium' quality on a dpr=2 display, a 1600-device-pixel-wide canvas
    // was treated as 800 CSS px wide, so raising `maxSize` alone had no
    // effect: the destination canvas below was already capped at that
    // wrong, halved number before maxSize ever got a chance to matter.
    const originalWidth = originalCanvas.width;
    const originalHeight = originalCanvas.height;

    // Was 800 — sized only for the small Projects-page grid card
    // (.projects-model-item-img never renders wider than ~300-400px there).
    // Now also used as a full-viewport background while a .sog loads
    // (showSogLoadingThumbnail), where 800px gets upscaled ~2x+ on most
    // desktop screens and reads as visibly blurry. 1600 covers that case
    // sharply on typical displays while staying a trivial JPEG file size.
    const maxSize = 1600;
    const scale = Math.min(1, maxSize / Math.max(originalWidth, originalHeight));
    const thumbWidth = Math.round(originalWidth * scale);
    const thumbHeight = Math.round(originalHeight * scale);

    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.width = thumbWidth;
    thumbCanvas.height = thumbHeight;
    thumbCanvas.getContext('2d').drawImage(
      originalCanvas,
      0, 0, originalCanvas.width, originalCanvas.height,
      0, 0, thumbWidth, thumbHeight
    );

    const imageData = thumbCanvas.toDataURL('image/jpeg', 0.8);

    console.log(`[Thumbnail] Image captured (${new Date().toISOString()})`);
    const imageBlob = await base64ToBlob(imageData, 'image/jpeg');

    try {
      const res = await authFetch(
        `${API_URL}?extension=jpg&id=${modelId}`
      );
      if (!res.ok) throw new Error('Failed to get thumbnail presigned URL');
      const { url } = await res.json();
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: imageBlob,
        // NOT immutable/long-max-age like the one-off capture uploads below —
        // this thumbnail lives at a fixed {modelId}.jpg key that gets
        // overwritten every time defaultPosition=true re-captures it. An
        // immutable/1-year cache told every browser that had ever loaded it
        // to keep showing the old bytes indefinitely, regardless of what
        // actually changed in S3. 'no-cache' forces a conditional
        // (ETag-based) revalidation on every load — a cheap 304 when
        // unchanged, but always picks up a genuinely new capture.
        headers: { 'Content-Type': 'binary/octet-stream', 'Cache-Control': 'no-cache' },
      });
      // fetch() only rejects on a network failure, not on an HTTP error
      // status — an S3 403 (e.g. a Cache-Control header here that doesn't
      // exactly match what the backend's presigned URL was signed with)
      // would otherwise fall straight through to the success log below
      // with the actual PUT silently having done nothing.
      if (!uploadResponse.ok) {
        throw new Error(`Thumbnail PUT failed with status ${uploadResponse.status}`);
      }
      console.log(`[Thumbnail] Stored in S3 for model ${modelId} (${new Date().toISOString()})`);
    } catch (err) {
      console.error('[Thumbnail] Upload failed:', err);
    }
  }

  // Shared by both the logged-in upload flow (takeScreenshot) and the
  // shared-link local-download flow (downloadScreenshotLocally) — same
  // crop-to-aspect-ratio math either way, just a different destination for
  // the resulting Blob.
  // Captures are rendered at a fixed size, independent of the screen: the
  // same framing the user sees (the aspect-ratio frame, or 16:9 when none is
  // set), re-rendered at ~8.3 MP — 3840x2160 for 16:9; other ratios keep the
  // same pixel count so file sizes stay alike — and saved as JPEG (~1.5-2.5
  // MB; PNG at this size would be 15+ MB). Capped at the GPU's max size.
  const CAPTURE_PIXELS = 3840 * 2160;
  // Capture crop when the aspect ratio is None (the only place 16:9 is a
  // default — the view itself follows the screen, with no overlay).
  const CAPTURE_DEFAULT_ASPECT = '16:9';
  const CAPTURE_JPEG_QUALITY = 0.92;

  function captureMaxDimension() {
    try {
      if (renderer.isWebGPURenderer) {
        return renderer.backend?.device?.limits?.maxTextureDimension2D || 8192;
      }
      const gl = renderer.getContext();
      const dims = gl.getParameter(gl.MAX_VIEWPORT_DIMS);
      return Math.min(renderer.capabilities?.maxTextureSize || 4096, dims ? Math.min(dims[0], dims[1]) : 4096);
    } catch (e) {
      return 4096;
    }
  }

  // Captures boost everything that can change without a reload, whatever
  // the user's Quality settings (adaptive quality paused meanwhile):
  //  - render quality: Best covClamp (resolution is the fixed capture size;
  //    navigation quality is reset by ensureFullResolution)
  //  - static: every loaded splat drawn (the live splat share may be lower)
  //  - spherical harmonics at the loaded degree (the live degree may be lower)
  //  - streamed SOG: budget raised to the GPU pool's capacity, adaptive
  //    detail at 100 % and no Performance draw target, then wait (up
  //    to CAPTURE_STREAM_WAIT_MS) until the view has finished loading
  // Restored right after. Splat count, SH degree and file format decide
  // what's loaded at all (changing them needs a reload), so the capture
  // uses the data already loaded.
  const CAPTURE_STREAM_WAIT_MS = 15000;
  const CAPTURE_SORT_WAIT_MS = 2000;
  const CAPTURE_ADAPTIVE_HOLD_MS = 2500;
  async function withBestCaptureQuality(fn) {
    captureInProgress = true;
    const restore = [];
    try {
      const mat = liveSplatMesh?.material;
      if (mat && 'covClamp' in mat) {
        const prevCov = mat.covClamp;
        mat.covClamp = qualitySettings(QUALITY_POS.best, dpr).covClamp;
        restore.push(() => { mat.covClamp = prevCov; });
      }
      // Spherical harmonics at the loaded degree (the live degree may be lower).
      if (liveShDegree < loadedShDegree) {
        const prevSh = liveShDegree;
        setLiveShDegree(loadedShDegree);
        restore.push(() => setLiveShDegree(prevSh));
      }
      // Static: draw every loaded splat (the live share may be lower).
      if (!activeOctree && liveSplatPct < staticSplatLoadedPct) {
        const prevPct = liveSplatPct;
        setLiveSplatPct(staticSplatLoadedPct);
        restore.push(() => setLiveSplatPct(prevPct));
        // The CPU sort (WebGL) leaves skipped splats out of the draw order,
        // so the extra splats only render once a sort at the full share has
        // landed — wait for it (the WebGPU GPU sort always includes all).
        const mesh = liveSplatMesh;
        if (mesh && 'sortedDensity' in mesh) {
          const start = performance.now();
          while (mesh.sortedDensity < 1 && performance.now() - start < CAPTURE_SORT_WAIT_MS) {
            await new Promise((r) => requestAnimationFrame(r));
          }
        }
      }

      const octree = activeOctree;
      if (octree?.pool && typeof octree.viewStatus === 'function') {
        const prevBudget = octree.budget;
        if (octree.pool.capacity > prevBudget) {
          octree.setTuning({ budget: octree.pool.capacity });
          restore.push(() => octree.setTuning({ budget: prevBudget }));
        }
        // Adaptive quality's detail lever and the Performance draw target
        // both scale the plan down — a raised budget alone still loaded
        // (and viewStatus waited for) the coarser view.
        if (octree.detailPct < 100) {
          const prevDetail = octree.detailPct;
          octree.setDetail(100);
          restore.push(() => octree.setDetail(prevDetail));
        }
        if (Number.isFinite(octree._visibleCap)) {
          const prevCap = octree._visibleCap;
          octree.setVisibleCap(Infinity);
          restore.push(() => octree.setVisibleCap(prevCap));
        }
        // animate() keeps streaming meanwhile. Two frames first so the
        // re-solve has run before viewStatus is read.
        const start = performance.now();
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        if (!octree.viewStatus().complete) setOverlayStatus('Loading full detail for capture…');
        while (!octree.viewStatus().complete && performance.now() - start < CAPTURE_STREAM_WAIT_MS) {
          await new Promise((r) => setTimeout(r, 100));
        }
      }

      return await fn();
    } finally {
      restore.reverse().forEach((undo) => { try { undo(); } catch (e) { /* keep restoring */ } });
      captureInProgress = false;
      // Don't let the capture's own slow frames and the streaming catch-up
      // look like the scene struggling — adaptive quality measures afresh.
      adaptiveQuality?.holdOff?.(CAPTURE_ADAPTIVE_HOLD_MS);
    }
  }

  async function getCroppedScreenshotBlob(aspectRatio = null) {
    return withBestCaptureQuality(() => renderCaptureBlob(aspectRatio));
  }

  async function renderCaptureBlob(aspectRatio) {
    controls.update();
    updateCameraRotation();
    ensureFullResolution();

    const [ratioW, ratioH] = (aspectRatio || CAPTURE_DEFAULT_ASPECT).split(':').map(Number);
    const targetAspect = ratioW > 0 && ratioH > 0 ? ratioW / ratioH : 16 / 9;

    // Framing: the centred targetAspect rectangle of the on-screen view
    // (CSS px) — what the aspect-ratio frame shows.
    const viewSize = renderer.getSize(new THREE.Vector2());
    let cropWidth = viewSize.x;
    let cropHeight = viewSize.y;
    if (viewSize.x / viewSize.y > targetAspect) cropWidth = viewSize.y * targetAspect;
    else cropHeight = viewSize.x / targetAspect;
    const offsetX = (viewSize.x - cropWidth) / 2;
    const offsetY = (viewSize.y - cropHeight) / 2;

    // Output size: CAPTURE_PIXELS at targetAspect, within the GPU limit.
    let outWidth = Math.sqrt(CAPTURE_PIXELS * targetAspect);
    let outHeight = outWidth / targetAspect;
    const maxDim = captureMaxDimension();
    const fit = Math.min(1, maxDim / outWidth, maxDim / outHeight);
    outWidth = Math.round(outWidth * fit);
    outHeight = Math.round(outHeight * fit);

    // Render that framing at the output size, copy it, then restore the
    // on-screen size — all in this task, so the screen never shows it.
    const prevPixelRatio = renderer.getPixelRatio();
    const output = document.createElement('canvas');
    output.width = outWidth;
    output.height = outHeight;
    // Only the model — never the (normally hidden) picking point cloud,
    // even with ?debug. Labels and the focal circle are DOM, not in here.
    const hiddenForCapture = scene.children.filter((o) => o.isPoints && o.visible);
    hiddenForCapture.forEach((o) => { o.visible = false; });
    try {
      camera.setViewOffset(viewSize.x, viewSize.y, offsetX, offsetY, cropWidth, cropHeight);
      renderer.setPixelRatio(1);
      renderer.setSize(outWidth, outHeight, false);
      renderer.render(scene, camera);

      const ctx = output.getContext('2d');
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, outWidth, outHeight);
      ctx.drawImage(renderer.domElement, 0, 0, outWidth, outHeight);
    } finally {
      hiddenForCapture.forEach((o) => { o.visible = true; });
      camera.clearViewOffset();
      renderer.setPixelRatio(prevPixelRatio);
      renderer.setSize(viewSize.x, viewSize.y, false);
      renderer.render(scene, camera);
    }

    lastCaptureSize = { width: outWidth, height: outHeight };
    return new Promise((resolve) => output.toBlob(resolve, 'image/jpeg', CAPTURE_JPEG_QUALITY));
  }

  // Camera settings a capture was taken with — saved next to it
  // (meta/capture_<ts>.json) for the Captures page's PDF shotlist.
  let lastCaptureSize = null;
  const SENSOR_NAMES = { 2.97: 'Super 16', 1.61: 'Super 35', 1: 'Large Format' };
  function captureMetadata() {
    return {
      version: 1,
      takenAt: new Date().toISOString(),
      sensor: SENSOR_NAMES[camera.zoom] || `zoom ${camera.zoom}`,
      focalLength: focalLength,
      aspectRatio: aspectRatio || 'None',
      frame: aspectRatio || CAPTURE_DEFAULT_ASPECT,
      width: lastCaptureSize?.width,
      height: lastCaptureSize?.height,
      pan: rotateAngleY, tilt: rotateAngleX, roll: rotateAngleZ,
      cameraPosition: camera.position.toArray(),
      cameraTarget: controls.target.toArray(),
    };
  }

  // Small JPEG of a capture for the Captures page grid (full-size captures
  // are a few MB and made the grid slow and occasionally fail to load).
  // Fullscreen view and ZIP download still use the original.
  // Keep in sync with captureThumbKey's _w1024 in backend/index.js.
  const CAPTURE_THUMB_WIDTH = 1024;
  async function makeCaptureThumbnail(blob) {
    const bitmap = await createImageBitmap(blob);
    const scale = Math.min(1, CAPTURE_THUMB_WIDTH / bitmap.width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (bitmap.close) bitmap.close();
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  }

  // The current view as URL params — the same set the Captures link passes,
  // which scene.js / initialize() restore on load.
  function currentViewParams(params) {
    params.set('modelId', modelId);
    params.set('ss', camera.zoom);
    params.set('ar', aspectRatio ? aspectRatio : 'None');
    params.set('fl', focalLength);
    params.set('ptr', `${rotateAngleX},${rotateAngleY},${rotateAngleZ}`);
    params.set('ldo', `${translatePan},${translateTilt},${translateDolly}`);
    params.set('cp', camera.position.toArray().join(','));
    params.set('tp', controls.target.toArray().join(','));
    return params;
  }

  async function takeScreenshot(aspectRatio = null) {
    // Show the spinner overlay during the upload process
    spinner(true);
    setOverlayStatus('Taking capture…');
    let saved = false;

    try {
      const imageBlob = await getCroppedScreenshotBlob(aspectRatio);
      setOverlayStatus('Saving capture…');

      // Fetch the presigned URLs (capture + thumbnail; v=2, captureFormat=jpg)
      let response = await authFetch(`${API_URL}?extension=png&v=2&captureFormat=jpg&id=${modelId}`, {
        method: 'GET',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch presigned URL for capture upload');
      }

      const data = await response.json();
      let presignedUrl = data.url;
      modelId = data.id;
      let uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: imageBlob,
        headers: {
          'Content-Type': 'image/jpeg',
          'Cache-Control': 'public, max-age=31536000, immutable',
        },
      });
      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }
      saved = true;

      // Its settings, for the PDF shotlist — like the thumbnail, a failure
      // here doesn't fail the capture.
      if (data.metaUrl) {
        try {
          const metaResponse = await fetch(data.metaUrl, {
            method: 'PUT',
            body: JSON.stringify(captureMetadata()),
            headers: {
              'Content-Type': 'application/json',
              'Cache-Control': 'public, max-age=31536000, immutable',
            },
          });
          if (!metaResponse.ok) console.warn('Capture metadata upload failed:', metaResponse.status);
        } catch (err) {
          console.warn('Capture metadata failed:', err);
        }
      }

      // A failed thumbnail doesn't fail the capture — the Captures page
      // falls back to the full-size image when there's no thumbnail.
      if (data.thumbUrl) {
        try {
          const thumbBlob = await makeCaptureThumbnail(imageBlob);
          if (thumbBlob) {
            const thumbResponse = await fetch(data.thumbUrl, {
              method: 'PUT',
              body: thumbBlob,
              headers: {
                'Content-Type': 'image/jpeg',
                'Cache-Control': 'public, max-age=31536000, immutable',
              },
            });
            if (!thumbResponse.ok) console.warn('Capture thumbnail upload failed:', thumbResponse.status);
          }
        } catch (err) {
          console.warn('Capture thumbnail failed:', err);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      spinner(false);
    }
    // Same place as the Projects share toast: centred, just below the header.
    showToast(saved ? 'Capture saved' : 'Capture failed', {
      position: 'top',
      between: [document.getElementById('top-nav')],
    });
    return saved;
  }

  // Shared-link viewers get a local download instead of an S3 upload — same
  // .modal-overlay/.modal-content confirm-dialog markup/styling
  // CaptureHandler.js's delete/download-empty modals use, and the same
  // file-saver saveAs() the Captures page's ZIP download already uses,
  // just for a single PNG Blob instead of a zipped set of already-uploaded
  // images.
  function confirmAndDownloadScreenshot(aspectRatio) {
    if (document.body.classList.contains('modal-open')) return;

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';

    // Matches the shared modal styling (src/modalButtons.js) so this
    // hand-built dialog looks identical to the React modals.
    const content = document.createElement('div');
    content.className = 'modal-content';
    content.style.cssText = 'background-color:#fff;border-radius:12px;padding:24px 28px 20px;max-width:380px;width:90%;text-align:left;pointer-events:auto;';

    const title = document.createElement('p');
    title.textContent = 'Download this capture';
    title.style.cssText = 'font-size:15px;font-weight:600;color:#1a1a1a;font-family:Inter,Arial,sans-serif;margin:0 0 8px;';
    const desc = document.createElement('p');
    desc.textContent = 'Save the current view as an image to your device?';
    desc.style.cssText = 'font-size:13px;color:#666;font-family:Inter,Arial,sans-serif;margin:0 0 20px;';

    const buttonRow = document.createElement('div');
    buttonRow.style.cssText = 'margin-top:20px;display:flex;gap:10px;';
    const btnBase = 'flex:1;padding:11px;border-radius:8px;border:none;cursor:pointer;font-size:14px;font-family:Inter,Arial,sans-serif;font-weight:500;line-height:1.2;';

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.cssText = btnBase + 'background:#ececec;color:#1a1a1a;';

    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = 'Download';
    confirmBtn.style.cssText = btnBase + 'background:#1a1a1a;color:#fff;';

    function close() {
      overlay.remove();
      document.body.classList.remove('modal-open');
    }
    cancelBtn.addEventListener('click', close);
    // Deliberately NOT closing on overlay/outside click — only Cancel or
    // Download dismiss this (matches the captures-page confirm dialogs).
    overlay.addEventListener('click', (e) => e.stopPropagation());
    content.addEventListener('click', (e) => e.stopPropagation());
    confirmBtn.addEventListener('click', async () => {
      close();
      const blob = await getCroppedScreenshotBlob(aspectRatio);
      saveAs(blob, `${modelId || 'capture'}.jpg`);
    });

    buttonRow.appendChild(cancelBtn);
    buttonRow.appendChild(confirmBtn);
    content.appendChild(title);
    content.appendChild(desc);
    content.appendChild(buttonRow);
    overlay.appendChild(content);
    document.body.appendChild(overlay);
    document.body.classList.add('modal-open');
  }

  // Prevent <base href="/"> from making href="#" navigate to /# and trigger login redirect
  const fullscreenAnchor = document.getElementById('fullscreen-button')?.closest('a');
  if (fullscreenAnchor) fullscreenAnchor.addEventListener('click', (e) => e.preventDefault());

  // Handle the capture button click event (hidden entirely for shared-link
  // viewers — see viewer-top.html — so this may legitimately be absent)
  const captureLink = document.getElementById('viewer-top-captures');
  captureLink?.addEventListener('click', (event) => {
    event.preventDefault();

    // Current URL parameters plus the current view (modelId, camera, lens,
    // aspect, rotation, translation)
    const queryParams = currentViewParams(new URLSearchParams(window.location.search));

    // To the captures page in-app (no page reload — keeps fullscreen; the
    // scene is disposed when Scene.js unmounts). A full load only if the
    // app's navigation isn't available.
    const url = `/captures?${queryParams.toString()}`;
    if (window.__appNavigate) window.__appNavigate(url);
    else window.location.href = url;
  });

  // Prevent multiple listeners
  const captureButton = document.getElementById('viewer-capture-button');
  const wrapperButton = document.getElementById('viewer-capture-wrapper-button');

  function attachEventOnce(button, handler) {
    if (button) {
      button.replaceWith(button.cloneNode(true)); // Removes all previous listeners
      const newButton = document.getElementById(button.id); // Re-fetch the new element
      newButton.addEventListener('click', handler);
    }
  }

  // Handler for screenshot button
  function handleScreenshotClick(event) {
    event.preventDefault();
    event.stopPropagation();
    // Shared-link viewers (covers both the button click and the "C"
    // keyboard shortcut, both of which call this) get a local download
    // with a confirm step instead of the logged-in S3-upload flow — they
    // have no account for an uploaded capture to belong to.
    if (sessionStorage.getItem('shareToken')) {
      confirmAndDownloadScreenshot(aspectRatio);
      return;
    }
    // Logged-out visitors (a public model) can't upload captures either —
    // the backend requires a login — so they also save locally.
    hasSession().then((loggedIn) => {
      if (loggedIn) takeScreenshot(aspectRatio);
      else confirmAndDownloadScreenshot(aspectRatio);
    });
  }

  // Function to capture doubleclick event on touch devices
  let lastTapTime = 0;
  const doubleTapThreshold = 300;

  // Two-finger pinch distance from the previous touchmove, for pinch-zoom
  // (OrbitControls' own zoom is disabled — see zoomBy).
  let _pinchPrevDist = null;

  function handleTouchStart(event) {
    // The joystick's finger isn't a scene touch (no tap, pinch or drag).
    if (isJoystickTouch(event)) return;
    const touches = sceneTouches(event.touches);
    if (touches.length > 1) {
      isPinching = true;
      _pinchPrevDist = null;
      return;
    }
    if (touches.length === 1) {
      const t = touches[0];
      touchStartX = t.clientX;
      touchStartY = t.clientY;
      isTouchDragging = false;
      shouldClearCircleOnDrag = true;
    }

    const now = Date.now();
    const timeSinceLastTap = now - lastTapTime;
    if (timeSinceLastTap < doubleTapThreshold) {
      event.preventDefault();
      event.stopPropagation();
      onMouseUp(event);
      return;
    }
    lastTapTime = now;
  }

  function handleTouchMove(event) {
    const touches = sceneTouches(event.touches);
    if (touches.length === 0) return;
    if (touches.length > 1) {
      isPinching = true;
      // No pinch-zoom while the scene is loading (see blockPinchWhileLoading).
      if (sceneLoadingLock()) { _pinchPrevDist = null; return; }
      const a = touches[0], b = touches[1];
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (_pinchPrevDist != null) {
        // ~12px of finger-spread change ≈ one wheel notch; apart -> zoom in.
        zoomBy((d - _pinchPrevDist) / 12);
      }
      _pinchPrevDist = d;
      return;
    }
    _pinchPrevDist = null;
    const t = touches[0];
    const dx = Math.abs(t.clientX - touchStartX);
    const dy = Math.abs(t.clientY - touchStartY);
    if (dx > TAP_MOVE_TOLERANCE || dy > TAP_MOVE_TOLERANCE) {
      isTouchDragging = true;
      if (shouldClearCircleOnDrag) {
        clearCircle();
        shouldClearCircleOnDrag = false;
      }
    }
  }

  function handleTouchEnd(event) {
    onLabelPlacementTouchEnd(event);
    const touches = sceneTouches(event.touches);
    if (touches.length < 2) _pinchPrevDist = null;
    if (touches.length === 0) {
      setTimeout(() => {
        isPinching = false;
        isTouchDragging = false;
      }, 0);
    }
  }

  // Attach events
  attachEventOnce(captureButton, handleScreenshotClick);
  attachEventOnce(wrapperButton, handleScreenshotClick);

  // Capture button is fully enabled for shared-link viewers too now —
  // handleScreenshotClick routes them to a local download instead of the
  // logged-in S3-upload flow (see there).

  onWindow('resize', onWindowResize);
  onWindow('mousedown', onMouseDown);
  //onWindow('mouseup', onMouseUp);
  onWindow('mousemove', onPointerMove);
  onWindow('dblclick', onMouseUp);
  onWindow('click', onLabelPlacementClick);
  onWindow('touchstart', handleTouchStart, { passive: false });
  onWindow('touchmove',  handleTouchMove,  { passive: true  });
  onWindow('touchend',   handleTouchEnd,   { passive: true  });
  // True while this scene's loading overlay is up. container.isConnected:
  // initializeScene has no teardown, so an instance left mid-load (user
  // navigated away) must not keep blocking input on later pages.
  function sceneLoadingLock() {
    return spinnerActive && container.isConnected;
  }
  // While the loading overlay is up, a pinch must zoom neither the camera
  // (handleTouchMove skips it) nor the page itself — preventDefault on a
  // two-finger touchmove, plus iOS Safari's own gesture events.
  const blockPinchWhileLoading = (e) => {
    if (sceneLoadingLock() && (e.type !== 'touchmove' || (e.touches && e.touches.length > 1))) e.preventDefault();
  };
  // While loading, keys do nothing except I (info modal): arrows, H (reset
  // view), F, C, W/A/S/D/Q/E… all wait until the scene has finished
  // loading. Capture phase on window runs before every other key listener
  // (this file, public/js/scene.js, React's). Browser shortcuts still work —
  // only the app's handlers are skipped; scroll keys' default is blocked.
  const blockKeysWhileLoading = (e) => {
    if (!sceneLoadingLock()) return;
    if ((e.key || '').toLowerCase() === 'i' && !e.metaKey && !e.ctrlKey && !e.altKey) return;
    const tag = e.target && e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable)) return;
    e.stopImmediatePropagation();
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) e.preventDefault();
  };
  onWindow('keydown', blockKeysWhileLoading, true);
  onWindow('touchmove', blockPinchWhileLoading, { passive: false });
  onWindow('gesturestart', blockPinchWhileLoading, { passive: false });
  onWindow('gesturechange', blockPinchWhileLoading, { passive: false });

  onWindow('mouseout', (event) => {
    // When the mouse leaves the window (relatedTarget is null)
    if (!event.relatedTarget) {
      const syntheticEvent = new PointerEvent('pointerup', {
        bubbles: true,
        cancelable: true,
        pointerId: 1,
        clientX: window.innerWidth / 2,
        clientY: window.innerHeight / 2,
      });
      renderer.domElement.dispatchEvent(syntheticEvent);
    }
  });

  // Logic to clear the circle on control interaction

  // Fades the circle out rather than yanking it from the DOM instantly —
  // shared by every "something else just happened, dismiss the stale
  // circle" path (drag-to-orbit starting right after a click, camera
  // movement while a clear was pending, etc.). An abrupt disappearance on
  // any of those read as a visual glitch rather than a natural give-way,
  // which is what was actually behind the "sometimes vanishes instead of
  // fading" reports — most of those paths have zero movement tolerance
  // (drag-to-orbit immediately following a double-click is a very common,
  // completely ordinary gesture), so they fire far more often than they
  // looked like they should.
  function clearCircle() {
    cancelCircleFadeTimers();
    const circle = document.querySelector('.circle');
    if (!circle) return;
    circle.style.transition = 'opacity 0.3s ease';
    circle.style.opacity = '0';
    setTimeout(() => circle.remove(), 300);
  }

  let pendingClear = false;

  controls.addEventListener('start', () => {
    pendingClear = true;
    // A real user-initiated drag/orbit — let them take over rather than
    // fighting an in-flight camera tween (focal-point recenter or
    // label-to-label navigation). Also drop the independent tooltip-hide
    // deadline so it doesn't keep the tooltip hidden for the rest of an
    // interaction the user just cancelled.
    cameraTween = null;
    cameraTweenActiveUntil = 0;
  });

  controls.addEventListener('change', () => {
    // cameraTween drives camera.position/controls.target itself every
    // frame during a focal-point/label-navigation animation, and
    // controls.update() (called right after in animate()) fires this same
    // 'change' event for that purely programmatic movement — not just for
    // real user drags. Without this guard, a 'start' left armed from the
    // mousedown/mouseup that actually TRIGGERED the very focal-point change
    // (its matching 'end' hadn't fired yet by the time the tween's first
    // frame ran) would wipe the circle updateOrCreateCircle() had just
    // created, well before its intended fade — intermittent because it
    // depended on exact event-ordering timing between OrbitControls'
    // internal pointerup handling and our dblclick handler.
    if (pendingClear && !cameraTween) {
      clearCircle();
      pendingClear = false;
    }
  });

  controls.addEventListener('end', () => {
    pendingClear = false;
  });

  // controls.addEventListener('start', () => {
  //   // comment this out if you only want to clear on actual movement
  //   clearCircle();
  // });

  onWindow('rollControlValueChange', (event) => {
    const {
      rotateX,
      rotateY,
      rotateZ
    } = event.detail;

    if (rotateX !== undefined) {
      rotateAngleX = rotateX;
    }
    if (rotateY !== undefined) {
      rotateAngleY = rotateY;
    }
    if (rotateZ !== undefined) {
      rotateAngleZ = rotateZ;
    }

  });

  onWindow('lensSizeChange', (event) => {
    const {
      activeSize
    } = event.detail;
    focalLength = parseInt(activeSize, 10);
    applyLens();
  });

  onWindow('sensorSizeChange', (event) => {
    const {
      selectedValue
    } = event.detail;
    camera.zoom = selectedValue;
    camera.updateProjectionMatrix();
  });

  onWindow('clickOnControls', (event) => {
    clickOnControls = true;
  });

  onWindow('translateButton', (event) => {
    onKeyDown({
      key: event.detail[0],
      value: event.detail[1]
    });
  });

  onWindow('translateOverride', (event) => {
    onKeyDown({
      key: event.detail[0],
      value: event.detail[1]
    });
  });

  onWindow('resetCamera', () => {
    camera.position.set(initialCameraPositionHome[0], initialCameraPositionHome[1], initialCameraPositionHome[2]);
    controls.target.set(initialTargetPositionHome[0], initialTargetPositionHome[1], initialTargetPositionHome[2]);
    // Tilt / pan / roll on top of the orbit too: scene.js's 'h' only re-sends
    // its own scroll-based copies when they changed, so it never reset values
    // set here (the arrow keys used to add to the tilt) — Home kept the tilt.
    // Arrow / look-joystick pitch now turns the target, reset just above.
    rotateAngleX = 0; rotateAngleY = 0; rotateAngleZ = 0;
  });

  function onKeyDown(event) {
    const sphBefore = getPickCloudSphere();
    const distBefore = sphBefore ? camera.position.distanceTo(sphBefore.center) : 0;
    let direction = new THREE.Vector3();
    let up = new THREE.Vector3();
    let right = new THREE.Vector3();
    let tempPanSpeed = panningSpeed;

    // Calculate the right and up vectors relative to the camera's current orientation
    camera.getWorldDirection(direction);
    right.crossVectors(direction, camera.up).normalize();
    up.copy(camera.up).normalize();

    switch (event.key) {
      case 'ArrowLeft':
        // Move camera to the left
        if (event.value == '0') {
          panningSpeed = panningSpeed * translatePan;
          camera.position.addScaledVector(right, -panningSpeed);
          controls.target.addScaledVector(right, -panningSpeed);
          translatePan = 0;
        } else {
          panningSpeed = panningSpeed * (translatePan - event.value);
          camera.position.addScaledVector(right, -panningSpeed);
          controls.target.addScaledVector(right, -panningSpeed);
          translatePan = event.value;
        }
        break;
      case 'ArrowRight':
        // Move camera to the right
        if (event.value == '0') {
          panningSpeed = panningSpeed * (-translatePan);
          camera.position.addScaledVector(right, panningSpeed);
          controls.target.addScaledVector(right, panningSpeed);
          translatePan = 0;
        } else {
          panningSpeed = panningSpeed * (-translatePan + event.value);
          camera.position.addScaledVector(right, panningSpeed);
          controls.target.addScaledVector(right, panningSpeed);
          translatePan = event.value;
        }
        break;
      case 'w':
        // Move camera up
        if (event.value == '0') {
          panningSpeed = panningSpeed * (-translateDolly);
          camera.position.addScaledVector(direction, panningSpeed);
          controls.target.addScaledVector(direction, panningSpeed);
          translateDolly = 0;
        } else {
          panningSpeed = panningSpeed * (-translateDolly + event.value);
          camera.position.addScaledVector(direction, panningSpeed);
          controls.target.addScaledVector(direction, panningSpeed);
          translateDolly = event.value;
        }
        break;
      case 's':
        // Move camera down
        if (event.value == '0') {
          panningSpeed = panningSpeed * translateDolly;
          camera.position.addScaledVector(direction, -panningSpeed);
          controls.target.addScaledVector(direction, -panningSpeed);
          translateDolly = 0;
        } else {
          panningSpeed = panningSpeed * (translateDolly - event.value);
          camera.position.addScaledVector(direction, -panningSpeed);
          controls.target.addScaledVector(direction, -panningSpeed);
          translateDolly = event.value;
        }
        break;
      case 'ArrowUp':
        // Move camera in
        if (event.value == '0') {
          panningSpeed = panningSpeed * (-translateTilt);
          camera.position.addScaledVector(up, panningSpeed);
          controls.target.addScaledVector(up, panningSpeed);
          translateTilt = 0;
        } else {
          panningSpeed = panningSpeed * (-translateTilt + event.value);
          camera.position.addScaledVector(up, panningSpeed);
          controls.target.addScaledVector(up, panningSpeed);
          translateTilt = event.value;
        }
        break;
      case 'ArrowDown':
        // Move camera out
        if (event.value == '0') {
          panningSpeed = panningSpeed * translateTilt;
          camera.position.addScaledVector(up, -panningSpeed);
          controls.target.addScaledVector(up, -panningSpeed);
          translateTilt = 0;
        } else {
          panningSpeed = panningSpeed * (translateTilt - event.value);
          camera.position.addScaledVector(up, -panningSpeed);
          controls.target.addScaledVector(up, -panningSpeed);
          translateTilt = event.value;
        }
        break;
      case 'PanOverride':
        // Set the new Pan value
        translatePan = event.value;
        break;
      case 'TiltOverride':
        // Set the new Tilt value
        translateTilt = event.value;
        break;
      case 'DollyOverride':
        // Set the new Dolly value
        translateDolly = event.value;
        break;
    }

    if (sphBefore) keepWithinZoomOutLimit(distBefore);
    // Controls are updated to reflect the change
    controls.update();
    // Revert the default panning speed
    panningSpeed = tempPanSpeed;
  };

  // Smooth navigation handling
  const pressedKeys = {
    pan: 0,   // -1 left, +1 right
    tilt: 0,  // -1 down, +1 up
    dolly: 0, // -1 backward, +1 forward
    yaw: 0,   // -1 left turn, +1 right turn
    pitch: 0  // -1 look down, +1 look up
  };
  let isShiftDown = false;

  // On-screen joysticks (Settings → General, per browser; default on for
  // phones, off for tablets / desktops — isJoystickEnabled), analog, through the same smooth movement below:
  //  - Move (left side): walks like W/A/S/D — forward/back = dolly,
  //    sideways = pan.
  //  - Look (right side): turns like the arrow keys — sideways = yaw,
  //    up/down = pitch.
  // Shown only while the scene is usable (not loading, capturing, placing a
  // label, or behind a modal); another finger still drags to look around.
  // Sit as far above the footer as they are from the screen edge.
  const JOYSTICK_EDGE = 20;
  const JOYSTICKS = {
    move: { key: JOYSTICK_KEYS.move, side: 'left', axes: ['pan', 'dolly'], set: (x, y) => { pressedKeys.pan = x; pressedKeys.dolly = y; } },
    look: { key: JOYSTICK_KEYS.look, side: 'right', axes: ['yaw', 'pitch'], set: (x, y) => { pressedKeys.yaw = -x; pressedKeys.pitch = y; } },
  };
  // var / function declarations (not let / const): animate() can start
  // before this point runs, and calls updateJoystick (temporal dead zone).
  var joysticks = {}; // eslint-disable-line no-var
  var joysticksShown = false; // eslint-disable-line no-var
  var joystickBottom = -1; // eslint-disable-line no-var
  function isJoystickTouch(t) {
    return !!(t && t.target && Object.values(joysticks).some((j) => j.element.contains(t.target)));
  }
  function sceneTouches(list) { return list ? [...list].filter((t) => !isJoystickTouch(t)) : []; }
  window.__setJoystickEnabled = (name, on) => {
    const def = JOYSTICKS[name];
    if (!def) return;
    if (on && !joysticks[name]) {
      const j = createTouchJoystick({ side: def.side, onChange: def.set });
      joysticks[name] = j;
      j.setVisible(joysticksShown);
      if (joystickBottom >= 0) j.setBottom(joystickBottom);
    } else if (!on && joysticks[name]) {
      joysticks[name].destroy();
      delete joysticks[name];
      def.axes.forEach((a) => { pressedKeys[a] = 0; });
    }
  };
  // Per frame (animate): show / hide, follow the footer's height, and remove
  // them once the scene is gone.
  function updateJoystick() {
    const list = Object.values(joysticks);
    if (!list.length) return;
    if (!container.isConnected) { Object.keys(joysticks).forEach((n) => window.__setJoystickEnabled(n, false)); return; }
    const show = !spinnerActive && !captureInProgress && !labelPlacementActive
      && !document.body.classList.contains('modal-open');
    if (show !== joysticksShown) {
      joysticksShown = show;
      list.forEach((j) => j.setVisible(show));
    }
    if (!show) return;
    const footer = document.getElementById('html-container2');
    const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight;
    const bottom = Math.round(Math.max(0, window.innerHeight - footerTop) + JOYSTICK_EDGE);
    if (bottom !== joystickBottom) {
      joystickBottom = bottom;
      list.forEach((j) => j.setBottom(bottom));
    }
  }
  Object.entries(JOYSTICKS).forEach(([name, def]) => {
    if (isJoystickEnabled(def.key)) window.__setJoystickEnabled(name, true);
  });
  // Leaving the scene (e.g. to Captures) stops animate() before
  // updateJoystick's own cleanup runs, which left them on the next page.
  onDispose(() => {
    Object.keys(joysticks).forEach((n) => window.__setJoystickEnabled(n, false));
    delete window.__setJoystickEnabled;
  });

  // Smooth movement settings
  const smoothSpeed = 15; // Higher is faster
  const smoothFactor = 0.01; // Lower is smoother
  const shiftMultiplier = 4;
  const turnSpeed = 2; // Angular speed for in-place yaw
  const pitchSpeed = 2; // Angular speed for in-place pitch
  // Per-user multipliers (Settings → Controls, utils/speedPrefs.js), same
  // local-then-DB loading and live slider as zoomSpeed: moveSpeed for moving
  // (keys, move joystick), lookSpeed for looking around (arrow-key turning /
  // tilting, look joystick).
  let moveSpeed = readCachedSpeed(MOVE_SPEED);
  fetchSpeed(MOVE_SPEED).then((v) => { if (v != null) moveSpeed = v; });
  window.__setMoveSpeed = (v) => { moveSpeed = v; };
  let lookSpeed = readCachedSpeed(LOOK_SPEED);
  fetchSpeed(LOOK_SPEED).then((v) => { if (v != null) lookSpeed = v; });
  window.__setLookSpeed = (v) => { lookSpeed = v; };

  // --- Update smooth movement ---
  function updateSmoothMovement() {
    const multiplier = (isShiftDown ? shiftMultiplier : 1) * moveSpeed;
    const lookMultiplier = (isShiftDown ? shiftMultiplier : 1) * lookSpeed;

    let direction = new THREE.Vector3();
    let up = new THREE.Vector3();
    let right = new THREE.Vector3();

    camera.getWorldDirection(direction);
    up.copy(camera.up).normalize();
    right.crossVectors(direction, up).normalize();

    const moving = pressedKeys.pan !== 0 || pressedKeys.tilt !== 0 || pressedKeys.dolly !== 0;
    const sph = moving ? getPickCloudSphere() : null;
    const distBefore = sph ? camera.position.distanceTo(sph.center) : 0;

    // Apply movement only based on currently pressed keys
    if (pressedKeys.pan !== 0) {
      camera.position.addScaledVector(right, pressedKeys.pan * smoothSpeed * multiplier * smoothFactor);
      controls.target.addScaledVector(right, pressedKeys.pan * smoothSpeed * multiplier * smoothFactor);
    }

    if (pressedKeys.tilt !== 0) {
      camera.position.addScaledVector(up, pressedKeys.tilt * smoothSpeed * multiplier * smoothFactor);
      controls.target.addScaledVector(up, pressedKeys.tilt * smoothSpeed * multiplier * smoothFactor);
    }

    if (pressedKeys.dolly !== 0) {
      camera.position.addScaledVector(direction, pressedKeys.dolly * smoothSpeed * multiplier * smoothFactor);
      controls.target.addScaledVector(direction, pressedKeys.dolly * smoothSpeed * multiplier * smoothFactor);
    }
    if (sph) keepWithinZoomOutLimit(distBefore);

    // Apply yaw separately - rotate target around camera without affecting pan/tilt/dolly
    if (pressedKeys.yaw !== 0) {
      const yawAngle = pressedKeys.yaw * turnSpeed * lookMultiplier * smoothFactor;
      const targetOffset = new THREE.Vector3().subVectors(controls.target, camera.position);
      targetOffset.applyAxisAngle(up, yawAngle);
      controls.target.copy(camera.position).add(targetOffset);
    }

    // Pitch the same way yaw turns: the target around the camera, so arrows /
    // look joystick and dragging share one view direction. (It used to add to
    // the camera tilt offset, rotateAngleX, on top of the orbit: after looking
    // up at the sky with the arrows, a drag could only bring the view down to
    // the orbit's own limit plus that offset — never back to the ground.)
    // Stops 5° short of straight up / down so the orbit never flips.
    if (pressedKeys.pitch !== 0) {
      const pitchAngle = pressedKeys.pitch * pitchSpeed * lookMultiplier * smoothFactor;
      const targetOffset = new THREE.Vector3().subVectors(controls.target, camera.position);
      const len = targetOffset.length();
      if (len > 0) {
        const elevation = Math.asin(THREE.MathUtils.clamp(targetOffset.dot(up) / len, -1, 1));
        const limit = THREE.MathUtils.degToRad(85);
        const delta = THREE.MathUtils.clamp(elevation + pitchAngle, -limit, limit) - elevation;
        const pitchAxis = new THREE.Vector3().crossVectors(targetOffset, up).normalize();
        if (delta && pitchAxis.lengthSq() > 0) {
          targetOffset.applyAxisAngle(pitchAxis, delta);
          controls.target.copy(camera.position).add(targetOffset);
        }
      }
    }

    controls.update();
  }

  // --- Smooth movement key handlers ---
  function onSmoothKeyDown(event) {
    if (window.__accountModalOpen) return;
    const targetTag = event.target && event.target.tagName;
    if (targetTag === 'INPUT' || targetTag === 'TEXTAREA' || (event.target && event.target.isContentEditable)) return;
    const code = event.code;

    // Handle Shift separately (works regardless of order)
    if (code === 'ShiftLeft' || code === 'ShiftRight') {
      isShiftDown = true;
      return;
    }

    // Movement key mapping (physical key code)
    switch (code) {
      case 'KeyW': pressedKeys.dolly = 1; break;
      case 'KeyS': pressedKeys.dolly = -1; break;
      case 'KeyA': pressedKeys.pan = -1; break;
      case 'KeyD': pressedKeys.pan = 1; break;
      case 'KeyQ': pressedKeys.tilt = 1; break;
      case 'KeyE': pressedKeys.tilt = -1; break;
      case 'ArrowLeft': pressedKeys.yaw = 1; break;
      case 'ArrowRight': pressedKeys.yaw = -1; break;
      case 'ArrowUp': pressedKeys.pitch = 1; break;
      case 'ArrowDown': pressedKeys.pitch = -1; break;
      case 'KeyC':
        if (spinnerActive) {
          return;
        }
        handleScreenshotClick(event);
        break;
      default: return;
    }

    if (event.preventDefault) event.preventDefault();
  }

  function onSmoothKeyUp(event) {
    if (window.__accountModalOpen) return;
    const code = event.code;

    // Handle Shift release
    if (code === 'ShiftLeft' || code === 'ShiftRight') {
      isShiftDown = false;
      return;
    }

    // Stop movement for released key
    switch (code) {
      case 'KeyW':
      case 'KeyS':
        pressedKeys.dolly = 0;
        break;
      case 'KeyA':
      case 'KeyD':
        pressedKeys.pan = 0;
        break;
      case 'KeyQ':
      case 'KeyE':
        pressedKeys.tilt = 0;
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        pressedKeys.yaw = 0;
        break;
      case 'ArrowUp':
      case 'ArrowDown':
        pressedKeys.pitch = 0;
        break;
      default:
        return;
    }

    if (event.preventDefault) event.preventDefault();
  }

  // --- Safety: reset on window blur (prevents stuck movement) ---
  onWindow('blur', () => {
    pressedKeys.pan = 0;
    pressedKeys.tilt = 0;
    pressedKeys.dolly = 0;
    pressedKeys.yaw = 0;
    pressedKeys.pitch = 0;
    isShiftDown = false;
  });

  // --- Attach listeners ---
  onWindow('keydown', onSmoothKeyDown);
  onWindow('keyup', onSmoothKeyUp);

  initialize();
  app();
}
